// src/pages/EditorPage.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, Menu, MenuItem, Typography, Snackbar, Tooltip } from '@mui/material';

import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import useEditorAutosave, { hasVisibleText } from '../hooks/useEditorAutosave';
import { manuscriptWordStats } from '../utils/wordStats';
import useEditorSocket from '../hooks/useEditorSocket';

import EditorSidebar from '../components/editor/EditorSidebar';
import EditorRightRail from '../components/editor/EditorRightRail';
import EditorFormatBar from '../components/editor/EditorFormatBar';
import StoryElementsPanel from '../components/editor/StoryElementsPanel';
import ElementHoverCard from '../components/editor/ElementHoverCard';
import ConfirmModal from '../components/editor/ConfirmModal';
import EditorToolbar from '../components/editor/EditorToolbar';
import InsertPanel from '../components/editor/InsertPanel';
import ViewPanel from '../components/editor/ViewPanel';
import EditorStatusBar from '../components/editor/EditorStatusBar';
import SidebarPanelHeader from '../components/editor/SidebarPanelHeader';
import FilePanel from '../components/editor/FilePanel';
import EditorRuler, { EditorHRuler, EditorVRuler, maxHMargin, maxVMargin, pageStrideFor } from '../components/editor/EditorRuler';
import CollabPanel from '../components/editor/CollabPanel';
import CommentsPanel from '../components/editor/CommentsPanel';
import CollabTabs from '../components/editor/CollabTabs';
import CommenterCredentials from '../components/editor/CommenterCredentials';
import GoalsSidePanel from '../components/editor/goals/GoalsSidePanel';
import GoalsView from '../components/editor/goals/GoalsView';
import GoalFormModal from '../components/editor/goals/GoalFormModal';
import GoalsRailSection from '../components/editor/goals/GoalsRailSection';
import useManuscriptGoals from '../components/editor/goals/useManuscriptGoals';
import '../styles/GoalsTab.css';
import ToolsPanel from '../components/editor/ToolsPanel';
import SpellCheckModal from '../components/editor/SpellCheckModal';
import WordCountModal from '../components/editor/WordCountModal';
import CitationModal from '../components/editor/CitationModal';
import FloatingToolbar from '../components/editor/FloatingToolbar';
import CommentSelectionChip from '../components/comments/CommentSelectionChip';
import PublishModal from '../components/editor/PublishModal';
import CollaboratorsDialog from '../components/editor/CollaboratorsDialog';
import '../styles/EditorPage.css';
import { API_BASE } from '../utils/api'; // added
import { DEFAULT_BOOK_SIZE, bookSize, dimensionsLabel, pageSizePx } from '../config/bookSizes';
import { columnWidthOf, fitImageToColumn, fitNewImageToPage } from '../utils/imageFit';
import { stripSplitMarks } from '../utils/splitBlocks';
import { createEmptyPageEl, isBodyOverflowing, reflowPages } from '../utils/paginate';
import { clearOverrides, completeTheme, countOverrides, makeSceneBreak, themeCss, themeValues } from '../utils/manuscriptTheme';
import { errorCode, setManuscriptTheme } from '../utils/manuscriptThemeApi';
import ManuscriptThemeMenu from '../components/manuscriptTheme/ManuscriptThemeMenu';
import TemplateMenu from '../components/editor/TemplateMenu';
import { changeTemplate, listTemplates, revertTemplate } from '../utils/manuscriptTemplateApi';
import { ORPHAN_ATTR, computedLook, orphanableIds, orphanBlocks, releaseBlock, unorphanBlocks } from '../utils/templateOrphan';
import { templateTheme } from '../config/templateThemes';
import { commentApi, storyElementApi } from '../utils/readerApi';
import { getCleanHTML } from '../utils/editorSerialize';
import {
  findLeftoverDrafts, clearLeftoverDrafts, pruneLocalDrafts, hashContent,
} from '../utils/editorStorage';
import { buildOutline, getChapterRange, ensureBlockIds, getChapterHeadingId, getSiblingChapterId, buildBlockChapterMap, resolveActiveHeadingId } from '../utils/outline';
import { applyCommentHighlights, clearCommentHighlights } from '../utils/commentHighlights';
import { applyCollabHighlights, clearCollabHighlights, COLLAB_BLOCK_SEL } from '../utils/collabHighlights';
import { applyFormattingMarks, clearFormattingMarks } from '../utils/formattingMarks';
import { applyElementHighlights, clearElementHighlights } from '../utils/elementHighlights';
import { applyLineNumbers, clearLineNumbers, buildLineIndex } from '../utils/lineNumbers';
import ImageUploadModal from '../components/editor/modals/ImageUploadModal';
import TableWizard from '../components/editor/modals/TableWizard';
import TableContextMenu from '../components/editor/modals/TableContextMenu';
import ImageContextMenu from '../components/editor/modals/ImageContextMenu';
import { tokens } from '../theme';
import ImageLayer from '../components/editor/layers/ImageLayer';
import ShapePicker from '../components/editor/modals/ShapePicker';
import DividerPicker from '../components/editor/DividerPicker';
import TextBoxPicker from '../components/editor/TextBoxPicker';
import InsertPageModal from '../components/editor/modals/InsertPageModal';
import { getSelectionRange, restoreSelection, insertHTMLAtCursor } from '../utils/selection';
import { joinBleedPieces, bleedBlocksOf, bleedBlocker, bleedPieceHasRoom, bleedPiecesOf, firstPieceOf as firstBleedPieceOf, releasePastedPieces, isBleedOn, BLEED_ATTR, OFFSET_ATTR } from '../utils/imageBleed';
import { DRAG_START_PX, dropGaps, liftImage, nearestSpot } from '../utils/imageDrag';
import PageNumberModal from '../components/editor/modals/PageNumberModal';
import BookPreviewModal from '../components/editor/modals/BookPreviewModal';
import EbookPreviewModal from '../components/editor/modals/EbookPreviewModal';
import PrintReviewNotice from '../components/intake/PrintReviewNotice';
import '../styles/editor-page.css';
import '../styles/editor-inserts.css';
// --- Table Insert DEBUG ---
const DEBUG_TABLE = true; // set to false or remove when done
const dbg = (...args) => { if (DEBUG_TABLE) console.debug('[TableInsert]', ...args); };

/**
 * Build the resolver the Collab index uses to label a comment's line.
 *
 * Prefers a LIVE measurement of where the comment's block currently renders,
 * because line numbers are a property of layout: inserting a paragraph
 * renumbers everything below it, so a number stored at write time goes stale
 * immediately. `line_hint` is only the fallback for a block that no longer
 * renders — deleted, or on a draft that hasn't painted yet — so those comments
 * still show their last known position instead of "Unplaced".
 *
 * Returned as a factory so the resulting function's identity changes only when
 * the measurement does, keeping the panel's grouping memo stable.
 */
const makeLineResolver = (index) => (c) => {
  if (!c) return null;
  const live = c.block_id ? index.get(c.block_id) : undefined;
  if (live != null) return live;
  return c.line_hint != null ? c.line_hint : null;
};

/**
 * Build the resolver that decides which chapter a comment belongs to.
 *
 * Reads the CURRENT document rather than the `chapter_id` stored on the
 * comment: a paragraph can be dragged into another chapter, or its heading
 * renamed or deleted, and the stored value would then quietly disagree with
 * what's on screen. The stored value survives only as a fallback for comments
 * whose block no longer exists at all.
 */
const makeChapterResolver = (map) => (c) => {
  if (!c) return null;
  if (c.block_id && map.has(c.block_id)) return map.get(c.block_id);
  return c.chapter_id != null ? c.chapter_id : null;
};

// --- Zoom bounds (display scale, separate from the document's logical geometry) ---
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;
const clampZoom = (z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Number(z) || 1));

// --- Heading model (Navigation/Outline feature) ---
// A heading is a block <p data-heading-level="1..4" data-block-id="uuid">.
// These visual values mirror the heading entries in EditorToolbar's TEXT_STYLES
// so a block-level heading looks identical to the old inline-styled one.
const HEADING_STYLES = {
  1: { fontSize: '24px', fontWeight: '700', fontFamily: 'Arial', textAlign: 'left' },
  2: { fontSize: '20px', fontWeight: '700', fontFamily: 'Arial', textAlign: 'left' },
  3: { fontSize: '18px', fontWeight: '700', fontFamily: 'Arial', textAlign: 'left' },
  4: { fontSize: '16px', fontWeight: '700', fontFamily: 'Arial', textAlign: 'left' },
};

// Generate a stable id for a heading block. crypto.randomUUID is available in
// all supported browsers; fall back to a timestamp+random string just in case.
const makeBlockId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `blk-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// What the server needs to know about the floating image widgets, for telling
// a real change from a reload. A widget with an s3Key gets a fresh presigned
// `src` on every load, so that field alone is not a change.
const imagesKey = (list) => JSON.stringify(
  (list || []).map((img) => (img?.s3Key ? { ...img, src: undefined } : img)),
);

// The manuscript theme's stylesheet, one per editor page.
const THEME_STYLE_ID = 'rv-manuscript-theme';
// Under a theme every chapter starts on a new page, as it will in print.
const isChapterOpening = (block) =>
  block.getAttribute?.('data-heading-level') === '1' && !!block.textContent?.trim();

const EditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [doc, setDoc] = useState(null);
  const [content, setContent] = useState('');
  // Comment composer flow (selection chip → Collab panel composer).
  // One object replaces the old showComments/composerOpen/quote/anchor quartet:
  // either we're composing a comment on a passage or we aren't.
  // { quote, blockId, chapterId, lineHint } | null
  const [pendingComment, setPendingComment] = useState(null);
  const [commentChip, setCommentChip] = useState(null); // { text, top, left } | null
  // The Goal tab's middle: the goals dashboard, one goal, or the manuscript
  // itself ("Edit"). Only means anything while the Goal tab is open.
  const [goalsView, setGoalsView] = useState({ mode: 'dashboard' });
  const [goalForm, setGoalForm] = useState(null); // null, or { goal } (goal null = new)
  // Below 900px the Goal list floats over the goals page; "tucked" slides it
  // away so the page can be read. No effect on wider screens.
  const [goalsListTucked, setGoalsListTucked] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [showShape, setShowShape] = useState(false);
  const [showDivider, setShowDivider] = useState(false);
  const [showTextBox, setShowTextBox] = useState(false);
  const [savedRange, setSavedRange] = useState(null);
  const [selectionData, setSelectionData] = useState(null);
  const [pendingFormat, setPendingFormat] = useState({ fontFamily: null, fontSize: null });
  const [images, setImages] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [outgoingInvitations, setOutgoingInvitations] = useState([]);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [activeSidebarPanel, setActiveSidebarPanel] = useState(null);
  // Navigation/Outline panel — derived heading tree + the heading currently in view.
  const [outline, setOutline] = useState([]);
  const [chapterWordCounts, setChapterWordCounts] = useState({});
  const [activeHeadingId, setActiveHeadingId] = useState(null);
  const outlineTimerRef = useRef(null);
  // Custom delete-chapter confirmation: { blockId, title } | null.
  const [chapterToDelete, setChapterToDelete] = useState(null);
  // Structural-undo: snapshots of editor.innerHTML taken before each chapter op
  // (insert/delete/move/level). Ctrl+Z restores the latest snapshot ONLY while
  // lastStructuralRef is set; typing clears it so native typing-undo resumes.
  const structuralUndoRef = useRef([]);
  const lastStructuralRef = useRef(false);
  const [undoToast, setUndoToast] = useState(null); // { label } | null
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showRuler, setShowRuler] = useState(false);
  // Single page, or two side by side like an open book. Survives a reload the
  // way the editor's other view preferences do; storage can be unavailable
  // (private windows, blocked site data) so the read is guarded.
  const [pageViewMode, setPageViewMode] = useState(() => {
    try { return localStorage.getItem('revisionari_page_view') === 'spread' ? 'spread' : 'single'; }
    catch { return 'single'; }
  });
  const [showFormattingMarks, setShowFormattingMarks] = useState(false);
  // View tab — annotation toggles
  const [showCommentHighlights, setShowCommentHighlights] = useState(false);
  const [showCollabEdits, setShowCollabEdits] = useState(false);
  // Comments on this draft (lifted here so highlights work even with the
  // comment sidebar closed). Each comment may carry a `quote` string we match
  // against the editor text to tint where the comment was made.
  const [draftComments, setDraftComments] = useState([]);
  // Collab tab — the panel opens on the comment index; the "←" (and the footer
  // button) go back to the collaborator roster, which lives nowhere else.
  const [collabView, setCollabView] = useState('comments'); // 'comments' | 'people'
  const [collabComments, setCollabComments] = useState([]);
  const [collabLoading, setCollabLoading] = useState(false);
  // Flips once the Collab panel's first comment fetch has settled, so a
  // notification deep link can tell "still loading" from "genuinely absent".
  const [collabLoadedOnce, setCollabLoadedOnce] = useState(false);
  const [collabError, setCollabError] = useState('');
  // Bumped by a card action (post/discard/tag/notify) to refetch immediately
  // rather than waiting out the poll.
  const [collabReloadKey, setCollabReloadKey] = useState(0);
  const [includeUnread, setIncludeUnread] = useState(true);
  // Which line/block the comment index has selected — drives the centre pane.
  const [selectedCommentBlockId, setSelectedCommentBlockId] = useState(null);
  // Which chapters the comment index has open. Held here, not in the panel,
  // so it survives the panel unmounting (People tab, closing the sidebar) for
  // the rest of this visit; a fresh visit starts from read state again.
  const [commentChaptersOpen, setCommentChaptersOpen] = useState(() => new Map());
  // Bumped on every deliberate selection so the panel reopens the line's
  // chapter even when the selection itself didn't change; the ref is the
  // panel's memory of what it already revealed, kept here for the same reason.
  const [commentRevealSeq, setCommentRevealSeq] = useState(0);
  const commentRevealedRef = useRef(null);
  // Live block-id → line-number map, re-measured while the comment index is
  // open. Line numbers come from rendered layout, so they can't be stored.
  const [lineIndex, setLineIndex] = useState(() => new Map());
  // block-id → chapter heading block-id, rebuilt with the line index.
  const [chapterMap, setChapterMap] = useState(() => new Map());
  // Rebuilt only when the measurement changes, so the panel's grouping memo
  // isn't invalidated on every unrelated render.
  const lineResolver = useMemo(() => makeLineResolver(lineIndex), [lineIndex]);
  const chapterResolver = useMemo(() => makeChapterResolver(chapterMap), [chapterMap]);
  // Whose credentials the right rail shows. A line can carry several comments;
  // we surface the most recent one's author, which is the first entry since the
  // API returns newest-first.
  const selectedComment = useMemo(() => {
    if (!selectedCommentBlockId) return null;
    return (collabComments || []).find((c) => c.block_id === selectedCommentBlockId) || null;
  }, [collabComments, selectedCommentBlockId]);
  // Recent collaborator edits received over the socket, used to tint where
  // other users changed text. Each entry: { id, userId, username, text }.
  // Capped to the most recent edits so the list never grows unbounded.
  const [collabEdits, setCollabEdits] = useState([]);
  const COLLAB_EDITS_CAP = 80;
  // View tab — preview overlays
  const [showBookPreview, setShowBookPreview] = useState(false);
  const [showEbookPreview, setShowEbookPreview] = useState(false);

  // ── Right-rail live preview ────────────────────────────────────────────────
  // Open/closed and the chosen device format survive a reload, the way the rest
  // of the editor's view preferences do. Storage can be unavailable (private
  // windows, blocked site data), so every read and write is guarded.
  const [railPreviewOpen, setRailPreviewOpen] = useState(() => {
    try { return localStorage.getItem('revisionari_preview_open') === '1'; } catch { return false; }
  });
  const [railPreviewFormat, setRailPreviewFormat] = useState(() => {
    try { return localStorage.getItem('revisionari_preview_format') || 'book'; } catch { return 'book'; }
  });
  const [railPreviewScope, setRailPreviewScope] = useState(() => {
    try { return localStorage.getItem('revisionari_preview_scope') === 'book' ? 'book' : 'chapter'; } catch { return 'chapter'; }
  });
  const [railPreviewHtml, setRailPreviewHtml] = useState('');
  // The author's front and back matter, fetched from the server.
  //
  // Both previews here are built from the editor's own DOM, which only ever holds
  // the manuscript — the structure lives on the draft, so until this was fetched
  // there was no way for an author to see the sections they had designed.
  const [bookMatter, setBookMatter] = useState({ frontMatter: '', backMatter: '' });

  // The trim size this book prints at. Owned by the draft, not the toolbar: it
  // decides what the PDF prints and what size cover the book needs.
  const [trimSize, setTrimSize] = useState(DEFAULT_BOOK_SIZE);
  // Set while the author is being asked to confirm a re-flow.
  const [pendingTrimSize, setPendingTrimSize] = useState(null);
  const [railPreviewRange, setRailPreviewRange] = useState(null);
  const [pageMargins, setPageMargins] = useState({ top: 48, right: 48, bottom: 48, left: 48 });
  // Current paper size in px — drives the ruler's dimensions/limits; kept in
  // sync with the --rv-page-width/--rv-page-height CSS vars by onPageSetupChange.
  const [pageSize, setPageSize] = useState({ width: 816, height: 1056 });
  // Real rendered page geometry, measured from the live DOM (see the geometry
  // effect below). Header/footer heights depend on box-sizing (MUI border-box)
  // and on whether the user toggled them off, so they cannot be hardcoded —
  // the ruler reads these to place its ticks, margin handles and limits.
  const [pageGeom, setPageGeom] = useState({ width: 816, height: 1056, headerH: 41, footerH: 41 });

  // ── Two-page spread ────────────────────────────────────────────────────────
  // A spread is a layout change, not a second renderer: .editable-content is a
  // column of .rv-page sheets, so laying it out as a two-column grid pairs the
  // pages up and leaves every page's own rendering, pagination and editing
  // untouched. Page operations all walk DOM order rather than layout position,
  // so none of them can tell the difference.
  //
  // The grid is two columns unconditionally rather than a wrapping row sized to
  // fit two pages: the container is not reliably flex to begin with (see the
  // .is-spread rules in EditorPage.css), and a layout that depends on two pages
  // exactly fitting collapses to one per row the moment anything nudges a
  // page's width.
  //
  // .is-spread zeroes the page margins, so a grid row is exactly one page tall
  // and consecutive rows touch — there is no band between them to show the
  // workspace through. That makes the spread stride differ from the
  // single-page column, which keeps its 48px PAGE_GAP, so the status bar hands
  // pageStrideFor() a gap of 0 while isSpread (see pageStridePx below). Beyond
  // the stride, only the mapping between rows and page numbers changes.
  const isSpread = pageViewMode === 'spread';
  // Logical px between the two pages — must match .is-spread's column-gap.
  // Zero since the spread became a fold rather than a gutter: the sheets meet
  // and a 1px inset hairline separates them, so there is no longer any gap for
  // the zoom sizer to reserve. Kept as a named constant rather than dropped
  // from the sum below, because it is the thing that would have to change
  // again if the gutter ever comes back, and the CSS points at it by name.
  const SPREAD_GAP = 0;
  // pageGeom.width comes from offsetWidth, which is an integer, but a paper
  // size can have a fractional CSS width (A4 is 8.27in = 793.92px). Rounding
  // down leaves the zoom sizer a hair narrower than the grid it has to hold,
  // which is enough to raise a scrollbar over a spread that actually fits. The
  // layout itself no longer depends on this — the grid gives two columns
  // whatever the arithmetic says — so this is only about that last sub-pixel.
  const SPREAD_SLACK = 2;
  const pagesPerRow = isSpread ? 2 : 1;
  // Logical width of the laid-out content. Everything that reasons about how
  // wide the document is — the zoom sizer's scroll area, fit-width/fit-page —
  // measures this rather than a single page.
  const layoutWidth = isSpread
    ? pageGeom.width * 2 + SPREAD_GAP + SPREAD_SLACK
    : pageGeom.width;
  // The rulers describe ONE page's margins and have no meaning against two
  // pages side by side, so they stand down in spread view. showRuler itself is
  // left alone, so the user's preference returns with single-page view.
  const rulersVisible = showRuler && !isSpread;
  // Real, live count of .rv-page elements — the vertical ruler needs this to
  // size its scrollable tape to the true document length instead of a
  // hardcoded page ceiling.
  const [pageCount, setPageCount] = useState(1);
  // Holds the latest measurement fn so imperative callers (header/footer
  // toggles) can force a re-measure without waiting for an observer.
  const measurePageGeomRef = useRef(() => {});

  // Zoom — display scale, kept separate from the document's logical geometry.
  // 'fit-width' recomputes automatically to the largest scale (capped at 100%)
  // that fits the page width in the viewport; a number is an explicit user zoom.
  // pageGeom above stays in real/logical px always — only paint is scaled, so
  // pagination, export, and the ruler's inch math are untouched by zoom.
  const [zoomMode, setZoomMode] = useState('fit-width');
  const [zoom, setZoom] = useState(1); // resolved numeric scale, always in sync with zoomMode
  // Natural (unscaled) height of .editable-content, measured live — see the
  // measurement effect below. Together with pageGeom.width this is what the
  // zoom sizer reserves scroll space for.
  const [contentHeight, setContentHeight] = useState(1056);

  const [paraIndents, setParaIndents] = useState({ left: 0, firstLine: 0, right: 0 });
  const [editorScrollTop, setEditorScrollTop] = useState(0);
  const [editorScrollLeft, setEditorScrollLeft] = useState(0);

  // Table context menu state
  const [tableContextMenu, setTableContextMenu] = useState(null); // { x, y, table, cell }
  const [imageMenu, setImageMenu] = useState(null); // { x, y, span } — span is the image's first piece

  // Image enlargement modal
  const [enlargedImage, setEnlargedImage] = useState(null); // { src, alt }
  
  // Tools panel modals
  const [showSpellCheck, setShowSpellCheck] = useState(false);
  const [showWordCount, setShowWordCount] = useState(false);
  const [showCitations, setShowCitations] = useState(false);
  // Tool tab — continuous line-number gutter (Google-Docs style)
  const [showLineNumbers, setShowLineNumbers] = useState(false);
  // Live word count shown in the header, refreshed as the document changes.
  const [liveWordCount, setLiveWordCount] = useState(0);
  const [liveChapterWords, setLiveChapterWords] = useState([]);

  // Paste options menu state
  const [pasteMenu, setPasteMenu] = useState(null); // { x, y, html, text }

  // Track current formatting at cursor position
  const [currentFormatting, setCurrentFormatting] = useState({
    fontFamily: 'Arial',
    fontSize: '14px',
    bold: false,
    italic: false,
    underline: false,
    textAlign: 'left'
  });

  // Title editing state
  const [editableTitle, setEditableTitle] = useState('');
  const titleSaveTimerRef = useRef(null);

  const saveImagesDebounced = useRef(null);
  // { draftId, key }: the widget list the server already has.
  const savedImagesRef = useRef(null);
  // { draftId, key, body }: a change waiting out the debounce.
  const pendingImagesRef = useRef(null);
  const imagesTokenRef = useRef(null);

 const captureSelection = () => {
  try {
    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
      // Clone the range for iOS Safari compatibility
      const clonedRange = range.cloneRange();
      setSavedRange(clonedRange);
      
      // Also store selection boundaries for iOS Safari
      setSelectionData({
        startContainer: range.startContainer,
        startOffset: range.startOffset,
        endContainer: range.endContainer,
        endOffset: range.endOffset,
      });
      
      // NEW: remember the current .rv-page-body so actions work even if focus leaves the editor
      const b = getCurrentPageBodyFromSelection?.();
      if (b) lastBodyRef.current = b;
    }
  } catch { /* noop */ }
};


// Resolve which block (and therefore which chapter) a selection sits in.
// Uses startContainer rather than commonAncestorContainer so a selection that
// spans several blocks anchors to where it STARTS, instead of collapsing to the
// page body and losing the anchor entirely.
const anchorFromRange = (range) => {
  const editor = editorRef.current;
  if (!editor || !range) return null;
  try {
    const node = range.startContainer;
    const el = node.nodeType === 1 ? node : node.parentElement;
    const block = el?.closest?.('.rv-page-body > *');
    if (!block || block.classList?.contains('rv-end-spacer')) return null;
    // Only HEADINGS carry a data-block-id by default — ensureBlockId is
    // otherwise only called when styling a block as a heading. Body
    // paragraphs, which is where nearly every comment is actually made, had
    // no id at all, so the anchor came back null and the comment fell into
    // "Unassigned / Unplaced". Stamp an id on demand instead.
    const blockId = ensureBlockId(block);
    if (!blockId) return null;
    return { blockId, chapterId: getChapterHeadingId(editor, block) };
  } catch {
    return null;
  }
};

// Show/hide the floating "+ Comment" chip based on the current selection.
// Called after selection-changing events. When there's a non-collapsed
// selection inside the editor with real text, we anchor a chip just above it.
const updateCommentChip = () => {
  try {
    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      setCommentChip((c) => (c ? null : c));
      return;
    }
    const range = sel.getRangeAt(0);
    const host = editorRef.current;
    if (!host || !host.contains(range.commonAncestorContainer)) {
      setCommentChip((c) => (c ? null : c));
      return;
    }
    const text = sel.toString().trim();
    if (text.length < 2) {
      setCommentChip((c) => (c ? null : c));
      return;
    }
    const rect = range.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) {
      setCommentChip((c) => (c ? null : c));
      return;
    }
    // Capture the durable anchor NOW, while the selection is still live —
    // by the time the composer is submitted the caret has long since moved.
    // Both lookups are cheap DOM traversals; the line number costs a layout
    // measurement, so it's deferred to openCommentForSelection.
    const anchor = anchorFromRange(range);

    setCommentChip({
      text,
      top: Math.max(rect.top - 8, 8), // a hair above the selection
      left: rect.left + rect.width / 2,
      blockId: anchor?.blockId || null,
      chapterId: anchor?.chapterId || null,
    });
  } catch {
    setCommentChip((c) => (c ? null : c));
  }
};

// Chip clicked: open the sidebar + composer and seed the selection as the quote.
const openCommentForSelection = () => {
  setCommentChip((c) => {
    const text = c?.text || '';
    if (text) {
      // Snapshot the line number the block currently renders on. It's a
      // display fallback only — the Collab panel re-measures live, because
      // line numbers move as the document is edited.
      let lineHint = null;
      if (c?.blockId && editorRef.current) {
        try {
          const n = buildLineIndex(editorRef.current).get(c.blockId);
          lineHint = n == null ? null : n;
        } catch { /* measurement is best-effort */ }
      }
      // That block id may have just been stamped onto a paragraph that had
      // none, so persist it — otherwise it's regenerated on reload and the
      // comment's anchor no longer resolves.
      if (c?.blockId) triggerChange();

      setPendingComment({
        quote: text,
        blockId: c?.blockId || null,
        chapterId: c?.chapterId || null,
        lineHint,
      });
      // Comments live in the Collab tab now — there's no separate overlay.
      setActiveSidebarPanel('collab');
      setCollabView('comments');
    }
    return null;
  });
};

const restoreIntoEditor = () => {
  if (!savedRange || !editorRef.current) return false;
  editorRef.current.focus();
  try {
    const sel = window.getSelection?.();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(savedRange);
    return true;
  } catch {
    return false;
  }
};
  const [showInsertPage, setShowInsertPage] = useState(false);
  const [textboxes, setTextboxes] = useState([]);

// Page numbering config + modal (persist per-draft)
//
// Numbering is ON by default. It used to be off, which meant a manuscript
// opened without page numbers and every author had to go and find
// Insert > Page Number to switch them on. A numbered page is what a
// manuscript looks like; an unnumbered one is the special case, so the
// default now matches the expectation rather than the implementation.
//
// `enabled` is still stored, so an author who turns numbering off keeps it
// off: the saved config wins over this default on every later visit. Only a
// draft that has never had the setting touched picks it up.
const [showPageNumberModal, setShowPageNumberModal] = useState(false);
const [pageNumberCfg, setPageNumberCfg] = useState(() => {
  // DEFAULTS_V is why a stored config does not simply win.
  //
  // The persist effect below writes pageNumberCfg on mount, not only when the
  // author changes something. So every draft anyone has ever opened already
  // has `enabled: false` sitting in localStorage — the old default, echoed
  // back to storage before the author touched anything. Reading that as a
  // preference would mean the new default never reaches a single existing
  // reader, which is precisely the bug that made page numbers look broken.
  //
  // A stored config without the current `v` therefore predates the default
  // and is not evidence of a choice: take the new defaults and keep only the
  // cosmetic settings the author may genuinely have picked. From v2 onward
  // the stored value is a real preference and wins outright, so an author who
  // turns numbering off keeps it off.
  const DEFAULTS_V = 2;
  try {
    const key = `rv.pageNumberCfg.${id || 'draft'}`;
    const saved = JSON.parse(localStorage.getItem(key) || 'null');
    if (saved && saved.v !== DEFAULTS_V) {
      return { ...saved, v: DEFAULTS_V, enabled: true };
    }
    return saved ?? {
      v: DEFAULTS_V,
      enabled: true,
      position: 'bottom-right',
      format: 'arabic',
      fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      fontSize: 12,
    };
  } catch {
    return {
      v: DEFAULTS_V,
      enabled: true,
      position: 'bottom-right',
      format: 'arabic',
      fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      fontSize: 12,
    };
  }
});
// The live config, readable from a stale closure.
//
// applyPageNumbers is re-created every render, but it is not only called from
// fresh code: the MutationObserver that keeps numbers in step with pagination
// is registered once with [] deps, so it holds the FIRST render's copy for the
// life of the page — the one where `enabled` is still false. Turning numbering
// on used to paint the numbers and then immediately erase them: the paint is a
// DOM change, the DOM change woke that observer, and the observer re-ran the
// first-render closure, which set every number to display:none again. The
// numbers were there for one frame. Only a reload fixed it, because then the
// first render already read enabled:true out of localStorage.
//
// Reading the config through a ref makes every closure, however old, see the
// current settings. Assigned during render rather than in an effect so it is
// already correct by the time any effect or observer callback runs.
const pageNumberCfgRef = useRef(pageNumberCfg);
pageNumberCfgRef.current = pageNumberCfg;
const handleOpenPageNumber = () => setShowPageNumberModal(true);

// Roman helpers + formatter
const toRoman = (num) => {
  if (num <= 0) return '';
  const map = [[1000,'M'],[900,'CM'],[500,'D'],[400,'CD'],[100,'C'],[90,'XC'],[50,'L'],[40,'XL'],[10,'X'],[9,'IX'],[5,'V'],[4,'IV'],[1,'I']];
  let res = '';
  for (const [v,s] of map) { while (num >= v) { res += s; num -= v; } }
  return res;
};
const formatPageIndex = (i, fmt) => {
  const n = i + 1;
  if (fmt === 'roman-upper') return toRoman(n);
  if (fmt === 'roman-lower') return toRoman(n).toLowerCase();
  return String(n);
};

// Ensure a sibling .rv-page-number (outside body)
const ensureNumberContainer = (pageEl) => {
  let box = pageEl.querySelector('.rv-page-number');
  if (!box) {
    box = pageEl.ownerDocument.createElement('div');
    box.className = 'rv-page-number';
    box.setAttribute('contenteditable', 'false');
    pageEl.appendChild(box);
  }
  return box;
};

// Apply numbering to all pages (with inline position fallback)
const applyPageNumbers = () => {
  const editor = editorRef.current;
  if (!editor) return;

  const pages = editor.querySelectorAll('.rv-page');
  if (!pages.length) {
    // Pages not laid out yet — try on next frame
    requestAnimationFrame(applyPageNumbers);
    return;
  }

  const setInlinePosition = (el, pos) => {
    // reset any previous inline position
    el.style.top = '';
    el.style.bottom = '';
    el.style.left = '';
    el.style.right = '';
    el.style.transform = '';

    switch (pos) {
      case 'top-left':
        el.style.top = '8px'; el.style.left = '16px'; break;
      case 'top-middle':
        el.style.top = '8px'; el.style.left = '50%'; el.style.transform = 'translateX(-50%)'; break;
      case 'top-right':
        el.style.top = '8px'; el.style.right = '16px'; break;
      case 'bottom-left':
        el.style.bottom = '8px'; el.style.left = '16px'; break;
      case 'bottom-middle':
        el.style.bottom = '8px'; el.style.left = '50%'; el.style.transform = 'translateX(-50%)'; break;
      default: // 'bottom-right'
        el.style.bottom = '8px'; el.style.right = '16px'; break;
    }
  };

  const cfg = pageNumberCfgRef.current;

  pages.forEach((page, idx) => {
    const box = ensureNumberContainer(page);
    const show = !!cfg.enabled;

    // content + style
    box.textContent = show ? formatPageIndex(idx, cfg.format) : '';
    box.style.fontFamily = cfg.fontFamily;
    box.style.fontSize = `${cfg.fontSize}px`;

    // both data-attr (for CSS) and inline fallback (for robustness)
    const pos = cfg.position || 'bottom-right';
    box.dataset.position = pos;
    setInlinePosition(box, pos);

    box.style.display = show ? 'block' : 'none';
  });
};

  const debugPageNumbers = (on = true) => {
  const editor = editorRef.current;
  if (!editor) return;
  editor.querySelectorAll('.rv-page-number').forEach((el) => {
    el.style.outline = on ? '1px dashed red' : '';
    el.style.background = on ? 'rgba(255,0,0,0.06)' : '';
  });
};


 const editorRef = useRef(null);
  const lastBodyRef = useRef(null);
  const overlayRef = useRef(null);
  const mainContentRef = useRef(null);
  // Chapter chosen by an outline click, held until the author scrolls for
  // themselves. A ref, not state: the scroll-spy reads it inside a listener
  // that must not be re-bound on every jump.
  const jumpPinRef = useRef(null);
  const currentParaRef = useRef(null);
  
  const selectedTableRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user'));
  const accessToken = localStorage.getItem('rv_access_token');

  // Normalize current user identity for presence payloads
  const currentUserId =
    user?._id || user?.id || user?.auth_sub || null;
  const currentUsername =
    user?.username || user?.name || 'You';

  // ── Goal tab ───────────────────────────────────────────────────────────
  // One copy of the goals, shared by the left panel, the goals page and the
  // right rail. The editor's live count stands in for the server's until the
  // first save records one, and chapters are the outline's top level.
  const goalsApi = useManuscriptGoals(doc?._id);
  const liveWords = useMemo(
    () => ({ total: liveWordCount, chapters: liveChapterWords }),
    [liveWordCount, liveChapterWords],
  );
  const chapterList = useMemo(
    () => outline.filter((n) => n.blockId).map((n) => ({ id: n.blockId, title: n.text })),
    [outline],
  );
  const openGoal = useCallback((id, tab) => {
    setActiveSidebarPanel('goals');
    setGoalsView({ mode: 'detail', id, tab });
    setGoalsListTucked(true);
  }, []);
  // "Continue": back to the words for a writing goal (at its chapter), the
  // checklist for a checklist goal, and one more for anything counted by hand.
  const continueGoal = useCallback((goal) => {
    if (goal.metric === 'tasks') { setGoalsView({ mode: 'detail', id: goal._id, tab: 'tasks' }); return; }
    if (goal.metric === 'images' || goal.metric === 'sessions') { goalsApi.logProgress(goal._id, 1); return; }
    setGoalsView({ mode: 'manuscript' });
    setGoalsListTucked(true);
    if (goal.scope && goal.scope !== 'book') {
      setTimeout(() => {
        editorRef.current?.querySelector(`[data-block-id="${CSS.escape(goal.scope)}"]`)
          ?.scrollIntoView({ block: 'start' });
      }, 0);
    }
  }, [goalsApi]);
  // Reopening the Goal tab starts on the goals page; every return to that page
  // reloads, since words-today moves with each save.
  const goalsOpen = activeSidebarPanel === 'goals';
  useEffect(() => {
    if (!goalsOpen) { setGoalsView({ mode: 'dashboard' }); setGoalsListTucked(false); }
  }, [goalsOpen]);
  useEffect(() => {
    if (goalsOpen && goalsView.mode !== 'manuscript') goalsApi.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalsOpen, goalsView.mode, goalsView.id]);
  // ?goal=<id> (from an inbox notification) opens that goal. A goal this user
  // cannot see, or one since deleted, just lands on the goals page.
  const deepLinkGoalId = searchParams.get('goal');
  useEffect(() => {
    if (!deepLinkGoalId || !doc?._id) return;
    openGoal(deepLinkGoalId);
    const next = new URLSearchParams(searchParams);
    next.delete('goal');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkGoalId, doc?._id]);

  // Who the comment cards let you act as. Both are read off what the editor
  // already has: `roles` is the array the backend checks (`role` is a legacy
  // singular mirror that is not always populated, so reading it alone
  // disagrees with the server), and the manuscript's author is on `doc`.
  const isAdmin = Boolean(
    (Array.isArray(user?.roles) && user.roles.includes('admin')) || user?.role === 'admin'
  );
  const isItemOwner = Boolean(
    currentUserId &&
    String(doc?.author?._id || doc?.author?.id || doc?.author_id || '') === String(currentUserId)
  );

  // ── Manuscript theme ────────────────────────────────────────────────────
  // The look of the author's book (src/utils/manuscriptTheme.js), not the app's
  // own design tokens. The draft carries its own copy of the theme; switching
  // writes a new copy. Only the author may change it, as the API enforces.
  const manuscriptTheme = doc?.manuscriptTheme || null;
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const [themeToast, setThemeToast] = useState('');
  // How many places carry formatting that hides the theme: the rail's count,
  // and (non-zero) the offer to clear them straight after a theme is applied.
  const [themeOverrideCount, setThemeOverrideCount] = useState(0);
  const [themeOverridePrompt, setThemeOverridePrompt] = useState(0);
  const [themeSample, setThemeSample] = useState(null);

  // ── Manuscript template ─────────────────────────────────────────────────
  // Which catalogue template the manuscript follows, and the record of changes
  // to it (backend/routes/drafts.py, "Changing a manuscript's template"). Author
  // only, as the API enforces.
  const templateId = doc?.template_id || null;
  const templateHistory = doc?.template_history || [];
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);
  const [templateToSwitch, setTemplateToSwitch] = useState(null);
  const [templateName, setTemplateName] = useState('');
  useEffect(() => {
    if (!templateId) { setTemplateName(''); return undefined; }
    let cancelled = false;
    listTemplates()
      .then((list) => {
        const t = Array.isArray(list) ? list.find((x) => x.id === templateId) : null;
        if (!cancelled) setTemplateName(t?.name || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [templateId]);

  // Who can be notified about a comment: the draft's author plus its
  // collaborators, minus yourself — notifying yourself is noise, and the
  // one-notify-per-person rule would burn the slot.
  const notifyCandidates = useMemo(() => {
    const seen = new Set();
    const out = [];
    const add = (u) => {
      const uid = String(u?._id || u?.id || '');
      if (!uid || uid === String(currentUserId || '') || seen.has(uid)) return;
      seen.add(uid);
      out.push(u);
    };
    if (doc?.author) add(doc.author);
    (collaborators || []).forEach(add);
    return out;
  }, [doc?.author, collaborators, currentUserId]);

  // --- Hooks: autosave + Socket.IO ---
  const paginateRef = useRef(null); // set after paginateAllPages is defined
  // True only once this draft's content is in the editor. Autosave refuses to
  // write anything before that: when the load fails the editor is a blank
  // page, and saving it replaced a real manuscript with nothing.
  const draftLoadedRef = useRef(false);
  const [loadError, setLoadError] = useState('');
  // Unsaved work a previous visit left in this browser: { html, ts, sameBase }.
  const [recovery, setRecovery] = useState(null);
  const {
    saveStatus, triggerChange, forceSave, noteServerContent, getServerHash,
    connectionLost, localBackupOk, visitId,
  } = useEditorAutosave(id, editorRef, accessToken, draftLoadedRef);
  const recoveryTimerRef = useRef(null);

  // Offer what an earlier visit left unsaved in this browser. A copy younger
  // than 15 s is most likely another open tab mid-save, so it is looked at
  // again once it has aged rather than offered now — it is never overwritten
  // meanwhile, since every visit writes under its own key.
  const checkForLeftovers = useCallback((draftId) => {
    if (!draftLoadedRef.current) return;
    try {
      const { offer, youngPending } = findLeftoverDrafts(draftId, { exceptVisit: visitId, minAgeMs: 15000 });
      const serverHash = getServerHash();
      if (offer && hashContent(offer.html) === serverHash) {
        // It did reach the server after all.
        clearLeftoverDrafts(draftId, visitId, { upToTs: offer.ts });
      } else if (offer) {
        setRecovery((cur) => cur || { html: offer.html, ts: offer.ts, sameBase: offer.baseHash === serverHash });
      }
      // A younger copy is looked at again whatever else was found: it may be
      // newer work than the one on offer.
      if (youngPending) {
        if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
        recoveryTimerRef.current = setTimeout(() => checkForLeftovers(draftId), 16000);
      }
    } catch {}
  }, [visitId, getServerHash]);
  useEffect(() => () => {
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
  }, [id]);

  // While the load has failed the page is not the manuscript: take it out of
  // reach (inert blocks focus, typing and clicks), as the banner promises.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (loadError) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  }, [loadError]);

  // Leaving the editor flushes to the server first. The autosave's own
  // unmount flush is a keepalive PUT, which caps at ~64 KB; a
  // full-length manuscript exceeds that and the save is dropped silently,
  // losing up to the 60 s server-sync interval. So every exit saves
  // explicitly rather than relying on that fallback. Silent, because the
  // author is navigating, not saving — a toast here would be noise.
  const saveThenGo = useCallback(async (to, options) => {
    await forceSave(true);
    navigate(to, options);
  }, [forceSave, navigate]);

  // Ctrl+S / Cmd+S → immediate server save
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        forceSave();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [forceSave]);

  // Ctrl+Z / Cmd+Z — when the last action was a structural chapter op (which the
  // browser's native undo can't reverse), restore our snapshot instead. The flag
  // is cleared the moment the user types, so ordinary typing-undo is untouched.
  // Capture phase so we intercept before contentEditable's own handler.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        if (lastStructuralRef.current && structuralUndoRef.current.length) {
          e.preventDefault();
          e.stopPropagation();
          undoStructural();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Alt+↑ / Alt+↓ — reorder the chapter the caret is in among its same-level
  // siblings, without touching the mouse. A reliable alternative to drag.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const editor = editorRef.current;
      if (!editor) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const anchor = sel.anchorNode;
      if (!anchor || !editor.contains(anchor)) return;
      const fromEl = anchor.nodeType === 1 ? anchor : anchor.parentElement;
      const headingId = getChapterHeadingId(editor, fromEl);
      if (!headingId) return;
      const dir = e.key === 'ArrowUp' ? 'up' : 'down';
      const siblingId = getSiblingChapterId(editor, headingId, dir);
      if (!siblingId) return;
      e.preventDefault();
      e.stopPropagation();
      moveChapter(headingId, siblingId, dir === 'up' ? 'before' : 'after');
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination debug surface.
  //   __rv_pauseAuto()       — freeze auto-pagination.
  //   __rv_resumeAuto()      — un-freeze it.
  //   __rv_reflow()          — run a one-shot reflow now.
  //   __rv_setReflow(true)   — auto-pagination uses the new reflow (default).
  //   __rv_setReflow(false)  — auto-pagination falls back to the legacy
  //                            800ms-debounced cascade.
  useEffect(() => {
    window.__rv_reflow = () => {
      const t0 = performance.now();
      reflowDocument();
      console.log(`[reflow] done in ${(performance.now() - t0).toFixed(1)}ms`);
    };
    window.__rv_pauseAuto = () => {
      skipAutoPaginationRef.current = true;
      console.log('[reflow] auto-pagination paused');
    };
    window.__rv_resumeAuto = () => {
      skipAutoPaginationRef.current = false;
      console.log('[reflow] auto-pagination resumed');
    };
    window.__rv_setReflow = (on) => {
      useReflowRef.current = !!on;
      console.log(`[reflow] auto-pagination path: ${on ? 'reflow (rAF)' : 'legacy (800ms)'}`);
    };
    return () => {
      delete window.__rv_reflow;
      delete window.__rv_pauseAuto;
      delete window.__rv_resumeAuto;
      delete window.__rv_setReflow;
    };
  });

  // Record an incoming collaborator edit (which paragraph + author) so it can be
  // tinted. Edits are keyed by paragraphIndex, not text, so that two people who
  // edit the SAME paragraph both stay attributed to it (their colors blend)
  // rather than the later editor's text erasing the earlier one's match.
  // Dedupes when the same user re-edits the same paragraph back-to-back, and
  // caps the list length.
  const handleRemoteEdit = useCallback(({ userId, username, text, raw, paragraphIndex }) => {
    if (!Number.isInteger(paragraphIndex) || paragraphIndex < 0) return;
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      userId,
      username,
      text: (text || '').trim(),
      raw: typeof raw === 'string' ? raw : (text || ''),
      paragraphIndex,
    };
    setCollabEdits((prev) => {
      const last = prev[prev.length - 1];
      // Same author still editing the same paragraph: replace the last event in
      // place so we keep the LATEST text (needed for accurate replay) without
      // growing the list on every keystroke.
      if (last && last.userId === userId && last.paragraphIndex === paragraphIndex) {
        if (last.raw === entry.raw) return prev; // nothing changed
        const copy = prev.slice(0, -1);
        copy.push(entry);
        return copy;
      }
      const next = [...prev, entry];
      return next.length > COLLAB_EDITS_CAP ? next.slice(next.length - COLLAB_EDITS_CAP) : next;
    });
  }, []);

  // Record the LOCAL user's own edit (the paragraph their caret is in) into the
  // same collab-edit list, attributed to the local user. Remote clients only
  // ever hear about OTHER users over the socket, so without this a paragraph
  // touched by two people would only ever show one color on any given screen.
  // Recording our own edits — keyed by the same paragraphIndex the socket diff
  // uses — lets a jointly-edited paragraph blend both authors' colors here too.
  const recordLocalEdit = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const anchor = sel.anchorNode;
    if (!anchor || !editor.contains(anchor)) return;
    const startEl = anchor.nodeType === 3 ? anchor.parentElement : anchor;
    const block = startEl?.closest(COLLAB_BLOCK_SEL);
    if (!block) return;
    const raw = block.textContent;
    if (!raw.trim()) return; // don't tint a blank line
    const blocks = Array.from(editor.querySelectorAll(COLLAB_BLOCK_SEL));
    const idx = blocks.indexOf(block);
    if (idx < 0) return;
    handleRemoteEdit({
      userId: currentUserId ? String(currentUserId) : null,
      username: currentUsername,
      text: raw.trim(),
      raw,
      paragraphIndex: idx,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleRemoteEdit, currentUserId, currentUsername]);

  const { onlineUsers, emitDraftUpdate, storyElementsRev } = useEditorSocket({
    docId: doc?._id,
    editorRef,
    setContent,
    paginateRef,
    currentUserId,
    currentUsername,
    onRemoteEdit: handleRemoteEdit,
  });

  // --- Soft pagination (new) ---
const paginatingRef = useRef(false);
const paginateTimerRef = useRef(null);
const paginateRAFRef = useRef(null); // rAF handle for reflow-based pagination
const skipAutoPaginationRef = useRef(false); // Flag to skip auto-pagination during manual ops
const forcePaginateRef = useRef(false); // next pagination re-lays out even if nothing overflows
// Feature flag: when true, paginateAllPages uses the single-pass reflow on
// requestAnimationFrame. When false, falls back to the legacy 800ms-debounced
// moveTrailingBlockToNextPage cascade. Toggle at runtime via __rv_setReflow().
const useReflowRef = useRef(true);
// Layout rules beyond "fill the page": a manuscript theme starts each chapter
// on a new page. Read by reflowDocument; see src/utils/paginate.js.
const reflowOptionsRef = useRef({});
// Debounce handle for the formatting-marks ("Show Non-Printing Characters")
// decoration pass. Marks are rebuilt only when editing/pagination settles.
const marksTimerRef = useRef(null);
// Debounce handle for the comment-highlight decoration pass.
const commentHlTimerRef = useRef(null);
// Debounce handle for the collaborator-edit highlight decoration pass.
const collabHlTimerRef = useRef(null);
// Debounce handle for the element-recognition pass, the board nodes it reads
// (a ref, so the pagination closure sees the latest), and whether an IME
// composition is open — the pass waits for it, since re-noding the text under
// a composition cancels it.
const elementHlTimerRef = useRef(null);
const storyNodesRef = useRef([]);
const composingRef = useRef(false);
// Debounce handle + latest-state ref for the line-number gutter pass.
const lineNumbersTimerRef = useRef(null);
// Debounce handle for the Collab index's line-number measurement pass.
const lineIndexTimerRef = useRef(null);
const showLineNumbersRef = useRef(false);
// Refs so the debounced highlight pass reads the LATEST toggle/data state
// without re-creating the closure (paginateAllPages captures it once).
const showCommentHighlightsRef = useRef(false);
// What the decoration pass reads: the Collab panel's comments while it's open,
// otherwise the View toggle's own fetch.
const decorationCommentsRef = useRef([]);
// data-block-id of the passage whose comment is selected in Collab, or null.
const focusedCommentBlockRef = useRef(null);
const draftCommentsRef = useRef([]);
const showCollabEditsRef = useRef(false);
const collabEditsRef = useRef([]);

// Migrate old page structure to include headers and footers
const migratePageStructure = () => {
  if (!editorRef.current) return;
  const pages = editorRef.current.querySelectorAll('.rv-page');
  
  pages.forEach(page => {
    // Check if page already has header and footer
    const hasHeader = page.querySelector('.rv-page-header');
    const hasFooter = page.querySelector('.rv-page-footer');
    
    if (!hasHeader || !hasFooter) {
      const body = page.querySelector('.rv-page-body');
      if (!body) return;
      
      // Create header if missing
      if (!hasHeader) {
        const header = document.createElement('div');
        header.className = 'rv-page-header';
        header.setAttribute('contenteditable', 'false');
        header.innerHTML = '<div class="rv-header-content"></div>';
        page.insertBefore(header, body);
      }
      
      // Create footer if missing
      if (!hasFooter) {
        const footer = document.createElement('div');
        footer.className = 'rv-page-footer';
        footer.setAttribute('contenteditable', 'false');
        footer.innerHTML = '<div class="rv-footer-content"></div>';
        page.appendChild(footer);
      }
    }
  });
};

const createEmptyPage = (doc) => {
  const page = doc.createElement('section');
  page.className = 'rv-page';
  page.setAttribute('data-type', 'page');

  const header = doc.createElement('div');
  header.className = 'rv-page-header';
  header.setAttribute('contenteditable', 'false');
  header.innerHTML = '<div class="rv-header-content"></div>';

  const body = doc.createElement('div');
  body.className = 'rv-page-body';
  body.setAttribute('contenteditable', 'true');
  body.innerHTML = '<p><br/></p><p class="rv-end-spacer" contenteditable="false"><br/></p>';

  const footer = doc.createElement('div');
  footer.className = 'rv-page-footer';
  footer.setAttribute('contenteditable', 'false');
  footer.innerHTML = '<div class="rv-footer-content"></div>';

  page.appendChild(header);
  page.appendChild(body);
  page.appendChild(footer);
  return page;
};

const paginateIfOverflow = (pageBody) => {
  if (!pageBody || paginatingRef.current) return false;

  if (!isBodyOverflowing(pageBody)) return false;

  paginatingRef.current = true;
  
  // Save caret position before pagination
  const caretMarker = saveCaretPosition();
  
  try {
    // Use the improved moveTrailingBlockToNextPage which handles splitting
    moveTrailingBlockToNextPage(pageBody, 0);
    return true;
  } finally {
    // Restore caret position after pagination
    if (caretMarker) {
      restoreCaretPosition(caretMarker);
    }
    setTimeout(() => (paginatingRef.current = false), 0);
  }
};

  const handleBookCoverClick = () => {
    saveThenGo(`/editor/${id}/cover`);
  };

  // --- Paging helpers: create next page, caret checks, and soft overflow move ---

// Create an empty page element

// Return the .rv-page-body that contains the caret (or null)
const getCurrentPageBodyFromSelection = () => {
  const host = editorRef.current;
  if (!host) return null;
  const sel = document.getSelection?.();
  if (!sel || sel.rangeCount === 0) return null;
  const node = sel.anchorNode;
  if (!node) return null;
  const body = (node.nodeType === 1 ? node : node.parentElement)?.closest?.('.rv-page-body');
  return body || null;
};

// A paragraph holding an image has no text but is not empty. Counting it as
// empty let the page-merge filler cleanup delete the image at a page edge \u2014
// which is exactly where the first piece of a running image always sits.
const isEmptyParagraph = (el) =>
  el &&
  el.tagName === 'P' &&
  !el.querySelector('img') &&
  (el.textContent.replace(/\u200B/g, '').trim() === '' || /^\s*<br\/?>\s*$/.test(el.innerHTML || ''));

// Check if caret is at the very start of the body
const isCaretAtStartOfBody = (body) => {
  const sel = document.getSelection?.();
  if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  
  // Check if we're at offset 0 of the first text node or first element
  try {
    const testRange = document.createRange();
    testRange.selectNodeContents(body);
    testRange.collapse(true); // collapse to start
    return range.compareBoundaryPoints(Range.START_TO_START, testRange) === 0;
  } catch {}
  
  return false;
};

// Walk back from the end to find the last *contentful* block
const getLastContentBlock = (body) => {
  let el = body.lastElementChild;
  while (el && (isEmptyParagraph(el) || el.classList?.contains('rv-end-spacer'))) {
    el = el.previousElementChild;
  }
  return el || body.lastElementChild; // fallback to the last element (may be empty)
};

// Logical end-of-content check. Compares against the end of the last
// *content* block, skipping the non-editable `.rv-end-spacer` (and any
// trailing empty paragraphs) that always sits at the tail of a body.
// Comparing against the body's own end would always return false because
// the spacer is logically after the caret.
const isCaretAtEndOfBody = (body) => {
  const sel = document.getSelection?.();
  if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!body.contains(range.startContainer)) return false;

  try {
    let lastBlock = body.lastElementChild;
    while (
      lastBlock &&
      (lastBlock.classList?.contains('rv-end-spacer') ||
        (lastBlock.tagName === 'P' &&
          lastBlock.textContent.trim() === '' &&
          !lastBlock.querySelector('img, table, .rv-shape, .rv-textbox')))
    ) {
      lastBlock = lastBlock.previousElementSibling;
    }

    // Empty body (or only spacers/empty paragraphs): caret is trivially at end.
    if (!lastBlock) return true;

    const endRange = range.cloneRange();
    endRange.selectNodeContents(lastBlock);
    endRange.collapse(false);
    return range.compareBoundaryPoints(Range.END_TO_END, endRange) >= 0;
  } catch {
    return false;
  }
};

// Resolve a viewport point to a DOM range across browsers.
const rangeFromViewportPoint = (x, y) => {
  if (document.caretRangeFromPoint) {
    return document.caretRangeFromPoint(x, y);
  }
  if (document.caretPositionFromPoint) {
    const pos = document.caretPositionFromPoint(x, y);
    if (!pos) return null;
    const r = document.createRange();
    r.setStart(pos.offsetNode, pos.offset);
    r.collapse(true);
    return r;
  }
  return null;
};

// True when ArrowDown from the current caret cannot land on another line
// inside `body` — i.e. the caret is visually on the last line of this page.
// Lets us hop to the next page even when the caret is mid-paragraph but
// visually on the last rendered line.
const isCaretAtVisualEnd = (body) => {
  const sel = document.getSelection?.();
  if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!body.contains(range.startContainer)) return false;

  // Logical end always counts as visual end.
  if (isCaretAtEndOfBody(body)) return true;

  try {
    const rects = range.getClientRects();
    const caretRect = rects[rects.length - 1] || range.getBoundingClientRect();
    if (!caretRect || (caretRect.top === 0 && caretRect.bottom === 0)) return false;

    // Probe roughly one line below the caret. 8px is enough to clear the
    // current line's descender without skipping a short next line.
    const probe = rangeFromViewportPoint(caretRect.left, caretRect.bottom + 8);
    if (!probe) return true;

    // Probe escaped the editable body (e.g. into the page footer or beyond).
    if (!body.contains(probe.startContainer)) return true;

    // Probe landed in the non-editable end spacer — treat as past last line.
    const probeEl = probe.startContainer.nodeType === 1
      ? probe.startContainer
      : probe.startContainer.parentElement;
    if (probeEl?.closest?.('.rv-end-spacer')) return true;

    // Probe resolved to the same line (didn't advance vertically).
    const probeRect = probe.getBoundingClientRect();
    if (probeRect.top <= caretRect.top + 2) return true;

    return false;
  } catch {
    return false;
  }
};

// True when ArrowUp from the current caret cannot land on another line
// inside `body` — i.e. the caret is visually on the first line of this page.
const isCaretAtVisualStart = (body) => {
  const sel = document.getSelection?.();
  if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!body.contains(range.startContainer)) return false;

  if (isCaretAtStartOfBody(body)) return true;

  try {
    const rects = range.getClientRects();
    const caretRect = rects[0] || range.getBoundingClientRect();
    if (!caretRect || (caretRect.top === 0 && caretRect.bottom === 0)) return false;

    const probe = rangeFromViewportPoint(caretRect.left, caretRect.top - 8);
    if (!probe) return true;

    if (!body.contains(probe.startContainer)) return true;

    const probeRect = probe.getBoundingClientRect();
    if (probeRect.bottom >= caretRect.bottom - 2) return true;

    return false;
  } catch {
    return false;
  }
};
// Ensure a next page exists (returns the next page body)
const ensureNextPageBody = (currentPageEl) => {
  const doc = currentPageEl.ownerDocument;
  let next = currentPageEl.nextElementSibling;
  if (!(next && next.classList.contains('rv-page'))) {
    const newPage = createEmptyPageEl(doc);
    currentPageEl.insertAdjacentElement('afterend', newPage);
    next = newPage;
  }
  return next.querySelector('.rv-page-body');
};

// Paginate all overflowing pages in the document
// Cheap check: do we actually have layout work to do?
const paginationWorkNeeded = () => {
  const editor = editorRef.current;
  if (!editor) return false;
  const bodies = Array.from(editor.querySelectorAll('.rv-page-body'));
  if (bodies.some(isBodyOverflowing)) return true;

  const pages = Array.from(editor.querySelectorAll('.rv-page'));
  if (pages.length <= 1) return false;

  // Trailing empty page → reflow will drop it.
  const last = pages[pages.length - 1].querySelector('.rv-page-body');
  const lastHasContent =
    last &&
    Array.from(last.children).some(
      (el) => !el.classList?.contains('rv-end-spacer')
    );
  if (!lastHasContent) return true;

  // A running image's piece always reaches the bottom of its page. When the
  // text above it gets shorter, the gap it leaves is any size at all, so it
  // gets its own check rather than the 100px backflow threshold below.
  for (let i = 0; i < pages.length - 1; i++) {
    const body = pages[i].querySelector('.rv-page-body');
    if (body && bleedPieceHasRoom(body)) return true;
  }

  // Backflow opportunity → a non-last page has significant unused vertical
  // space AND a later page has content that could move back.
  for (let i = 0; i < pages.length - 1; i++) {
    const body = pages[i].querySelector('.rv-page-body');
    if (!body) continue;
    const freeSpace = body.clientHeight - body.scrollHeight;
    if (freeSpace < 100) continue; // ignore small gaps to avoid jitter
    for (let j = i + 1; j < pages.length; j++) {
      const laterBody = pages[j].querySelector('.rv-page-body');
      const laterHasContent =
        laterBody &&
        Array.from(laterBody.children).some(
          (el) => !el.classList?.contains('rv-end-spacer')
        );
      if (laterHasContent) return true;
    }
  }

  return false;
};

// `force` skips the "work needed?" check. A page-size change needs a full
// re-layout even when nothing overflows: on a bigger page the content has to
// move back up, which that check does not detect.
const paginateAllPages = ({ force = false } = {}) => {
  if (force) forcePaginateRef.current = true;
  if (!editorRef.current) return;
  if (skipAutoPaginationRef.current) return;
  if (paginatingRef.current) return;

  // ---- New path: single-pass reflow, scheduled on rAF ----
  if (useReflowRef.current) {
    // Coalesce: only one rAF in flight at a time.
    if (paginateRAFRef.current != null) return;
    paginateRAFRef.current = requestAnimationFrame(() => {
      paginateRAFRef.current = null;
      if (skipAutoPaginationRef.current) return;
      if (!editorRef.current) return;
      const forced = forcePaginateRef.current;
      forcePaginateRef.current = false;
      if (!forced && !paginationWorkNeeded()) return;
      paginatingRef.current = true;
      try {
        clearFormattingMarks(editorRef.current);
        clearCommentHighlights(editorRef.current);
        clearCollabHighlights(editorRef.current);
        clearElementHighlights(editorRef.current);
        clearLineNumbers(editorRef.current);
        reflowDocument();
      } finally {
        paginatingRef.current = false;
        scheduleFormattingMarks();
        scheduleCommentHighlights();
        scheduleCollabHighlights();
        scheduleElementHighlights();
        scheduleLineNumbers();
      }
    });
    return;
  }

  // ---- Legacy path: 800ms-debounced trailing-block cascade ----
  if (paginateTimerRef.current) clearTimeout(paginateTimerRef.current);
  paginateTimerRef.current = setTimeout(() => {
    if (skipAutoPaginationRef.current) return;
    if (!editorRef.current) return;

    paginatingRef.current = true;
    clearFormattingMarks(editorRef.current);
    clearCommentHighlights(editorRef.current);
    clearCollabHighlights(editorRef.current);
    clearElementHighlights(editorRef.current);
    clearLineNumbers(editorRef.current);
    const caretMarker = saveCaretPosition();

    try {
      const allBodies = Array.from(editorRef.current.querySelectorAll('.rv-page-body'));
      allBodies.forEach(body => {
        if (isBodyOverflowing(body)) {
          moveTrailingBlockToNextPage(body, 0);
        }
      });
      syncListNumbering();
    } finally {
      if (caretMarker) restoreCaretPosition(caretMarker);
      paginatingRef.current = false;
      scheduleFormattingMarks();
      scheduleCommentHighlights();
      scheduleCollabHighlights();
      scheduleElementHighlights();
      scheduleLineNumbers();
    }
  }, 800);
};

// Keep paginateRef in sync so the Socket.IO hook can call it
paginateRef.current = paginateAllPages;

// ── Manuscript theme: its stylesheet, and the pages laid out in it ───────────
// The theme is a stylesheet over the page area, so the stored HTML never holds
// any of it. The margins land where the theme puts them: the page's header and
// footer bands sit inside the top and bottom margins.
const themeTrimHeightIn = bookSize(trimSize).heightIn;
const themeStyleText = useMemo(() => (manuscriptTheme
  ? themeCss(manuscriptTheme, {
    scope: '.editor-main-content.rv-themed',
    page: { headerPx: pageGeom.headerH, footerPx: pageGeom.footerH },
    trimHeightIn: themeTrimHeightIn,
  })
  : ''), [manuscriptTheme, pageGeom.headerH, pageGeom.footerH, themeTrimHeightIn]);

useEffect(() => {
  reflowOptionsRef.current = themeStyleText ? { breakBefore: isChapterOpening } : {};
  let el = document.getElementById(THEME_STYLE_ID);
  if (!themeStyleText) {
    el?.remove();
  } else {
    if (!el) {
      el = document.createElement('style');
      el.id = THEME_STYLE_ID;
      document.head.appendChild(el);
    }
    el.textContent = themeStyleText;
  }
  // The type, the margins and where chapters start have all changed: lay the
  // pages out again now, and once more when the theme's fonts have arrived.
  let cancelled = false;
  const relayout = () => { if (!cancelled) paginateRef.current?.({ force: true }); };
  relayout();
  document.fonts?.ready?.then(relayout);
  return () => { cancelled = true; };
}, [themeStyleText]);

// The stylesheet belongs to this page and leaves with it.
useEffect(() => () => document.getElementById(THEME_STYLE_ID)?.remove(), []);

// Read off the pages a moment after they change: what still hides the theme,
// and the opening of the first chapter for the rail's thumbnail.
useEffect(() => {
  if (!manuscriptTheme) {
    setThemeOverrideCount(0);
    return undefined;
  }
  const timer = setTimeout(() => {
    const ed = editorRef.current;
    if (!ed) return;
    setThemeOverrideCount(countOverrides(ed));
    const heading = ed.querySelector('.rv-page-body > [data-heading-level="1"]');
    let excerpt = '';
    for (let node = heading?.nextElementSibling; node && !excerpt; node = node.nextElementSibling) {
      if (node.tagName === 'P') excerpt = (node.textContent || '').trim();
    }
    setThemeSample({
      title: heading?.textContent?.trim() || doc?.title || undefined,
      excerpt: excerpt ? excerpt.slice(0, 180) : undefined,
    });
  }, 400);
  return () => clearTimeout(timer);
}, [manuscriptTheme, content, doc?.title]);

// Back from the Theme Editor's "Apply to Manuscript": say so, and once the pages
// are in, offer to clear any old formatting that would hide the theme. That
// needs the live pages, which is why the Theme Editor hands it over to here.
const location = useLocation();
const themeArrivalRef = useRef(false);
useEffect(() => {
  const name = location.state?.themeApplied;
  if (!name || themeArrivalRef.current || !manuscriptTheme) return;
  themeArrivalRef.current = true;
  setThemeToast(`${name} applied to the whole manuscript.`);
  setTimeout(() => {
    if (!editorRef.current) return;
    const hidden = countOverrides(editorRef.current);
    if (hidden) setThemeOverridePrompt(hidden);
    navigate(location.pathname, { replace: true, state: null });
  }, 1200);
}, [location.state, manuscriptTheme]); // eslint-disable-line react-hooks/exhaustive-deps

// ── Formatting marks ("Show Non-Printing Characters") ──────────────────────
// The block currently holding the caret, so the decoration pass can skip it
// (we never mutate the block being edited → the caret is never disturbed).
const getActiveBlock = () => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const node = sel.getRangeAt(0).startContainer;
  const el = node.nodeType === 1 ? node : node.parentElement;
  return el?.closest?.('p, li, h1, h2, h3, h4, h5, h6') || null;
};

// Apply or clear marks immediately, reflecting the current toggle state.
const refreshFormattingMarks = () => {
  const editor = editorRef.current;
  if (!editor) return;
  if (showFormattingMarks) applyFormattingMarks(editor, getActiveBlock());
  else clearFormattingMarks(editor);
};

// Debounced rebuild. Defers while pagination is running (marks must never be
// present during a structural reflow) and while a non-collapsed selection is
// active (so we don't mutate blocks inside the user's selection).
const scheduleFormattingMarks = () => {
  if (marksTimerRef.current) clearTimeout(marksTimerRef.current);
  marksTimerRef.current = setTimeout(() => {
    if (!editorRef.current || !showFormattingMarks) return;
    if (paginatingRef.current) { scheduleFormattingMarks(); return; }
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.anchorNode &&
        editorRef.current.contains(sel.anchorNode)) {
      scheduleFormattingMarks();
      return;
    }
    applyFormattingMarks(editorRef.current, getActiveBlock());
  }, 300);
};

// Debounced rebuild of comment highlights. Mirrors scheduleFormattingMarks:
// defers while pagination runs and while a non-collapsed selection is active
// (so we never wrap text inside the user's selection). Reads latest state via
// refs so the pagination closure always sees current values.
const scheduleCommentHighlights = () => {
  if (commentHlTimerRef.current) clearTimeout(commentHlTimerRef.current);
  commentHlTimerRef.current = setTimeout(() => {
    if (!editorRef.current) return;
    // Two independent reasons to decorate: the View toggle (show every
    // commented passage) and a comment being selected in Collab (show THIS
    // one). Either alone is enough.
    if (!showCommentHighlightsRef.current && !focusedCommentBlockRef.current) {
      clearCommentHighlights(editorRef.current);
      return;
    }
    if (paginatingRef.current) { scheduleCommentHighlights(); return; }
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.anchorNode &&
        editorRef.current.contains(sel.anchorNode)) {
      scheduleCommentHighlights();
      return;
    }
    applyCommentHighlights(
      editorRef.current,
      commentsToDecorate(),
      getActiveBlock(),
      { focusedBlockId: focusedCommentBlockRef.current },
    );
  }, 300);
};

// Which comments the decoration pass should consider.
//
// With the View toggle on, every comment is fair game. With it off but a
// comment selected, only that passage should be marked — otherwise selecting
// one comment would silently switch on the whole annotation layer, which is
// the View tab's job, not Collab's.
const commentsToDecorate = () => {
  const all = decorationCommentsRef.current || [];
  if (showCommentHighlightsRef.current) return all;
  const blockId = focusedCommentBlockRef.current;
  if (!blockId) return [];
  return all.filter((c) => c.block_id === blockId);
};

// Re-measure the block → line-number map for the Collab comment index.
// Must run only when layout has settled: mid-pagination the client rects are
// meaningless and would produce numbers that disagree with the gutter.
const refreshLineIndex = () => {
  if (!editorRef.current) return;
  if (paginatingRef.current) { scheduleLineIndex(); return; }
  try {
    setLineIndex(buildLineIndex(editorRef.current));
    // Built in the same pass: which chapter each block currently sits in.
    // Derived rather than read off the comment, so moving a paragraph between
    // chapters re-files its comments instead of leaving them somewhere stale.
    setChapterMap(buildBlockChapterMap(editorRef.current));
  } catch { /* keep the previous maps rather than blanking the panel */ }
};

// Debounced variant. Measuring walks every block's client rects, so this is
// deliberately lazy and only ever runs while the comment index is open.
const scheduleLineIndex = () => {
  if (lineIndexTimerRef.current) clearTimeout(lineIndexTimerRef.current);
  lineIndexTimerRef.current = setTimeout(() => {
    if (!editorRef.current) return;
    if (paginatingRef.current) { scheduleLineIndex(); return; }
    refreshLineIndex();
  }, 300);
};

// Debounced rebuild of collaborator-edit highlights. Same deferral rules as
// the comment-highlight pass.
const scheduleCollabHighlights = () => {
  if (collabHlTimerRef.current) clearTimeout(collabHlTimerRef.current);
  collabHlTimerRef.current = setTimeout(() => {
    if (!editorRef.current || !showCollabEditsRef.current) return;
    if (paginatingRef.current) { scheduleCollabHighlights(); return; }
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.anchorNode &&
        editorRef.current.contains(sel.anchorNode)) {
      scheduleCollabHighlights();
      return;
    }
    applyCollabHighlights(editorRef.current, collabEditsRef.current, getActiveBlock());
  }, 300);
};

// Debounced rebuild of element recognition (story element names tinted in the
// text). Always on — there is no View toggle — so a name lights up about 300ms
// after typing pauses, the paragraph being typed in included. Same deferral
// rules as the comment-highlight pass, plus an open IME composition.
const scheduleElementHighlights = () => {
  if (elementHlTimerRef.current) clearTimeout(elementHlTimerRef.current);
  elementHlTimerRef.current = setTimeout(() => {
    if (!editorRef.current) return;
    if (paginatingRef.current || composingRef.current) { scheduleElementHighlights(); return; }
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.anchorNode &&
        editorRef.current.contains(sel.anchorNode)) {
      scheduleElementHighlights();
      return;
    }
    applyElementHighlights(editorRef.current, storyNodesRef.current, getActiveBlock());
  }, 300);
};

// ── Line numbers ───────────────────────────────────────────────────────────
// Apply or clear the gutter immediately, reflecting the current toggle state.
const refreshLineNumbers = () => {
  const editor = editorRef.current;
  if (!editor) return;
  if (showLineNumbers) applyLineNumbers(editor);
  else clearLineNumbers(editor);
};

// Debounced rebuild. Defers while pagination is running (the gutter measures
// rendered line positions, which are only stable once layout settles).
const scheduleLineNumbers = () => {
  if (lineNumbersTimerRef.current) clearTimeout(lineNumbersTimerRef.current);
  lineNumbersTimerRef.current = setTimeout(() => {
    if (!editorRef.current || !showLineNumbersRef.current) return;
    if (paginatingRef.current) { scheduleLineNumbers(); return; }
    applyLineNumbers(editorRef.current);
  }, 300);
};

// Keep refs in sync for the debounced/pagination paths.
useEffect(() => { showCommentHighlightsRef.current = showCommentHighlights; }, [showCommentHighlights]);
useEffect(() => { draftCommentsRef.current = draftComments; }, [draftComments]);
useEffect(() => { showCollabEditsRef.current = showCollabEdits; }, [showCollabEdits]);
useEffect(() => { collabEditsRef.current = collabEdits; }, [collabEdits]);
useEffect(() => { showLineNumbersRef.current = showLineNumbers; }, [showLineNumbers]);

// Apply/clear the line-number gutter immediately when the toggle flips.
useEffect(() => {
  refreshLineNumbers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [showLineNumbers]);

// Rebuild the gutter (debounced) after content changes — local edits,
// pagination, or an incoming collaborator update.
useEffect(() => {
  if (!showLineNumbers) return;
  scheduleLineNumbers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [content]);

// Live word count for the header (debounced so it doesn't run every keystroke).
// The same count the saves send for word goals, so the two always agree.
useEffect(() => {
  const t = setTimeout(() => {
    const stats = manuscriptWordStats(editorRef.current);
    setLiveWordCount(stats.total);
    setLiveChapterWords(stats.chapters);
  }, 400);
  return () => clearTimeout(t);
}, [content]);

// Which pages the chapter under the caret occupies, for the preview's "This
// chapter" scope. A chapter here is a derived range of blocks between headings,
// not a set of whole pages — a short chapter can share a sheet with its
// neighbours — so this is "every page the chapter appears on", and the first and
// last of those may show a little of the chapters either side.
const railPreviewChapterRange = () => {
  const editor = editorRef.current;
  if (!editor) return null;
  let block = null;
  const sel = window.getSelection();
  if (sel && sel.rangeCount) {
    const node = sel.getRangeAt(0).startContainer;
    const el = node?.nodeType === 1 ? node : node?.parentElement;
    if (el && editor.contains(el)) block = el.closest('.rv-page-body > *');
  }
  // No caret in the editor (just opened the panel, clicked elsewhere): fall back
  // to the first chapter so the scope always has something to show.
  if (!block) block = editor.querySelector('.rv-page-body > *');
  if (!block) return null;
  const headingId = getChapterHeadingId(editor, block);
  if (!headingId) return null;
  const blocks = getChapterRange(editor, headingId);
  if (!blocks.length) return null;
  const pages = Array.from(editor.querySelectorAll('.rv-page'));
  const start = pages.indexOf(blocks[0].closest('.rv-page'));
  const end = pages.indexOf(blocks[blocks.length - 1].closest('.rv-page'));
  if (start < 0 || end < 0) return null;
  return { start, end };
};

// Pull the composed front and back matter. Cheap, and re-fetched whenever a
// preview is opened rather than polled: the author edits structure on a different
// page, so opening a preview is exactly when it can have changed.
const fetchBookMatter = useCallback(async () => {
  if (!id) return;
  try {
    const token = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/book-html`, {
      credentials: 'omit',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return;
    const data = await res.json();
    setBookMatter({
      frontMatter: data.frontMatter || '',
      backMatter: data.backMatter || '',
    });
  } catch {
    // A preview that shows the manuscript without its front matter is worth more
    // than one that fails to open, so this stays silent.
  }
}, [id]);

useEffect(() => { fetchBookMatter(); }, [fetchBookMatter]);

/**
 * The manuscript with the author's front and back matter around it.
 *
 * Only for whole-book views. In "This chapter" scope the page indices in
 * `railPreviewChapterRange()` are offsets into the manuscript's own sheets, and
 * prepending front matter would silently shift every one of them.
 */
const withBookMatter = useCallback(
  (html) => `${bookMatter.frontMatter}${html || ''}${bookMatter.backMatter}`,
  [bookMatter],
);

// Does this book have a cover that a size change would leave behind? Either
// shape counts — a linked cover draft, or cover versions carried on this draft.
const hasCoverToResize = Boolean(doc?.linkedCoverId || (doc?.coverVersions || []).length);

/**
 * Change the book's trim size.
 *
 * Re-flowing a manuscript that already has words in it changes every line break,
 * every page break and the page count, and moves any page break the author placed
 * by hand. That is not something to do because a dropdown was brushed past, so an
 * existing manuscript gets asked first. An empty one just changes.
 */
const requestTrimSize = useCallback((sizeId) => {
  if (!sizeId || sizeId === trimSize) return;
  const hasWords = Boolean(getCleanHTML(editorRef.current)?.replace(/<[^>]*>/g, '').trim());
  if (hasWords) {
    setPendingTrimSize(sizeId);
    return;
  }
  setTrimSize(sizeId);
}, [trimSize]);

// Persist whenever the size actually changes. The toolbar re-flows off the same
// value, so the page and the stored size cannot disagree.
useEffect(() => {
  if (!id || !doc) return;
  if ((doc.trimSize || DEFAULT_BOOK_SIZE) === trimSize) return;
  const token = localStorage.getItem('rv_access_token');
  fetch(`${API_BASE}/api/drafts/${id}`, {
    method: 'PUT',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ trimSize }),
  })
    .then((res) => { if (res.ok) setDoc((prev) => (prev ? { ...prev, trimSize } : prev)); })
    .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [trimSize, id]);

// Feed the right-rail live preview. Debounced like the word count, and gated on
// the panel being open so a hidden preview costs nothing while typing.
useEffect(() => {
  if (!railPreviewOpen) return undefined;
  const t = setTimeout(() => {
    setRailPreviewHtml(getCleanHTML(editorRef.current));
    setRailPreviewRange(railPreviewChapterRange());
  }, 220);
  return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [content, railPreviewOpen]);

// In "This chapter" scope the visible range follows the caret, so moving it
// without typing has to re-resolve the chapter too.
useEffect(() => {
  if (!railPreviewOpen || railPreviewScope !== 'chapter') return undefined;
  let t = null;
  const onSelection = () => {
    clearTimeout(t);
    t = setTimeout(() => setRailPreviewRange(railPreviewChapterRange()), 220);
  };
  document.addEventListener('selectionchange', onSelection);
  return () => { clearTimeout(t); document.removeEventListener('selectionchange', onSelection); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [railPreviewOpen, railPreviewScope]);

// Persist the rail preview's view preferences.
useEffect(() => {
  try { localStorage.setItem('revisionari_preview_open', railPreviewOpen ? '1' : '0'); } catch { /* storage unavailable */ }
}, [railPreviewOpen]);
useEffect(() => {
  try { localStorage.setItem('revisionari_preview_format', railPreviewFormat); } catch { /* storage unavailable */ }
}, [railPreviewFormat]);
useEffect(() => {
  try { localStorage.setItem('revisionari_page_view', pageViewMode); } catch { /* storage unavailable */ }
}, [pageViewMode]);
useEffect(() => {
  try { localStorage.setItem('revisionari_preview_scope', railPreviewScope); } catch { /* storage unavailable */ }
}, [railPreviewScope]);

// ── Navigation outline ──────────────────────────────────────────────────────
// Rebuild the derived heading tree. Cheap, but debounced and gated on the panel
// being open so we don't scan the DOM on every keystroke for a hidden panel.
// Strip the heading marker from any *empty* block — a blank line that inherited
// data-heading-level/data-block-id (e.g. from pressing Enter after a heading)
// is not really a heading and would show up as a phantom "Untitled" entry. The
// block under the caret is left alone so we never disturb an in-progress edit.
const cleanEmptyHeadings = (editor) => {
  const active = getActiveBlock();
  let changed = false;
  editor.querySelectorAll('.rv-page-body [data-heading-level]').forEach((el) => {
    if (el === active) return;
    if ((el.textContent || '').replace(/​/g, '').trim() === '') {
      el.removeAttribute('data-heading-level');
      // The id stays: every block has one now, and comments anchor to it. A
      // copy inherited from Enter is a duplicate ensureBlockIds re-assigns.
      el.style.removeProperty('font-size');
      el.style.removeProperty('font-weight');
      el.style.removeProperty('font-family');
      el.style.removeProperty('text-align');
      changed = true;
    }
  });
  return changed;
};

const rebuildOutline = () => {
  if (!editorRef.current) return;
  // Normalize the DOM before reading it: drop phantom headings on empty lines,
  // then make sure every block carries a unique id — assigning to blocks that
  // have none, and re-assigning duplicates (a side effect of contentEditable
  // cloning attributes on Enter). Persist if anything changed so panel keys,
  // jump, reorder, and comment anchors all target the right block.
  const cleaned = cleanEmptyHeadings(editorRef.current);
  // New blocks follow the manuscript's template, even when Enter cloned them
  // from a paragraph the last template change kept as regular text.
  const stamped = ensureBlockIds(editorRef.current, makeBlockId, releaseBlock);
  if (cleaned || stamped) triggerChange();
  const { flat, tree } = buildOutline(editorRef.current);
  setOutline(tree);

  // Per-chapter word counts for the Chapters panel. Each chapter spans its
  // heading through the block before the next same-or-higher heading
  // (getChapterRange), so counts include any nested sub-section text.
  const counts = {};
  flat.forEach((h) => {
    if (!h.blockId) return;
    const blocks = getChapterRange(editorRef.current, h.blockId);
    const text = blocks.map((el) => el.textContent || '').join(' ');
    const words = text.replace(/​/g, '').trim();
    counts[h.blockId] = words ? words.split(/\s+/).length : 0;
  });
  setChapterWordCounts(counts);
};

const scheduleOutline = () => {
  if (outlineTimerRef.current) clearTimeout(outlineTimerRef.current);
  outlineTimerRef.current = setTimeout(() => {
    if (!editorRef.current) return;
    if (paginatingRef.current) { scheduleOutline(); return; }
    rebuildOutline();
  }, 300);
};

// Build immediately when the panel opens; rebuild (debounced) as content changes
// while it's open.
useEffect(() => {
  if (activeSidebarPanel !== 'navigation') return;
  rebuildOutline();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeSidebarPanel]);

useEffect(() => {
  if (activeSidebarPanel !== 'navigation') return;
  scheduleOutline();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [content]);

// Track which heading is currently in view so the panel can highlight it.
// Scroll-spy: pick the last heading whose top has scrolled above a line near the
// top of the viewport (i.e. the heading you're currently reading under). This
// works for short documents too, where an IntersectionObserver band would never
// change. Active only while the panel is open; re-bound when the outline shifts.
useEffect(() => {
  if (activeSidebarPanel !== 'navigation') return;
  const container = mainContentRef.current;
  const editor = editorRef.current;
  if (!container || !editor) return;

  const computeActive = () => {
    const cTop = container.getBoundingClientRect().top;
    const heads = Array.from(editor.querySelectorAll('[data-heading-level]'))
      .filter((h) => (h.textContent || '').trim())
      .map((h) => ({ blockId: h.getAttribute('data-block-id'), top: h.getBoundingClientRect().top }));
    setActiveHeadingId(resolveActiveHeadingId(heads, { containerTop: cTop, pinnedId: jumpPinRef.current }));
  };

  // A jump smooth-scrolls its chapter to the centre of the viewport, well below
  // the marker line, so every scroll event that scroll fires would otherwise
  // resolve to the chapter above it. The pin holds the author's choice until
  // they scroll for themselves — a scroll event alone can't say who scrolled.
  const releasePin = () => { jumpPinRef.current = null; };

  computeActive();
  container.addEventListener('scroll', computeActive, { passive: true });
  container.addEventListener('wheel', releasePin, { passive: true });
  container.addEventListener('touchmove', releasePin, { passive: true });
  container.addEventListener('pointerdown', releasePin);
  container.addEventListener('keydown', releasePin);
  return () => {
    container.removeEventListener('scroll', computeActive);
    container.removeEventListener('wheel', releasePin);
    container.removeEventListener('touchmove', releasePin);
    container.removeEventListener('pointerdown', releasePin);
    container.removeEventListener('keydown', releasePin);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeSidebarPanel, outline]);

// Jump to a heading block: scroll it into view and drop the caret at its start.
const handleOutlineJump = (blockId) => {
  const editor = editorRef.current;
  if (!editor || !blockId) return;
  const el = editor.querySelector(`[data-block-id="${blockId}"]`);
  if (!el) return;
  // Pin before scrolling: the smooth scroll below fires scroll events the
  // scroll-spy would otherwise resolve against, overwriting this very choice.
  jumpPinRef.current = blockId;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  try {
    const body = el.closest('.rv-page-body');
    if (body) body.focus({ preventScroll: true });
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  } catch { /* best effort */ }
  setActiveHeadingId(blockId);
};

// Post the comment being composed for the selected passage. The anchor was
// captured when the chip appeared (see anchorFromRange) — by now the caret has
// moved, so it can't be re-derived here.
const handleSubmitPassageComment = async (text) => {
  const pending = pendingComment;
  if (!pending || !doc?._id) return;
  try {
    // Put the passage's block id on the server before a comment points at
    // it. The id may have been stamped seconds ago; if the draft were reloaded
    // before its next save, the paragraph would come back with a new id and
    // this comment would land in "Unassigned".
    await forceSave(true);
    await commentApi.create({
      bookId: doc._id,
      type: 'draft',
      text,
      quote: pending.quote || null,
      blockId: pending.blockId,
      chapterId: pending.chapterId,
      lineHint: pending.lineHint,
    });
    // Cleared only on success, so a failed post can be retried against the
    // same passage instead of silently becoming an unanchored comment.
    setPendingComment(null);
    // Reveal the line it just landed on rather than making the author hunt.
    if (pending.blockId) {
      setSelectedCommentBlockId(pending.blockId);
      setCommentRevealSeq((n) => n + 1);
    }
    setCollabReloadKey((k) => k + 1);
  } catch (e) {
    setCollabError(e?.message || 'Could not post that comment.');
  }
};

// Comment index: open a line. Scrolls the editor to the commented block, makes
// it the centre pane's subject, and marks its comments read for this viewer.
const handleSelectCommentLine = async (line) => {
  if (!line) return;
  setSelectedCommentBlockId(line.blockId || null);
  setCommentRevealSeq((n) => n + 1);
  if (line.blockId) handleOutlineJump(line.blockId);

  const unread = (line.comments || []).filter((c) => !c.read);
  if (unread.length === 0) return;
  const unreadIds = new Set(unread.map((c) => c.id));

  // Dim optimistically so the row responds immediately, then persist.
  setCollabComments((prev) =>
    prev.map((c) => (unreadIds.has(c.id) ? { ...c, read: true } : c)));
  try {
    await Promise.all(unread.map((c) => commentApi.markRead(c.id)));
  } catch {
    // Roll back — a row that looks read but isn't would hide real feedback.
    setCollabComments((prev) =>
      prev.map((c) => (unreadIds.has(c.id) ? { ...c, read: false } : c)));
  }
};

// Run a structural DOM mutation as a single, coherent change: pause auto-
// pagination while we mutate, then reflow once, serialize, broadcast to
// collaborators, autosave, and refresh the outline — one undo/save unit.
const commitStructuralChange = (mutate, label = 'Change', before = null) => {
  const editor = editorRef.current;
  if (!editor) return;
  // Snapshot the exact pre-op DOM so Ctrl+Z / the toast can restore it — these
  // structural edits bypass contentEditable's native undo stack. An op that
  // already changed the page before it got here (an image drag lifts the image
  // out first) passes the DOM from before it started.
  const snapshot = before ?? editor.innerHTML;
  skipAutoPaginationRef.current = true;
  try {
    mutate();
    reflowDocument();
  } finally {
    skipAutoPaginationRef.current = false;
  }
  const updated = getCleanHTML(editor);
  setContent(updated);
  emitDraftUpdate(updated);
  triggerChange();
  rebuildOutline();

  const stack = structuralUndoRef.current;
  stack.push(snapshot);
  if (stack.length > 25) stack.shift();
  lastStructuralRef.current = true;
  setUndoToast({ label });
};

// ── Manuscript theme: switching, and clearing what hides it ───────────────
// Switching writes a copy of the theme onto the draft (author only), then
// offers to clear the old inline fonts and sizes that would sit on top of it.
const applyManuscriptTheme = async (theme, meta = {}) => {
  if (!isItemOwner) {
    setThemeToast('Only the author can change the theme.');
    return;
  }
  try {
    if (theme) {
      const copy = {
        ...themeValues(completeTheme(theme)),
        name: meta.name || 'Untitled theme',
        sourceId: meta.sourceId ?? null,
      };
      await setManuscriptTheme(id, copy, { name: copy.name, sourceId: copy.sourceId });
      setDoc((prev) => (prev ? { ...prev, manuscriptTheme: copy } : prev));
      setThemeToast(`${copy.name} applied to the whole manuscript.`);
      const hidden = countOverrides(editorRef.current);
      if (hidden) setThemeOverridePrompt(hidden);
    } else {
      await setManuscriptTheme(id, null);
      setDoc((prev) => {
        if (!prev) return prev;
        const { manuscriptTheme: removed, ...rest } = prev;
        return rest;
      });
      setThemeToast('Theme removed. The manuscript is back to its own formatting.');
    }
  } catch (err) {
    setThemeToast(errorCode(err) === 'not_author'
      ? 'Only the author can change the theme.'
      : 'The theme could not be saved. Please try again.');
  }
};

// Undoable and autosaved like any other structural edit.
const clearThemeOverrides = () => {
  setThemeOverridePrompt(0);
  commitStructuralChange(() => { clearOverrides(editorRef.current); }, 'Old formatting cleared');
  setThemeOverrideCount(0);
};

const openThemeEditor = () => saveThenGo(`/manuscript/${id}/theme`);

// ── Manuscript template: switching, and undoing a switch ──────────────────
// A whole-document rewrite saved the way structural edits are, but without the
// structural undo toast: undoing a template change is the Template menu's job,
// and a content-only undo would leave the template switched.
const commitTemplateRewrite = (mutate) => {
  const editor = editorRef.current;
  if (!editor) return;
  skipAutoPaginationRef.current = true;
  try {
    mutate(editor);
    reflowDocument();
  } finally {
    skipAutoPaginationRef.current = false;
  }
  const updated = getCleanHTML(editor);
  setContent(updated);
  emitDraftUpdate(updated);
  triggerChange();
  rebuildOutline();
};

const pickTemplate = (template) => {
  if (!isItemOwner) {
    setThemeToast('Only the author can change the template.');
    return;
  }
  setTemplateToSwitch(template);
};

// The ticket's rule: text already written becomes regular text, and the new
// template's formatting applies to what is written next (src/utils/templateOrphan.js).
const confirmTemplateSwitch = async () => {
  const template = templateToSwitch;
  setTemplateToSwitch(null);
  const editor = editorRef.current;
  // Never rewrite a manuscript that did not load: saving it would save nothing.
  if (!template || !editor || !doc || loadError) return;
  // Fitted to this book's page size (src/config/templateThemes.js).
  const theme = templateTheme(template, trimSize);
  try {
    const res = await changeTemplate(id, template.id, theme, orphanableIds(editor));
    // "Regular" is the text's own look without any theme, so read it with the
    // theme's stylesheet switched off.
    commitTemplateRewrite((root) => {
      const sheet = document.getElementById(THEME_STYLE_ID)?.sheet;
      if (sheet) sheet.disabled = true;
      try {
        orphanBlocks(root, computedLook);
      } finally {
        if (sheet) sheet.disabled = false;
      }
    });
    setDoc((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        template_id: template.id,
        template_history: [...(prev.template_history || []), { template_name: res?.undo_name || null }],
      };
      if (res?.manuscriptTheme) next.manuscriptTheme = res.manuscriptTheme;
      else delete next.manuscriptTheme;
      return next;
    });
    setTemplateName(template.name);
    setThemeToast(`${template.name} applied. Your existing text is kept as regular text.`);
  } catch (err) {
    setThemeToast(errorCode(err) === 'not_author'
      ? 'Only the author can change the template.'
      : 'The template could not be changed. Please try again.');
  }
};

const undoTemplateSwitch = async () => {
  const editor = editorRef.current;
  if (!isItemOwner || !editor || !doc || loadError) return;
  try {
    const res = await revertTemplate(id);
    commitTemplateRewrite((root) => { unorphanBlocks(root, res?.orphaned || []); });
    setDoc((prev) => {
      if (!prev) return prev;
      const next = { ...prev, template_history: (prev.template_history || []).slice(0, -1) };
      if (res?.template_id) next.template_id = res.template_id;
      else delete next.template_id;
      if (res?.manuscriptTheme) next.manuscriptTheme = res.manuscriptTheme;
      else delete next.manuscriptTheme;
      return next;
    });
    setTemplateName(res?.template_name || '');
    setThemeToast(res?.template_name
      ? `Back to ${res.template_name}.`
      : 'Template change undone.');
  } catch (err) {
    setThemeToast(errorCode(err) === 'not_author'
      ? 'Only the author can change the template.'
      : 'The template change could not be undone. Please try again.');
  }
};

// Restore the most recent structural snapshot (chapter insert/delete/move/level).
const undoStructural = () => {
  const editor = editorRef.current;
  const stack = structuralUndoRef.current;
  if (!editor || !stack.length) return;
  const snapshot = stack.pop();
  skipAutoPaginationRef.current = true;
  try {
    editor.innerHTML = snapshot;
    reflowDocument();
  } finally {
    skipAutoPaginationRef.current = false;
  }
  const updated = getCleanHTML(editor);
  setContent(updated);
  emitDraftUpdate(updated);
  triggerChange();
  rebuildOutline();
  // Keep intercepting Ctrl+Z while earlier structural snapshots remain, so
  // repeated undos walk back through the chapter ops. Typing still clears it.
  lastStructuralRef.current = structuralUndoRef.current.length > 0;
  setUndoToast(null);
};

// Build a fresh level-1 heading block + an empty paragraph to type into.
/**
 * True when a page body holds nothing but blank placeholder paragraphs — an
 * untouched page 1 on a new manuscript, or a blank trailing page.
 *
 * Deliberately strict about what counts as blank: a block carrying a heading
 * level is real structure even with no title typed yet, and a paragraph
 * wrapping an image or table has no text but is very much not empty.
 * @param {Element|null} body a .rv-page-body
 */
const pageBodyIsBlank = (body) => {
  if (!body) return false;
  const blocks = Array.from(body.children).filter(
    (c) => !c.classList?.contains('rv-end-spacer')
  );
  // A body holding only the spacer is blank, which [].every() gives us.
  return blocks.every(
    (b) =>
      !b.hasAttribute?.('data-heading-level') &&
      !b.querySelector?.('img, table, figure, .rv-resizable-image, .rv-textbox') &&
      isEmptyParagraph(b)
  );
};

const makeChapterNodes = (doc) => {
  const style = HEADING_STYLES[1];
  const heading = doc.createElement('p');
  heading.setAttribute('data-heading-level', '1');
  heading.setAttribute('data-block-id', makeBlockId());
  heading.style.fontSize = style.fontSize;
  heading.style.fontWeight = style.fontWeight;
  heading.style.fontFamily = style.fontFamily;
  heading.style.textAlign = style.textAlign;
  heading.textContent = 'New Chapter';
  const para = doc.createElement('p');
  para.innerHTML = '<br/>';
  return { heading, para };
};

// Insert a new chapter on its own page — normally preceded by a page break, so
// a chapter never shares a page with the content before it. When `afterBlockId`
// is given, the new page lands right after the page holding that chapter's
// last block; when null, it's appended after the manuscript's very last page.
//
// Exception: if the page it would break away from is BLANK, the chapter goes
// into that page instead of a new one — otherwise a new manuscript's first
// chapter lands on page 2 with an empty page 1 above it.
const insertChapter = (afterBlockId = null) => {
  const editor = editorRef.current;
  if (!editor) return;
  const doc = editor.ownerDocument;
  let newHeading = null;

  // Chapters must keep their own page break, but commitStructuralChange's
  // reflowDocument() globally repacks blocks by page-height overflow — it
  // would immediately merge the fresh page back into the previous one and
  // erase the break. So this mirrors commitStructuralChange's snapshot/undo/
  // outline bookkeeping directly, using applyPageNumbers() instead (the same
  // approach handleInsertPageBreak uses for the same reason).
  const snapshot = editor.innerHTML;
  skipAutoPaginationRef.current = true;
  try {
    let afterPage = null;
    let trailingBlocks = [];
    if (afterBlockId) {
      const range = getChapterRange(editor, afterBlockId);
      const anchor = range[range.length - 1];
      afterPage = anchor?.closest?.('.rv-page') || null;

      // Short chapters often still share one physical page (nothing has
      // overflowed yet), so the anchor's page may also hold later chapters.
      // Pull anything after the anchor off that page so the new chapter's
      // break lands right after THIS chapter, not after the whole page.
      let sib = anchor?.nextElementSibling;
      while (sib) {
        const next = sib.nextElementSibling;
        if (!sib.classList?.contains('rv-end-spacer')) trailingBlocks.push(sib);
        sib = next;
      }
    }
    if (!afterPage) {
      const pages = editor.querySelectorAll('.rv-page');
      afterPage = pages[pages.length - 1] || null;
    }

    if (afterPage) {
      const { heading, para } = makeChapterNodes(doc);
      newHeading = heading;

      // If the page we'd be breaking away from is itself blank, put the chapter
      // straight into it. Otherwise a brand-new manuscript — whose page 1 is an
      // empty placeholder — gets its first chapter on page 2, stranding a blank
      // page above it. Same applies to a blank trailing page at the end of a
      // draft. Only safe when nothing has to be displaced.
      const afterBody = afterPage.querySelector('.rv-page-body');
      const reuseBlankPage = !trailingBlocks.length && pageBodyIsBlank(afterBody);

      const newPage = reuseBlankPage ? afterPage : createEmptyPageEl(doc);
      if (!reuseBlankPage) afterPage.insertAdjacentElement('afterend', newPage);
      const newBody = newPage.querySelector('.rv-page-body');

      if (newBody) {
        // Clear the blank placeholder paragraph(s) so the heading is the first
        // block. A fresh page has exactly one; a reused blank page may have
        // several, and they're all empty by definition of pageBodyIsBlank.
        const spacer = newBody.querySelector('.rv-end-spacer');
        Array.from(newBody.children)
          .filter((c) => c !== spacer)
          .forEach((c) => c.remove());

        if (spacer) {
          newBody.insertBefore(heading, spacer);
          newBody.insertBefore(para, spacer);
        } else {
          newBody.appendChild(heading);
          newBody.appendChild(para);
        }
      }

      // Re-home the displaced trailing content on its own page right after
      // the new chapter, so it still follows in document order.
      if (trailingBlocks.length) {
        const contPage = createEmptyPageEl(doc);
        newPage.insertAdjacentElement('afterend', contPage);
        const contBody = contPage.querySelector('.rv-page-body');
        if (contBody) {
          const contSpacer = contBody.querySelector('.rv-end-spacer');
          const contPlaceholder = Array.from(contBody.children).find((c) => c !== contSpacer);
          if (contPlaceholder) contPlaceholder.remove();
          trailingBlocks.forEach((b) => contBody.insertBefore(b, contSpacer));
        }
      }
    }
    applyPageNumbers();
  } finally {
    skipAutoPaginationRef.current = false;
  }

  const updated = getCleanHTML(editor);
  setContent(updated);
  emitDraftUpdate(updated);
  triggerChange();
  rebuildOutline();

  const stack = structuralUndoRef.current;
  stack.push(snapshot);
  if (stack.length > 25) stack.shift();
  lastStructuralRef.current = true;
  setUndoToast({ label: 'Chapter inserted' });

  // Select the new heading's text so the author can immediately type a title.
  if (newHeading) {
    try {
      const live = editor.querySelector(
        `[data-block-id="${newHeading.getAttribute('data-block-id')}"]`
      );
      if (live) {
        live.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const body = live.closest('.rv-page-body');
        if (body) body.focus({ preventScroll: true });
        const r = document.createRange();
        r.selectNodeContents(live);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      }
    } catch { /* best effort */ }
  }
};

// Open the custom confirmation modal for deleting a chapter. The actual removal
// happens in performDeleteChapter once the user confirms.
const deleteChapter = (blockId) => {
  const editor = editorRef.current;
  if (!editor || !blockId) return;
  const range = getChapterRange(editor, blockId);
  if (!range.length) return;
  const title = (range[0].textContent || '').trim() || 'this chapter';
  setChapterToDelete({ blockId, title });
};

// Delete the whole chapter (heading + its blocks up to the next same-or-higher
// heading). Never leaves the document with zero blocks.
const performDeleteChapter = (blockId) => {
  const editor = editorRef.current;
  if (!editor || !blockId) return;
  const range = getChapterRange(editor, blockId);
  if (!range.length) return;

  commitStructuralChange(() => {
    range.forEach((el) => el.remove());
    // Guarantee at least one editable block remains in the first body.
    const firstBody = editor.querySelector('.rv-page-body');
    if (firstBody) {
      const hasContent = Array.from(firstBody.children).some(
        (c) => !c.classList?.contains('rv-end-spacer')
      );
      if (!hasContent) {
        const p = editor.ownerDocument.createElement('p');
        p.innerHTML = '<br/>';
        firstBody.insertBefore(p, firstBody.firstChild);
      }
    }
  }, 'Chapter deleted');
};

// Rename a chapter/heading from the Navigation panel: set the heading block's
// text to `newText`. Empty titles are rejected (blank headings drop out of the
// outline). Reuses commitStructuralChange so the rename is undoable, autosaved,
// synced to collaborators, and re-derives the outline.
const renameChapter = (blockId, newText) => {
  const editor = editorRef.current;
  if (!editor || !blockId) return;
  const title = (newText || '').replace(/​/g, '').trim();
  if (!title) return;
  const el = editor.querySelector(`[data-block-id="${blockId}"]`);
  if (!el) return;
  if ((el.textContent || '').trim() === title) return; // no-op

  commitStructuralChange(() => {
    el.textContent = title;
  }, 'Chapter renamed');
};

// Promote (delta -1, toward Heading 1) or demote (delta +1) a heading, clamped
// to levels 1-4. Restyles to match the new level.
const changeHeadingLevel = (blockId, delta) => {
  const editor = editorRef.current;
  if (!editor || !blockId) return;
  const el = editor.querySelector(`[data-block-id="${blockId}"]`);
  if (!el) return;
  const current = Number(el.getAttribute('data-heading-level')) || 1;
  const next = Math.min(4, Math.max(1, current + delta));
  if (next === current) return;

  commitStructuralChange(() => {
    const style = HEADING_STYLES[next];
    el.setAttribute('data-heading-level', String(next));
    el.style.fontSize = style.fontSize;
    el.style.fontWeight = style.fontWeight;
    el.style.fontFamily = style.fontFamily;
    el.style.textAlign = style.textAlign;
  }, 'Heading level changed');
};

// Move a whole chapter (heading + its blocks) so it lands relative to a target
// heading. `position` is 'before' (immediately ahead of the target heading) or
// 'after' (immediately following the target's own chapter range). The moved
// nodes are the same DOM elements, so their data-block-ids survive the reflow.
const moveChapter = (sourceBlockId, targetBlockId, position = 'before') => {
  const editor = editorRef.current;
  if (!editor || !sourceBlockId || !targetBlockId) return;
  if (sourceBlockId === targetBlockId) return;

  const sourceRange = getChapterRange(editor, sourceBlockId);
  if (!sourceRange.length) return;

  // Refuse to drop a chapter inside its own range (would detach it from the DOM).
  if (sourceRange.some((el) => el.getAttribute?.('data-block-id') === targetBlockId)) {
    return;
  }

  commitStructuralChange(() => {
    if (position === 'after') {
      // Chain the source blocks directly after the target chapter's last block.
      // insertAdjacentElement handles the end-of-body case without a nextSibling
      // anchor; document order is what reflow uses to re-paginate.
      const targetRange = getChapterRange(editor, targetBlockId);
      let prev = targetRange[targetRange.length - 1];
      if (!prev) return;
      sourceRange.forEach((el) => {
        prev.insertAdjacentElement('afterend', el);
        prev = el;
      });
    } else {
      const targetHeading = editor.querySelector(`[data-block-id="${targetBlockId}"]`);
      if (!targetHeading || !targetHeading.parentNode) return;
      const parent = targetHeading.parentNode;
      sourceRange.forEach((el) => parent.insertBefore(el, targetHeading));
    }
  }, 'Chapter moved');
};

// Apply/clear marks immediately when the toggle flips.
useEffect(() => {
  refreshFormattingMarks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [showFormattingMarks]);

// Rebuild marks after the document content changes — local edits, pagination,
// or an incoming collaborator update that replaced the editor's innerHTML.
useEffect(() => {
  if (!showFormattingMarks) return;
  scheduleFormattingMarks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [content]);

// ── Comment highlights ─────────────────────────────────────────────────────
// Fetch this draft's comments while the highlight toggle is on, and poll so
// newly added comments light up without a reload. Cleared when toggled off.
useEffect(() => {
  if (!showCommentHighlights || !doc?._id) {
    setDraftComments([]);
    return;
  }
  let alive = true;
  const load = async () => {
    try {
      const data = await commentApi.list({
        bookId: doc._id,
        type: 'draft',
        page: null,   // all comments across the draft
        limit: 200,
      });
      if (alive) setDraftComments(Array.isArray(data) ? data : []);
    } catch {
      /* leave previous list in place on transient errors */
    }
  };
  load();
  const t = setInterval(load, 12000);
  return () => { alive = false; clearInterval(t); };
}, [showCommentHighlights, doc?._id]);

// ── Collab tab: comment index ──────────────────────────────────────────────
// Load the draft's comments while the Collab panel is showing them, and poll so
// a reviewer's new comment appears without a reload (same cadence as the
// highlight fetch above). Errors surface in the panel rather than being
// swallowed, since an empty index and a failed request look identical.
useEffect(() => {
  const active = activeSidebarPanel === 'collab' && collabView === 'comments';
  if (!active || !doc?._id) return;

  let alive = true;
  let firstRun = true;

  const load = async () => {
    try {
      if (firstRun) setCollabLoading(true);
      const data = await commentApi.list({
        bookId: doc._id,
        type: 'draft',
        page: null,     // every comment across the draft
        limit: 200,
      });
      if (!alive) return;
      setCollabComments(Array.isArray(data) ? data : []);
      setCollabError('');
    } catch (e) {
      // Only report if we have nothing to show; a transient poll failure
      // shouldn't blank out a list the user is reading.
      if (alive && firstRun) setCollabError(e?.message || 'Failed to load comments.');
    } finally {
      if (alive && firstRun) { setCollabLoading(false); setCollabLoadedOnce(true); firstRun = false; }
    }
  };

  load();
  const t = setInterval(load, 12000);
  return () => { alive = false; clearInterval(t); };
}, [activeSidebarPanel, collabView, doc?._id, collabReloadKey]);

// ── Notification deep link ─────────────────────────────────────────────────
// /editor/:id?comment=<id> — sent by a comment notification. Two steps,
// because comments are only fetched while the Collab panel is showing them,
// and the id has to be resolved against that list to find the block it anchors
// to: open the panel first, jump once the first fetch has settled.
const deepLinkCommentId = searchParams.get('comment');
const deepLinkDoneRef = useRef(false);

useEffect(() => {
  if (!deepLinkCommentId || deepLinkDoneRef.current) return;
  // Opening these isn't just presentation: the comment highlight only renders
  // while this panel and view are active, and the fetch only runs then too.
  setActiveSidebarPanel('collab');
  setCollabView('comments');
}, [deepLinkCommentId]);

useEffect(() => {
  if (!deepLinkCommentId || deepLinkDoneRef.current || !collabLoadedOnce) return;
  deepLinkDoneRef.current = true;

  // Drop the param either way: left in place it re-fires on every later
  // render, and a link to a comment that's gone shouldn't keep advertising it.
  setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.delete('comment');
    return next;
  }, { replace: true });

  const target = (collabComments || []).find((c) => c.id === deepLinkCommentId);
  // Not found: deleted since the notification was sent, or older than the page
  // of comments the editor loads. The manuscript is open with the comment index
  // showing, which is as close as we can honestly get.
  if (!target) return;

  // handleSelectCommentLine wants a grouped line; one comment is a valid line.
  // An unanchored comment (no block_id) selects without scrolling.
  handleSelectCommentLine({ blockId: target.block_id || null, comments: [target] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [deepLinkCommentId, collabLoadedOnce, collabComments]);

// The comment index groups by chapter, so it needs a current outline. The
// outline is otherwise only rebuilt while the Chapters panel is open.
useEffect(() => {
  if (activeSidebarPanel !== 'collab' || collabView !== 'comments') return;
  rebuildOutline();
  refreshLineIndex();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeSidebarPanel, collabView]);

// Keep the grouping and the line numbers current as the author edits —
// chapters get renamed and reordered, and inserting a paragraph renumbers
// every line below it.
useEffect(() => {
  if (activeSidebarPanel !== 'collab' || collabView !== 'comments') return;
  scheduleOutline();
  scheduleLineIndex();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [content]);

// Re-measure once the comment set changes too: a comment on a block that had
// none yet needs a number the moment it appears in the index.
useEffect(() => {
  if (activeSidebarPanel !== 'collab' || collabView !== 'comments') return;
  scheduleLineIndex();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [collabComments]);

// Single source of truth for what the decoration pass sees, and which passage
// (if any) is focused. Two lists can supply comments — the Collab panel's while
// it's open, the View toggle's fetch otherwise — so pick one here rather than
// letting both write to the same state and race.
useEffect(() => {
  const collabOpen = activeSidebarPanel === 'collab' && collabView === 'comments';
  decorationCommentsRef.current = collabOpen
    ? (collabComments || [])
    : (draftComments || []);
  // Only the Collab panel can focus a comment; leaving the tab drops the focus
  // rather than stranding a ring on a passage with nothing selected.
  focusedCommentBlockRef.current = collabOpen ? selectedCommentBlockId : null;
  scheduleCommentHighlights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [collabComments, draftComments, selectedCommentBlockId, activeSidebarPanel, collabView]);

// Apply/clear comment tint immediately when the toggle flips. Turning it OFF
// doesn't necessarily clear everything — a comment selected in Collab keeps its
// own focus ring, which is deliberately independent of this toggle.
useEffect(() => {
  const editor = editorRef.current;
  if (!editor) return;
  if (showCommentHighlights || focusedCommentBlockRef.current) {
    applyCommentHighlights(
      editor,
      commentsToDecorate(),
      getActiveBlock(),
      { focusedBlockId: focusedCommentBlockRef.current },
    );
  } else {
    clearCommentHighlights(editor);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [showCommentHighlights]);

// Rebuild comment highlights (debounced) when the comment set or document
// content changes — local edits, pagination, or an incoming collab update.
useEffect(() => {
  if (!showCommentHighlights) return;
  scheduleCommentHighlights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [draftComments, content]);

// Apply/clear collaborator-edit tint immediately when the toggle flips.
useEffect(() => {
  const editor = editorRef.current;
  if (!editor) return;
  if (showCollabEdits) {
    // Pass the caret block so per-word SEGMENT rendering skips it (block-level
    // tint is used there instead) — wrapping text nodes under the live caret
    // would disturb typing. Other paragraphs still get full segment coloring.
    applyCollabHighlights(editor, collabEditsRef.current, getActiveBlock());
  } else {
    clearCollabHighlights(editor);
    // Keep accumulated edits so toggling off + on restores highlights.
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [showCollabEdits]);

// Rebuild collaborator-edit highlights (debounced) when new edits arrive or
// the document content changes.
useEffect(() => {
  if (!showCollabEdits) return;
  scheduleCollabHighlights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [collabEdits, content]);

// ── Element recognition ────────────────────────────────────────────────────
// The Story Elements board's nodes, which the recognition pass matches against
// and the hover card describes. Fetched here rather than borrowed from the
// Story Elements panel, because the panel only exists while its tab is open.
// While it is open it reports its own edits up (onBoardChange) — the socket
// ignores our own echo, so a rename there would otherwise go unseen here.
const [storyNodes, setStoryNodes] = useState([]);

useEffect(() => {
  if (!id) return undefined;
  let cancelled = false;
  const load = () => {
    storyElementApi.get(id)
      .then((res) => {
        if (!cancelled) setStoryNodes(res?.storyElements?.nodes || []);
      })
      // A board that won't load costs the highlights, nothing else; keep what
      // was last shown rather than un-highlighting the whole manuscript.
      .catch(() => {});
  };
  load();
  // A collaborator's edit arrives as storyElementsRev; one made on the Cork
  // Board in another tab arrives when this one is looked at again.
  const refresh = () => { if (!document.hidden) load(); };
  window.addEventListener('focus', refresh);
  document.addEventListener('visibilitychange', refresh);
  return () => {
    cancelled = true;
    window.removeEventListener('focus', refresh);
    document.removeEventListener('visibilitychange', refresh);
  };
}, [id, storyElementsRev]);

// What recognition depends on: each element's id, type and name. A
// description edit changes none of them — the hover card reads descriptions
// live — so typing one in the panel does not re-scan the manuscript.
const storyNamesKey = useMemo(
  () => storyNodes.map((n) => `${n._id}|${n.type}|${n.name}`).join('\n'),
  [storyNodes],
);

useEffect(() => {
  storyNodesRef.current = storyNodes;
}, [storyNodes]);

// Re-recognize when a name or the text changes — local edits, pagination, or
// an incoming collab update.
useEffect(() => {
  scheduleElementHighlights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [storyNamesKey, content]);

// Re-sync list numbering across pages
// When lists are split, continuation lists have a 'start' attribute
// This function ensures the numbering stays correct when items are added/removed
const syncListNumbering = () => {
  if (!editorRef.current) return;
  
  const pages = Array.from(editorRef.current.querySelectorAll('.rv-page'));
  
  // Track running count for each list "chain" across pages
  // We identify chains by looking for lists with start attributes on subsequent pages
  
  for (let i = 0; i < pages.length - 1; i++) {
    const currentBody = pages[i].querySelector('.rv-page-body');
    const nextBody = pages[i + 1]?.querySelector('.rv-page-body');
    
    if (!currentBody || !nextBody) continue;
    
    // Check if current page ends with an OL
    const lastOL = currentBody.querySelector('ol:last-of-type');
    if (!lastOL) continue;
    
    // Check if next page starts with an OL that has a start attribute (continuation)
    const firstChild = Array.from(nextBody.children).find(
      el => el.tagName === 'OL' || (el.tagName !== 'P' && el.querySelector?.('ol'))
    );
    
    let nextOL = firstChild?.tagName === 'OL' ? firstChild : firstChild?.querySelector?.('ol');
    if (!nextOL || !nextOL.hasAttribute('start')) continue;
    
    // Count items in the current page's list
    const itemsOnCurrentPage = lastOL.querySelectorAll(':scope > li').length;
    const currentStart = parseInt(lastOL.getAttribute('start') || '1', 10);
    const expectedNextStart = currentStart + itemsOnCurrentPage;
    
    // Update the next page's list start attribute if needed
    const actualNextStart = parseInt(nextOL.getAttribute('start') || '1', 10);
    if (actualNextStart !== expectedNextStart) {
      console.log(`[SYNC LIST] Updating start from ${actualNextStart} to ${expectedNextStart}`);
      nextOL.setAttribute('start', String(expectedNextStart));
    }
  }
};

// Split a list (OL/UL) across pages by moving overflowing items to the next page
const splitListAcrossPages = (currentBody, list) => {
  if (!list) {
    console.log('[LIST SPLIT] Missing list');
    return false;
  }
  
  const page = currentBody.closest('.rv-page');
  if (!page) {
    console.log('[LIST SPLIT] No page found');
    return false;
  }
  
  const items = Array.from(list.children).filter(child => child.tagName === 'LI');
  if (items.length === 0) {
    console.log('[LIST SPLIT] No list items');
    return false;
  }
  
  // Don't split very small lists - just move the whole thing
  if (items.length <= 2) {
    console.log('[LIST SPLIT] List has only', items.length, 'items, will not split');
    return false;
  }
  
  // Use the page body's boundaries as the limits
  const bodyRect = currentBody.getBoundingClientRect();
  const bodyTop = bodyRect.top;
  const bodyBottom = bodyRect.bottom;
  
  console.log('[LIST SPLIT] Checking list with', items.length, 'items');
  console.log('[LIST SPLIT] Body top:', bodyTop, 'Body bottom:', bodyBottom);
  
  // Find the first item that overflows the page body BOTTOM
  // Only consider items that START within the page body (top >= bodyTop)
  let splitIndex = -1;
  for (let i = 0; i < items.length; i++) {
    const itemRect = items[i].getBoundingClientRect();
    console.log(`[LIST SPLIT] Item ${i} - top: ${itemRect.top}, bottom: ${itemRect.bottom}`);
    
    // Skip items that are entirely above the page body (shouldn't happen normally)
    if (itemRect.bottom < bodyTop) {
      console.log(`[LIST SPLIT] Item ${i} is above page body, skipping`);
      continue;
    }
    
    // Check if this item's bottom exceeds the page body bottom
    if (itemRect.bottom > bodyBottom - 10) { // 10px buffer for safety
      // Only split if this item STARTS within the page (not already overflowing from top)
      if (itemRect.top >= bodyTop - 10) {
        splitIndex = i;
        break;
      }
    }
  }
  
  console.log('[LIST SPLIT] Split index:', splitIndex);
  
  // If no items overflow, list fits perfectly - don't do anything
  if (splitIndex === -1) {
    console.log('[LIST SPLIT] List fits on page, no split needed');
    return 'fits';
  }
  
  // If only the first item overflows, can't split - move entire list
  if (splitIndex === 0) {
    console.log('[LIST SPLIT] First item overflows, cannot split');
    return false;
  }
  
  // Create a continuation list on the next page
  const nextBody = ensureNextPageBody(page);
  if (!nextBody) {
    console.log('[LIST SPLIT] Could not create next page');
    return false;
  }
  
  // Check if next page already has the same list (prevent duplicate splits)
  const existingList = nextBody.querySelector(list.tagName);
  if (existingList && existingList.getAttribute('start')) {
    console.log('[LIST SPLIT] Next page already has a continuation list, skipping');
    return 'fits';
  }
  
  const doc = list.ownerDocument;
  
  console.log(`[LIST SPLIT] Splitting: keeping ${splitIndex} items, moving ${items.length - splitIndex} items`);
  
  // Clone the list structure (OL or UL)
  const newList = doc.createElement(list.tagName);
  // Copy over attributes like class, style, start (for OL)
  for (const attr of list.attributes) {
    newList.setAttribute(attr.name, attr.value);
  }
  
  // For ordered lists, set the start attribute to continue numbering
  if (list.tagName === 'OL') {
    const startNum = parseInt(list.getAttribute('start') || '1', 10);
    newList.setAttribute('start', String(startNum + splitIndex));
  }
  
  // Move overflowing items to the new list
  const itemsToMove = items.slice(splitIndex);
  for (const item of itemsToMove) {
    newList.appendChild(item);
  }
  
  // Insert new list at the start of the next page
  const firstChild = nextBody.firstElementChild;
  if (firstChild) {
    nextBody.insertBefore(newList, firstChild);
  } else {
    nextBody.appendChild(newList);
  }
  
  console.log('[LIST SPLIT] Successfully split list');
  return true;
};

// Split a table across pages by moving overflowing rows to the next page
const splitTableAcrossPages = (currentBody, tableWrap, table) => {
  if (!table || !tableWrap) {
    console.log('[TABLE SPLIT] Missing table or wrapper');
    return false;
  }
  
  const page = currentBody.closest('.rv-page');
  if (!page) {
    console.log('[TABLE SPLIT] No page found');
    return false;
  }
  
  const tbody = table.querySelector('tbody');
  if (!tbody || tbody.children.length === 0) {
    console.log('[TABLE SPLIT] No tbody or empty');
    return false;
  }
  
  // Use the page body's bottom as the limit (not the page itself)
  const bodyRect = currentBody.getBoundingClientRect();
  const bodyBottom = bodyRect.bottom;
  const rows = Array.from(tbody.children);
  
  console.log('[TABLE SPLIT] Checking table with', rows.length, 'rows');
  console.log('[TABLE SPLIT] Body bottom:', bodyBottom);
  
  // Find the first row that overflows the page body
  let splitIndex = -1;
  for (let i = 0; i < rows.length; i++) {
    const rowRect = rows[i].getBoundingClientRect();
    console.log(`[TABLE SPLIT] Row ${i} bottom:`, rowRect.bottom);
    if (rowRect.bottom > bodyBottom - 20) { // 20px buffer for safety
      splitIndex = i;
      break;
    }
  }
  
  console.log('[TABLE SPLIT] Split index:', splitIndex);
  
  // If no rows overflow, table fits perfectly - don't do anything
  if (splitIndex === -1) {
    console.log('[TABLE SPLIT] Table fits on page, no split needed');
    return 'fits';
  }
  
  // If only the first row overflows, can't split - move entire table
  if (splitIndex === 0) {
    console.log('[TABLE SPLIT] First row overflows, cannot split');
    return false;
  }
  
  // Create a continuation table on the next page
  const nextBody = ensureNextPageBody(page);
  if (!nextBody) {
    console.log('[TABLE SPLIT] Could not create next page');
    return false;
  }
  
  const doc = table.ownerDocument;
  
  console.log(`[TABLE SPLIT] Splitting: keeping ${splitIndex} rows, moving ${rows.length - splitIndex} rows`);
  
  // Clone the table structure
  const newTableWrap = doc.createElement('div');
  newTableWrap.className = 'rv-table-wrap';
  newTableWrap.setAttribute('contenteditable', 'false');
  newTableWrap.style.width = tableWrap.style.width || '80%';
  
  const newTable = doc.createElement('table');
  newTable.className = 'rv-table';
  newTable.setAttribute('contenteditable', 'false');
  newTable.tabIndex = 0;
  
  // Copy header if it exists
  const thead = table.querySelector('thead');
  if (thead) {
    const newThead = thead.cloneNode(true);
    newTable.appendChild(newThead);
  }
  
  // Create new tbody with overflowing rows
  const newTbody = doc.createElement('tbody');
  for (let i = splitIndex; i < rows.length; i++) {
    newTbody.appendChild(rows[i]);
  }
  newTable.appendChild(newTbody);
  newTableWrap.appendChild(newTable);
  
  // Insert the new table at the start of the next page (before end-spacer)
  let insertBefore = nextBody.firstElementChild;
  if (insertBefore?.classList?.contains('rv-end-spacer')) {
    nextBody.insertBefore(newTableWrap, insertBefore);
  } else {
    nextBody.insertBefore(newTableWrap, nextBody.firstChild);
  }
  
  // Add spacer after the new table
  const spacer = doc.createElement('p');
  spacer.innerHTML = '<br/>';
  newTableWrap.insertAdjacentElement('afterend', spacer);
  
  console.log('[TABLE SPLIT] Table split successfully!');
  return true;
};

// Save caret position before pagination and restore after.
// Uses paired start/end marker spans with stable IDs so they can be located
// anywhere in the editor after pagination — even if the block containing
// them was moved or split across pages.
const CARET_START_ID = 'rv-pagination-caret-start';
const CARET_END_ID = 'rv-pagination-caret-end';

const makeCaretMarker = (doc, id, which) => {
  const m = doc.createElement('span');
  m.id = id;
  m.setAttribute('data-caret-marker', which);
  m.style.cssText = 'display:inline;font-size:0;line-height:0;padding:0;margin:0;';
  return m;
};

const removeExistingCaretMarkers = () => {
  if (!editorRef.current) return;
  editorRef.current.querySelectorAll(
    `#${CARET_START_ID}, #${CARET_END_ID}, [data-caret-marker]`
  ).forEach((el) => el.remove());
};

const saveCaretPosition = () => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editorRef.current?.contains(range.commonAncestorContainer)) return null;

  // Clear any stale markers from a previous pass that didn't clean up.
  removeExistingCaretMarkers();

  const doc = range.startContainer.ownerDocument || document;

  try {
    if (range.collapsed) {
      const marker = makeCaretMarker(doc, CARET_START_ID, 'start');
      range.insertNode(marker);
      return { collapsed: true };
    }

    // Non-collapsed: place an end marker at selection end first (so inserting
    // it doesn't shift the start), then a start marker at selection start.
    const endMarker = makeCaretMarker(doc, CARET_END_ID, 'end');
    const endRange = range.cloneRange();
    endRange.collapse(false);
    endRange.insertNode(endMarker);

    const startMarker = makeCaretMarker(doc, CARET_START_ID, 'start');
    const startRange = range.cloneRange();
    startRange.collapse(true);
    startRange.insertNode(startMarker);

    return { collapsed: false };
  } catch {
    removeExistingCaretMarkers();
    return null;
  }
};

const restoreCaretPosition = (saved) => {
  if (!saved || !editorRef.current) return;
  const editor = editorRef.current;

  // Search the entire editor — markers may have ridden a moved/split block
  // onto a different page, which is exactly the case we need to handle.
  const startMarker = editor.querySelector(`#${CARET_START_ID}`);
  const endMarker = editor.querySelector(`#${CARET_END_ID}`);

  const sel = window.getSelection();
  if (!sel) {
    removeExistingCaretMarkers();
    return;
  }

  try {
    const range = document.createRange();

    if (saved.collapsed) {
      if (!startMarker) return;
      range.setStartBefore(startMarker);
      range.collapse(true);
    } else if (startMarker && endMarker) {
      range.setStartAfter(startMarker);
      range.setEndBefore(endMarker);
    } else {
      // One marker lost (e.g. selection spanned a split). Collapse to the
      // surviving marker so the user still has a caret in a sensible place.
      const fallback = startMarker || endMarker;
      if (!fallback) return;
      range.setStartBefore(fallback);
      range.collapse(true);
    }

    sel.removeAllRanges();
    sel.addRange(range);

    // Focus the page body containing the (possibly new) caret so subsequent
    // typing lands in the right contenteditable.
    const node = range.startContainer;
    const body = (node.nodeType === 1 ? node : node.parentElement)
      ?.closest?.('.rv-page-body');
    if (body) {
      try { body.focus({ preventScroll: true }); } catch { body.focus(); }
    }
  } catch {
    // best-effort
  } finally {
    removeExistingCaretMarkers();
  }
};

// Find the best split point in text (prefer word boundaries)
const findSplitPoint = (text, maxChars) => {
  if (text.length <= maxChars) return text.length;
  
  // Look for a space or word boundary near the split point
  let splitAt = maxChars;
  for (let i = maxChars; i > maxChars * 0.5; i--) {
    if (text[i] === ' ' || text[i] === '\n') {
      splitAt = i;
      break;
    }
  }
  return splitAt;
};

// Split a paragraph that overflows and move excess to next page
const splitParagraphAtOverflow = (body, paragraph) => {
  const page = body.closest('.rv-page');
  if (!page) return false;
  
  const doc = body.ownerDocument;
  const bodyRect = body.getBoundingClientRect();
  const bodyBottom = bodyRect.bottom;
  
  // Get all text nodes in the paragraph
  const walker = doc.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT, null, false);
  const textNodes = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node);
    node = walker.nextNode();
  }
  
  if (textNodes.length === 0) return false;
  
  // Find which text node and character position causes overflow
  let splitNode = null;
  let splitOffset = 0;
  
  for (const textNode of textNodes) {
    const range = doc.createRange();
    for (let i = 0; i <= textNode.length; i++) {
      range.setStart(textNode, 0);
      range.setEnd(textNode, i);
      const rect = range.getBoundingClientRect();
      
      // If this position is past the page bottom, we found our split point
      if (rect.bottom > bodyBottom - 4) {
        splitNode = textNode;
        // Back up to find a good word boundary
        splitOffset = Math.max(0, i - 1);
        
        // Try to find a space to split at (word boundary)
        const text = textNode.textContent;
        for (let j = splitOffset; j > 0; j--) {
          if (text[j] === ' ') {
            splitOffset = j + 1; // Split after the space
            break;
          }
        }
        break;
      }
    }
    if (splitNode) break;
  }
  
  if (!splitNode || splitOffset <= 0) {
    // Couldn't find split point within paragraph, move whole paragraph
    return false;
  }
  
  // Create the next page body
  const nextBody = ensureNextPageBody(page);
  if (!nextBody) return false;
  
  // Clone the paragraph for the next page
  const newPara = doc.createElement(paragraph.tagName || 'p');
  // Copy styles/classes from original
  newPara.className = paragraph.className;
  if (paragraph.style.cssText) {
    newPara.style.cssText = paragraph.style.cssText;
  }
  
  // Move content after the split point to the new paragraph
  const splitRange = doc.createRange();
  splitRange.setStart(splitNode, splitOffset);
  splitRange.setEndAfter(paragraph.lastChild || paragraph);
  
  // Extract the overflow content
  const overflowContent = splitRange.extractContents();
  newPara.appendChild(overflowContent);
  
  // If the original paragraph is now empty, add a br
  if (!paragraph.textContent.trim() && !paragraph.querySelector('*')) {
    paragraph.innerHTML = '<br/>';
  }
  
  // Insert the new paragraph at the start of the next page
  let insertBefore = nextBody.firstElementChild;
  while (insertBefore && insertBefore.classList?.contains('rv-end-spacer')) {
    insertBefore = insertBefore.nextElementSibling;
  }
  if (!insertBefore) {
    insertBefore = nextBody.querySelector('.rv-end-spacer');
  }
  
  if (insertBefore) {
    nextBody.insertBefore(newPara, insertBefore);
  } else {
    nextBody.appendChild(newPara);
  }
  
  return true;
};

// Move the last block of a page to the front of the next page (simple approach)
const moveTrailingBlockToNextPage = (body, depth = 0) => {
  if (!body || depth > 100) return; // prevent infinite loops
  const page = body.closest('.rv-page');
  if (!page) return;

  // Only when it actually overflows the fixed-height page
  if (!isBodyOverflowing(body)) return;

  const doc = body.ownerDocument;

  // Get the last content block (not spacer, not empty)
  let lastBlock = body.lastElementChild;
  while (lastBlock && (
    lastBlock.classList?.contains('rv-end-spacer') ||
    (lastBlock.tagName === 'P' && lastBlock.textContent.trim() === '' && !lastBlock.querySelector('img, table, .rv-shape, .rv-textbox'))
  )) {
    lastBlock = lastBlock.previousElementSibling;
  }

  if (!lastBlock) return;

  // Handle tables specially - try to split rows
  if (lastBlock.classList?.contains('rv-table-wrap')) {
    const table = lastBlock.querySelector('.rv-table');
    const splitResult = splitTableAcrossPages(body, lastBlock, table);

    if (splitResult === true) {
      // Table was split, check if still overflowing
      if (isBodyOverflowing(body)) {
        moveTrailingBlockToNextPage(body, depth + 1);
      }
      return;
    } else if (splitResult === 'fits') {
      return; // Table fits, nothing to do
    }
    // If table can't be split, move the whole table below
  }

  // For paragraphs, try to split if the paragraph itself is causing overflow
  // Only if this is the ONLY block or the one causing overflow
  const isParagraphLike = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(lastBlock.tagName);
  if (isParagraphLike && lastBlock.textContent.trim().length > 20) {
    // Check if this paragraph starts on the page (partial overflow)
    const bodyRect = body.getBoundingClientRect();
    const blockRect = lastBlock.getBoundingClientRect();
    
    // If paragraph starts well above the bottom, try splitting it
    if (blockRect.top < bodyRect.bottom - 30) {
      const splitSuccess = splitParagraphAtOverflow(body, lastBlock);
      if (splitSuccess) {
        if (isBodyOverflowing(body)) {
          moveTrailingBlockToNextPage(body, depth + 1);
        }
        return;
      }
    }
  }

  // Handle lists specially - split at list item boundaries
  if (lastBlock.tagName === 'OL' || lastBlock.tagName === 'UL') {
    const splitResult = splitListAcrossPages(body, lastBlock);
    if (splitResult === true) {
      // List was split, check if still overflowing
      if (isBodyOverflowing(body)) {
        moveTrailingBlockToNextPage(body, depth + 1);
      }
      return;
    } else if (splitResult === 'fits') {
      return; // List fits, nothing to do
    }
    // If list can't be split (e.g., only one item), move the whole list below
  }

  // Move the entire last block to next page
  const nextBody = ensureNextPageBody(page);
  if (!nextBody) return;

  // Insert at the START of the next page (before first child or before spacer)
  const firstChild = nextBody.firstElementChild;
  if (firstChild) {
    nextBody.insertBefore(lastBlock, firstChild);
  } else {
    nextBody.appendChild(lastBlock);
  }

  // Ensure current page has a spacer at the end
  let spacer = body.querySelector('.rv-end-spacer');
  if (!spacer) {
    spacer = doc.createElement('p');
    spacer.className = 'rv-end-spacer';
    spacer.setAttribute('contenteditable', 'false');
    spacer.innerHTML = '<br/>';
  }
  body.appendChild(spacer);
  
  // Recursively check if still overflowing
  if (isBodyOverflowing(body)) {
    moveTrailingBlockToNextPage(body, depth + 1);
  }
};
// Move caret to the start of a .rv-page-body
const focusBodyStart = (body) => {
  if (!body) return;
  const sel = document.getSelection();
  const r = document.createRange();
  r.selectNodeContents(body);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
  body.focus();
};
// Place caret at the *end* of a .rv-page-body (just before the end spacer if present)
const focusBodyEndBeforeSpacer = (body) => {
  if (!body) return;
  const doc = body.ownerDocument;
  const sel = doc.getSelection();
  const r = doc.createRange();

  // If last child is a non-editable spacer, place before it
  const last = body.lastElementChild;
  if (last && last.classList?.contains?.('rv-end-spacer')) {
    r.setStartBefore(last);
    r.collapse(true);
  } else {
    r.selectNodeContents(body);
    r.collapse(false);
  }

  sel.removeAllRanges();
  sel.addRange(r);
  body.focus();
};


// Minimal text/paragraph insertion at the caret
const insertTextAtCaret = (text) => {
  const sel = document.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const r = sel.getRangeAt(0);
  r.deleteContents();
  r.insertNode(document.createTextNode(text));
  // place caret after inserted text
  r.setStart(r.endContainer, r.endOffset);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
};

const insertParagraphAtCaret = () => {
  const sel = document.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const r = sel.getRangeAt(0);
  // create <p><br/></p> and insert
  const p = document.createElement('p');
  p.innerHTML = '<br/>';
  r.deleteContents();
  r.insertNode(p);
  // move caret inside new paragraph
  const nr = document.createRange();
  nr.selectNodeContents(p);
  nr.collapse(true);
  sel.removeAllRanges();
  sel.addRange(nr);
};

// Pull first content block from next page to current page
const pullContentFromNextPage = (body) => {
  if (!body) return false;
  const page = body.closest('.rv-page');
  const nextPage = page?.nextElementSibling;
  
  if (!nextPage?.classList?.contains('rv-page')) return false;
  
  const nextBody = nextPage.querySelector('.rv-page-body');
  if (!nextBody) return false;
  
  // Find first content block in next page
  const firstContent = Array.from(nextBody.children).find(
    el => !el.classList?.contains('rv-end-spacer') && 
         !(el.tagName === 'P' && el.textContent.trim() === '' && !el.querySelector('*'))
  );
  
  if (!firstContent) return false;
  
  // Move it to current page (before the spacer)
  const spacer = body.querySelector('.rv-end-spacer');
  if (spacer) {
    body.insertBefore(firstContent, spacer);
  } else {
    body.appendChild(firstContent);
  }
  
  // Check if next page is now empty and should be removed
  const remainingContent = Array.from(nextBody.children).filter(
    el => !el.classList?.contains('rv-end-spacer') && 
         !(el.tagName === 'P' && el.textContent.trim() === '' && !el.querySelector('*'))
  );
  
  if (remainingContent.length === 0) {
    nextPage.remove();
    applyPageNumbers();
  }
  
  return true;
};

// Strip formatting from HTML, keep only text structure
const stripFormatting = (html) => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Remove all style attributes and classes
  temp.querySelectorAll('*').forEach(el => {
    el.removeAttribute('style');
    el.removeAttribute('class');
    // Convert headings to paragraphs
    if (/^H[1-6]$/.test(el.tagName)) {
      const p = document.createElement('p');
      p.innerHTML = el.innerHTML;
      el.replaceWith(p);
    }
  });
  
  // Remove spans that are just wrappers
  temp.querySelectorAll('span').forEach(span => {
    const parent = span.parentNode;
    while (span.firstChild) {
      parent.insertBefore(span.firstChild, span);
    }
    span.remove();
  });
  
  return temp.innerHTML;
};

// =====================================================================
// SINGLE-PASS REFLOW
//
// Lays every block out afresh in one deterministic pass: collect, clear each
// body to its spacer, place block by block, cut or move what overflows, drop
// unused pages. The pass itself is `reflowPages` in src/utils/paginate.js,
// shared with the Theme Editor's preview; what stays here is what the editor
// does around it — keeping the caret, renumbering lists, page numbers.
//
// Exposed as window.__rv_reflow() for manual testing.
// =====================================================================

const reflowDocument = () => {
  const editor = editorRef.current;
  if (!editor) return;
  if (!editor.querySelector('.rv-page')) return;

  const caretMarker = saveCaretPosition();

  try {
    // The layout itself is shared with the Theme Editor's preview
    // (src/utils/paginate.js). A theme starts every chapter on a new page.
    reflowPages(editor, reflowOptionsRef.current);

    try { syncListNumbering(); } catch {}
    try { applyPageNumbers(); } catch {}
  } finally {
    if (caretMarker) restoreCaretPosition(caretMarker);
  }
};

// Insert a list of block elements at the caret as SIBLINGS of the current
// top-level block — never nested inside it. This is the difference between
// `range.insertNode(frag)` (which nests) and what a real editor does (which
// splits the surrounding block and inserts between the halves).
//
// Steps:
//   1. Find the .rv-page-body holding the caret.
//   2. Walk up to the direct child of body that contains the caret.
//   3. Split that block at the caret: original keeps content before the
//      caret; a clone holds content after.
//   4. Insert new blocks between them, in order.
//   5. Drop the clone if it ended up empty.
// Returns the last inserted block (for caret placement).
const insertBlocksAtBodyLevel = (newBlocks, savedRange) => {
  if (!newBlocks || newBlocks.length === 0) return null;
  if (!savedRange) return null;

  const doc = newBlocks[0].ownerDocument || document;

  let host = savedRange.startContainer;
  if (host && host.nodeType === 3) host = host.parentElement;
  const body = host?.closest?.('.rv-page-body');
  if (!body) return null;

  let currentBlock = host;
  while (currentBlock && currentBlock.parentElement !== body) {
    currentBlock = currentBlock.parentElement;
  }
  if (!currentBlock || currentBlock.classList?.contains('rv-end-spacer')) {
    // Caret isn't inside a real block — fall back to appending before the
    // spacer so blocks still land at the body level.
    const spacer = Array.from(body.children).find((c) =>
      c.classList?.contains('rv-end-spacer')
    );
    newBlocks.forEach((b) => {
      if (spacer) body.insertBefore(b, spacer);
      else body.appendChild(b);
    });
    return newBlocks[newBlocks.length - 1];
  }

  // Split currentBlock at the caret. Everything from the caret to the end of
  // currentBlock moves into a clone with the same tag/attrs.
  const tailRange = doc.createRange();
  tailRange.setStart(savedRange.startContainer, savedRange.startOffset);
  tailRange.setEnd(currentBlock, currentBlock.childNodes.length);
  const tailContent = tailRange.extractContents();

  const tailBlock = doc.createElement(currentBlock.tagName);
  for (const a of currentBlock.attributes) tailBlock.setAttribute(a.name, a.value);
  tailBlock.appendChild(tailContent);

  // Insert new blocks after currentBlock, then the tail block.
  let after = currentBlock;
  newBlocks.forEach((b) => {
    after.insertAdjacentElement('afterend', b);
    after = b;
  });

  const tailHasContent =
    (tailBlock.textContent && tailBlock.textContent.trim().length > 0) ||
    !!tailBlock.querySelector('img, table, ul, ol, .rv-shape, .rv-textbox');
  if (tailHasContent) {
    after.insertAdjacentElement('afterend', tailBlock);
  }

  // If the original block is now empty, leave a placeholder <br/> so the
  // editor still has something focusable there.
  if (
    !currentBlock.textContent.trim() &&
    !currentBlock.querySelector('img, table, .rv-shape, .rv-textbox')
  ) {
    currentBlock.innerHTML = '<br/>';
  }

  return newBlocks[newBlocks.length - 1];
};

// Handle paste with formatting options
const handlePaste = (e) => {
  const clipboardData = e.clipboardData || window.clipboardData;
  if (!clipboardData) return;
  
  const html = clipboardData.getData('text/html');
  const text = clipboardData.getData('text/plain');
  
  // If there's HTML with formatting, show paste options menu
  if ((html && html.includes('style')) || (html && html.includes('class'))) {
    e.preventDefault();
    
    // Get cursor position for menu and SAVE the range
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0).cloneRange(); // Clone the range to save it
      const rect = range.getBoundingClientRect();
      
      setPasteMenu({
        x: rect.left || 100,
        y: rect.bottom + 5 || 100,
        html: html,
        text: text,
        savedRange: range // Save the range for later use
      });
    }
    return;
  }
  
  // No special formatting, let browser handle paste and then paginate
  setTimeout(() => {
    paginateAllPages();
    handleTextChange();
  }, 100);
};

// Paste with original formatting
const pasteWithFormatting = () => {
  if (!pasteMenu || !pasteMenu.savedRange) return;

  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(pasteMenu.savedRange);

  const range = pasteMenu.savedRange;
  range.deleteContents();

  // Parse the clipboard HTML and extract block-level elements. Anything that
  // isn't already block-level gets wrapped in a <p> so it can live at the
  // body level without breaking the editor's content model.
  const temp = document.createElement('div');
  temp.innerHTML = pasteMenu.html;
  // A copied piece of a running image arrives as a whole image, and a copied
  // half of a cut paragraph is never joined to whatever sits before it.
  releasePastedPieces(temp);
  stripSplitMarks(temp);

  const BLOCK_TAGS = new Set([
    'P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'UL', 'OL', 'BLOCKQUOTE', 'PRE', 'TABLE'
  ]);

  const newBlocks = [];
  Array.from(temp.children).forEach((child) => {
    if (BLOCK_TAGS.has(child.tagName)) {
      newBlocks.push(child);
    } else {
      // Inline element at top level — wrap in <p>.
      const wrap = document.createElement('p');
      wrap.appendChild(child);
      newBlocks.push(wrap);
    }
  });

  // If the clipboard had only inline content (no top-level elements), wrap
  // the whole HTML in a single <p>.
  if (newBlocks.length === 0 && temp.textContent.trim()) {
    const wrap = document.createElement('p');
    wrap.innerHTML = pasteMenu.html;
    newBlocks.push(wrap);
  }

  const lastBlock = insertBlocksAtBodyLevel(newBlocks, range);

  if (lastBlock) {
    const newRange = document.createRange();
    newRange.selectNodeContents(lastBlock);
    newRange.collapse(false);
    sel.removeAllRanges();
    sel.addRange(newRange);
  }

  setPasteMenu(null);

  setTimeout(() => {
    paginateAllPages();
    handleTextChange();
  }, 100);
};

// Paste matching document style (strip formatting)
const pasteMatchingStyle = () => {
  if (!pasteMenu || !pasteMenu.savedRange) return;

  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(pasteMenu.savedRange);

  const range = pasteMenu.savedRange;
  range.deleteContents();

  const lines = pasteMenu.text.split(/\n\n+/);
  const newBlocks = [];
  lines.forEach((line) => {
    if (line.trim()) {
      const p = document.createElement('p');
      p.textContent = line.trim();
      // Under a theme the paragraph takes the theme's type; an inline font
      // here would be one more place that hides it.
      if (!manuscriptTheme) {
        p.style.fontSize = '16px';
        p.style.fontFamily = 'Arial, sans-serif';
        p.style.marginBottom = '12px';
      }
      newBlocks.push(p);
    }
  });

  const lastBlock = insertBlocksAtBodyLevel(newBlocks, range);

  if (lastBlock) {
    const newRange = document.createRange();
    newRange.selectNodeContents(lastBlock);
    newRange.collapse(false);
    sel.removeAllRanges();
    sel.addRange(newRange);
  }

  setPasteMenu(null);

  setTimeout(() => {
    paginateAllPages();
    handleTextChange();
  }, 100);
};

// Paste as plain text
const pastePlainText = () => {
  if (!pasteMenu || !pasteMenu.savedRange) return;
  
  // Restore the saved range
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(pasteMenu.savedRange);
  
  const range = pasteMenu.savedRange;
  range.deleteContents();
  
  // Insert as single text node
  const textNode = document.createTextNode(pasteMenu.text);
  range.insertNode(textNode);
  
  // Move caret to end
  range.setStartAfter(textNode);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
  
  setPasteMenu(null);
  
  setTimeout(() => {
    paginateAllPages();
    handleTextChange();
  }, 100);
};

// True if this body no longer fits inside its fixed-height page

// Update numbers when config changes
useEffect(() => {
  applyPageNumbers();
}, [pageNumberCfg.enabled, pageNumberCfg.position, pageNumberCfg.format, pageNumberCfg.fontFamily, pageNumberCfg.fontSize]);

// Persist page-number settings per draft so they survive reload
useEffect(() => {
  try {
    const key = `rv.pageNumberCfg.${id || 'draft'}`;
    localStorage.setItem(key, JSON.stringify(pageNumberCfg));
  } catch {}
}, [id, pageNumberCfg]);

  useEffect(() => {
  const fetchDoc = async () => {
    draftLoadedRef.current = false;
    setLoadError('');
    setRecovery(null);
    try {
      const accessToken = localStorage.getItem('rv_access_token');

      const res = await fetch(`${API_BASE}/api/drafts/${id}`, {
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
        }
      });
      if (!res.ok) throw new Error('Failed to fetch document');

      const data = await res.json();

      // Ensure at least one page exists
      let html = data.content ?? '';
      if (!html.trim()) {
        html = `<section class="rv-page" data-type="page"><div class="rv-page-body" contenteditable="true"><p><br/></p><p class="rv-end-spacer" contenteditable="false"><br/></p></div></section>`;
      }

      setDoc(data);
      // The size the draft was quoted for, or the one the author chose. The
      // toolbar re-flows to it; nothing here has to touch the page directly.
      setTrimSize(data.trimSize || DEFAULT_BOOK_SIZE);
      setContent(html);

      // Hydrate images array ONLY if we don't already have images in state
      // This prevents overwriting newly added images that haven't been saved yet
      // — of THIS draft. Widgets left in state from a different draft are not
      // unsaved work here, and keeping them would save them into this one.
      const serverImages = Array.isArray(data.images) ? data.images : [];
      const sameDraft = savedImagesRef.current?.draftId === data._id;
      savedImagesRef.current = { draftId: data._id, key: imagesKey(serverImages) };
      setImages(prev => {
        // If we already have images in state, keep them (don't overwrite with server data)
        if (sameDraft && prev && prev.length > 0) return prev;
        // Otherwise, load from server
        return serverImages;
      });

      // Refresh presigned URLs for all images with s3Key in the draft
      // This solves expired URL issue (presigned URLs expire after 1 hour)
      // Check if there are ANY images in the content (with or without data-s3-key)
      const hasAnyImages = (html && (html.includes('<img') || html.includes('data-s3-key='))) || 
                            (Array.isArray(data.images) && data.images.length > 0);
      
      // Store URL maps for DOM update after render
      let imageUrlMap = {};        // s3Key -> newUrl (for images with data-s3-key)
      let urlReplacements = {};    // oldUrl -> newUrl (for legacy images without data-s3-key)
      
      if (hasAnyImages) {
        try {
          const urlRefreshRes = await fetch(`${API_BASE}/api/drafts/${id}/images/refresh-urls`, {
            method: 'POST',
            credentials: 'omit',
            headers: {
              'Content-Type': 'application/json',
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
            }
          });
          if (urlRefreshRes.ok) {
            const refreshData = await urlRefreshRes.json();
            imageUrlMap = refreshData.imageUrls || {};
            urlReplacements = refreshData.urlReplacements || {};
            
            // Also update overlay images if any
            setImages(prev => {
              if (Array.isArray(prev) && prev.length > 0) {
                return prev.map(img => 
                  img.s3Key && imageUrlMap[img.s3Key] 
                    ? { ...img, src: imageUrlMap[img.s3Key] }
                    : img
                );
              }
              return prev;
            });
          }
        } catch (err) {
          console.warn('Failed to refresh image URLs:', err);
        }
      }

      setTimeout(() => {
        if (editorRef.current) {
          // Set the HTML content first
          editorRef.current.innerHTML = html;
          draftLoadedRef.current = true;
          noteServerContent(data.content ?? '');
          
          // Update inline image src attributes with fresh presigned URLs
          // Method 1: For images WITH data-s3-key attribute
          if (Object.keys(imageUrlMap).length > 0) {
            editorRef.current.querySelectorAll('img[data-s3-key]').forEach(imgEl => {
              const s3Key = imgEl.getAttribute('data-s3-key');
              if (s3Key && imageUrlMap[s3Key]) {
                imgEl.src = imageUrlMap[s3Key];
              }
            });
          }
          
          // Method 2: For legacy images WITHOUT data-s3-key (replace old URLs directly)
          if (Object.keys(urlReplacements).length > 0) {
            editorRef.current.querySelectorAll('img:not([data-s3-key])').forEach(imgEl => {
              const oldSrc = imgEl.getAttribute('src');
              if (oldSrc && urlReplacements[oldSrc]) {
                imgEl.src = urlReplacements[oldSrc];
                // Also add data-s3-key for future refreshes
                const s3Key = Object.keys(imageUrlMap).find(key => imageUrlMap[key] === urlReplacements[oldSrc]);
                if (s3Key) {
                  imgEl.setAttribute('data-s3-key', s3Key);
                }
              }
            });
          }
          
          try {
            migratePageStructure(); // Add headers/footers to existing pages
            // Stamp structural heading markers on legacy documents so the
            // navigation outline can read them. Persist if it changed anything.
            if (backfillHeadings(editorRef.current)) triggerChange();
            applyPageNumbers?.();
            setTimeout(() => { paginateAllPages(); }, 100);
          } catch {}

          // Work an earlier visit left unsaved in this browser: offer it back.
          try { pruneLocalDrafts(); } catch {}
          checkForLeftovers(id);
        }
      }, 0);
    } catch (err) {
      console.error('Error loading document:', err);
      setLoadError("Couldn't load this draft. Editing is paused until it loads.");
    }
  };

  fetchDoc();
}, [id]);

  useEffect(() => {
    if (!doc || !doc._id) return;

    const loadCollaborators = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/drafts/${doc._id}/collaborators`, {
          credentials: 'omit',
          headers: {
            'Accept': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
          }
        });
        if (!res.ok) {
          // 403 if not author/collaborator; just ignore in UI
          return;
        }
        const list = await res.json();
        setCollaborators(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error loading collaborators:', err);
      }
    };

    const loadOutgoingInvitations = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/invitations/outgoing?draftId=${doc._id}`, {
          credentials: 'omit',
          headers: {
            'Accept': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
          }
        });
        if (res.ok) {
          const data = await res.json();
          setOutgoingInvitations(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Error loading outgoing invitations:', err);
      }
    };

    loadCollaborators();
    loadOutgoingInvitations();
  }, [doc, accessToken]);


    
// Persist the floating image widgets (debounced) — only when they actually
// change. Loading the draft, refreshing presigned links and a token refresh
// all used to write the list back: a needless PUT on every load, and an editor
// holding an older list could overwrite a collaborator's newer one.
imagesTokenRef.current = accessToken;

const sendImages = (pending, keepalive = false) => {
  const token = imagesTokenRef.current;
  fetch(`${API_BASE}/api/drafts/${pending.draftId}/images`, {
    method: 'PUT',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: pending.body,
    ...(keepalive ? { keepalive: true } : {}),
  }).then((res) => {
    if (res.ok && savedImagesRef.current?.draftId === pending.draftId) {
      savedImagesRef.current = { draftId: pending.draftId, key: pending.key };
    }
  }).catch(err => console.error('Save images failed:', err));
};

useEffect(() => {
  const draftId = doc?._id;
  const saved = savedImagesRef.current;
  // Not hydrated yet: nothing to compare against, nothing of ours to save.
  if (!draftId || !saved || saved.draftId !== draftId) return;

  if (saveImagesDebounced.current) {
    clearTimeout(saveImagesDebounced.current);
    saveImagesDebounced.current = null;
  }
  const key = imagesKey(images);
  if (key === saved.key) {
    pendingImagesRef.current = null;
    return;
  }
  const pending = { draftId, key, body: JSON.stringify({ images }) };
  pendingImagesRef.current = pending;
  saveImagesDebounced.current = setTimeout(() => {
    saveImagesDebounced.current = null;
    pendingImagesRef.current = null;
    sendImages(pending);
  }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [images, doc?._id]);

// Leaving the draft (unmount, or switching to another) sends a change that is
// still waiting out the debounce — and only then.
useEffect(() => () => {
  if (saveImagesDebounced.current) {
    clearTimeout(saveImagesDebounced.current);
    saveImagesDebounced.current = null;
  }
  if (pendingImagesRef.current) {
    sendImages(pendingImagesRef.current, true);
    pendingImagesRef.current = null;
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [doc?._id]);

useEffect(() => {
  const editor = editorRef.current;
  if (!editor) return;

  const onClick = (e) => {
    const clickedCell = e.target.closest('.rv-cell');

    // Prefer the wrapper; if user clicked the table, climb to wrapper
    const wrapEl =
      e.target.closest('.rv-table-wrap') ||
      (e.target.closest('table.rv-table')?.closest('.rv-table-wrap') ?? null);

    if (wrapEl && !clickedCell) {
      if (selectedTableRef.current && selectedTableRef.current !== wrapEl) {
        selectedTableRef.current.classList.remove('is-focused');
      }
      selectedTableRef.current = wrapEl;
      wrapEl.classList.add('is-focused');
      editor.focus(); // ensure keydown lands on editor
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Clicked elsewhere → clear selection
    if (selectedTableRef.current) {
      selectedTableRef.current.classList.remove('is-focused');
      selectedTableRef.current = null;
    }
  };

  editor.addEventListener('click', onClick);
  return () => editor.removeEventListener('click', onClick);
}, []);

// Track caret even if focus blips to the ribbon (prevents “lost target” on delete)
useEffect(() => {
  const host = editorRef.current;
  if (!host) return;

  const onSelChange = () => {
    try {
      const sel = document.getSelection?.();
      if (!sel || sel.rangeCount === 0) return;
      const node = sel.anchorNode;
      if (!node) return;
      const body = (node.nodeType === 1 ? node : node.parentElement)?.closest?.('.rv-page-body');
      if (body && host.contains(body)) {
        lastBodyRef.current = body;
      }
    } catch {}
  };

  document.addEventListener('selectionchange', onSelChange, true);
  return () => document.removeEventListener('selectionchange', onSelChange, true);
}, []);

// Ensure page structure is always valid AND keep page numbers updated
useEffect(() => {
  const host = editorRef.current;
  if (!host) return;

  // Initial pass
  sanitizePageStructure(host);
  applyPageNumbers();

  // Observe structural changes - debounce sanitize to avoid focus loss on rapid typing
  let pnRAF = 0;
  let sanitizeTimer = null;
  const mo = new MutationObserver((mutations) => {
    // Only check for structural changes that need sanitizing
    const needsSanitize = mutations.some(m => {
      // Page-level changes always need sanitizing
      if (Array.from(m.addedNodes).some(n => n.classList?.contains?.('rv-page')) ||
          Array.from(m.removedNodes).some(n => n.classList?.contains?.('rv-page'))) {
        return true;
      }
      // Check if contenteditable attributes changed
      if (m.type === 'attributes' && m.attributeName === 'contenteditable') {
        return true;
      }
      return false;
    });
    
    if (needsSanitize) {
      if (sanitizeTimer) clearTimeout(sanitizeTimer);
      sanitizeTimer = setTimeout(() => {
        sanitizePageStructure(host);
      }, 100);
    }
    
    // Always update page numbers (lightweight)
    if (pnRAF) cancelAnimationFrame(pnRAF);
    pnRAF = requestAnimationFrame(applyPageNumbers);
  });
  mo.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['contenteditable'] });

  return () => { 
    if (sanitizeTimer) clearTimeout(sanitizeTimer);
    try { mo.disconnect(); } catch {} 
  };
}, []);

useEffect(() => {
  const host = editorRef.current;
  if (!host) return;

  // On mount, make sure we start with at least one page
  ensureAtLeastOnePage();

  // If the last page is removed (not via undo), recreate one
  // Use a debounce to avoid interfering with undo/redo operations
  let recreateTimer = null;
  const mo = new MutationObserver(() => {
    // Clear any pending recreation
    if (recreateTimer) clearTimeout(recreateTimer);
    
    // Only recreate after a short delay - this allows undo to complete
    // without triggering page creation
    recreateTimer = setTimeout(() => {
      if (!host.querySelector('.rv-page')) {
        ensureAtLeastOnePage();
        // also persist so autosave doesn't capture purple text nodes
        try { handleTextChange(); } catch {}
      }
    }, 100);
  });

  mo.observe(host, { childList: true, subtree: false });
  return () => {
    if (recreateTimer) clearTimeout(recreateTimer);
    mo.disconnect();
  };
}, []);

useEffect(() => {
  const host = editorRef.current;
  if (!host) return;

  const onFocusIn = (e) => {
    const body = e.target?.closest?.('.rv-page-body');
    if (body && host.contains(body)) lastBodyRef.current = body;
  };

  host.addEventListener('focusin', onFocusIn);
  return () => host.removeEventListener('focusin', onFocusIn);
}, []);

  // Sync title from doc
  useEffect(() => {
    if (doc?.title) {
      setEditableTitle(doc.title);
    }
  }, [doc?.title]);

  // Cache for last formatting to prevent unnecessary re-renders
  const lastFormattingRef = useRef(null);

  // Notion-style formatting detection - walks DOM tree properly
  const getCurrentFormatting = () => {
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;
      
      const range = sel.getRangeAt(0);
      let node = range.startContainer;
      
      // Get element node (not text node)
      if (node.nodeType === 3) {
        node = node.parentElement;
      }
      
      if (!node || !editorRef.current?.contains(node)) return null;
      
      // Initialize formatting object with defaults
      const formatting = {
        fontFamily: 'Arial',
        fontSize: '16px',
        bold: false,
        italic: false,
        underline: false,
        textAlign: 'left',
        textStyle: null
      };
      
      // FIRST: Check for data-text-style marker (most reliable for style detection)
      let styleMarker = null;
      let checkNode = node;
      while (checkNode && editorRef.current?.contains(checkNode)) {
        if (checkNode.getAttribute && checkNode.getAttribute('data-text-style')) {
          styleMarker = checkNode.getAttribute('data-text-style');
          break;
        }
        checkNode = checkNode.parentElement;
      }
      formatting.textStyle = styleMarker;
      
      // SECOND: Walk UP from current node to collect ALL formatting (spans override blocks)
      let currentNode = node;
      const formattingChain = [];
      
      while (currentNode && editorRef.current?.contains(currentNode)) {
        if (currentNode.nodeType === 1) { // Element node
          formattingChain.unshift(currentNode);
        }
        if (currentNode === currentNode.ownerDocument?.body) break;
        currentNode = currentNode.parentElement;
      }
      
      // Process chain from root to current (so closer nodes override farther ones)
      for (const element of formattingChain) {
        const tag = element.nodeName?.toUpperCase();
        
        // Collect semantic markup
        if (tag === 'B' || tag === 'STRONG') formatting.bold = true;
        if (tag === 'I' || tag === 'EM') formatting.italic = true;
        if (tag === 'U') formatting.underline = true;
        
        // Collect inline styles (closest node wins)
        if (element.style) {
          if (element.style.fontSize) {
            formatting.fontSize = element.style.fontSize;
          }
          if (element.style.fontFamily) {
            formatting.fontFamily = element.style.fontFamily.replace(/"/g, '').split(',')[0].trim();
          }
          if (element.style.fontWeight === 'bold' || element.style.fontWeight === '700' || parseInt(element.style.fontWeight) >= 600) {
            formatting.bold = true;
          }
          if (element.style.fontStyle === 'italic') {
            formatting.italic = true;
          }
          if (element.style.textDecoration?.includes('underline') || 
              element.style.textDecorationLine?.includes('underline')) {
            formatting.underline = true;
          }
          if (element.style.textAlign) {
            formatting.textAlign = element.style.textAlign;
          }
        }
      }
      
      // THIRD: If no block-level styles found, check computed styles on closest block
      const block = node.closest('p, div, h1, h2, h3, h4, h5, h6, li, td, th');
      if (block && !formatting.fontSize.includes('px')) {
        const blockComputed = window.getComputedStyle(block);
        formatting.fontSize = blockComputed.fontSize;
      }
      if (block && formatting.textAlign === 'left') {
        const blockComputed = window.getComputedStyle(block);
        formatting.textAlign = blockComputed.textAlign;
      }
      
      return formatting;
    } catch (e) {
      console.error('[Formatting] Error:', e);
      return null;
    }
  };

  // Update formatting state with caching to prevent flicker
  const updateFormattingState = () => {
    const formatting = getCurrentFormatting();
    if (!formatting) return;
    
    // Only update if something actually changed (prevents unnecessary re-renders)
    const lastStr = JSON.stringify(lastFormattingRef.current);
    const newStr = JSON.stringify(formatting);
    
    if (lastStr !== newStr) {
      lastFormattingRef.current = formatting;
      setCurrentFormatting(formatting);
    }
  };

  // Listen for selection changes to update formatting state (IMMEDIATE, not debounced)
  useEffect(() => {
    const handleSelectionChange = () => {
      // Update immediately without debouncing for responsive toolbar
      updateFormattingState();
    };

    // Listen for selection changes
    document.addEventListener('selectionchange', handleSelectionChange);
    
    const editor = editorRef.current;
    if (editor) {
      // Update on all cursor movements and interactions
      editor.addEventListener('click', handleSelectionChange);
      editor.addEventListener('keyup', handleSelectionChange);
      editor.addEventListener('keydown', handleSelectionChange);
      editor.addEventListener('mouseup', handleSelectionChange);
      editor.addEventListener('input', handleSelectionChange);
    }

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (editor) {
        editor.removeEventListener('click', handleSelectionChange);
        editor.removeEventListener('keyup', handleSelectionChange);
        editor.removeEventListener('keydown', handleSelectionChange);
        editor.removeEventListener('mouseup', handleSelectionChange);
        editor.removeEventListener('input', handleSelectionChange);
      }
    };
  }, []);

  const handleTextChange = () => {
    // A real user edit — hand Ctrl+Z back to the native typing-undo stack.
    lastStructuralRef.current = false;
    const updatedContent = getCleanHTML(editorRef.current);
    setContent(updatedContent);
    
    // Update formatting state
    updateFormattingState();

    // Live collab: debounced Socket.IO emit (150 ms, via hook)
    emitDraftUpdate(updatedContent);

    // Track our own edit locally so jointly-edited paragraphs blend both
    // authors' colors on this screen (remote clients only send OTHER users).
    recordLocalEdit();

    // Autosave: local 400 ms + server 60 s (via hook)
    triggerChange();
    
    // Sync list numbering across pages (in case items were added/removed)
    syncListNumbering();
    
    // Check for overflow after a delay (500ms) - only paginates if truly overflowing
    paginateAllPages();
  };

  const handleTitleChange = (newTitle) => {
    setEditableTitle(newTitle);
    
    // Debounced auto-save
    if (titleSaveTimerRef.current) {
      clearTimeout(titleSaveTimerRef.current);
    }
    
    titleSaveTimerRef.current = setTimeout(async () => {
      if (!id || !newTitle.trim()) return;
      
      try {
        await fetch(`${API_BASE}/api/drafts/${id}`, {
          method: 'PUT',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
          },
          body: JSON.stringify({ title: newTitle.trim() }),
        });
        
        // Update local doc state
        setDoc(prev => ({ ...prev, title: newTitle.trim() }));
      } catch (err) {
        console.error('Failed to save title:', err);
      }
    }, 800); // 800ms debounce
  };

  const handleAddCollaborator = async (user, extras = {}) => {
    if (!doc?._id || !user?.id) return;
    try {
      const res = await fetch(`${API_BASE}/api/drafts/${doc._id}/collaborators`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
        },
        body: JSON.stringify({
          user_id: user.id,
          role: extras.role || 'edit',
          message: extras.message || undefined,
          dueDate: extras.dueDate || undefined,
          expirationDate: extras.expirationDate || undefined,
        })
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error('Failed to invite collaborator:', errText);
        return;
      }

      const data = await res.json();

      // Add to outgoing invitations for status display
      if (data.invitation) {
        setOutgoingInvitations(prev => [
          { ...data.invitation, toUsername: user.username, role: 'edit', type: 'draft', status: 'pending' },
          ...prev,
        ]);
      }
    } catch (err) {
      console.error('Error inviting collaborator:', err);
    }
  };

  const handleRemoveCollaborator = async (userId) => {
    if (!doc?._id || !userId) return;
    try {
      const res = await fetch(`${API_BASE}/api/drafts/${doc._id}/collaborators/${userId}`, {
        method: 'DELETE',
        credentials: 'omit',
        headers: {
          'Accept': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
        }
      });
      if (!res.ok) {
        console.error('Failed to remove collaborator:', await res.text());
        return;
      }

      setCollaborators(prev => prev.filter(c => c._id !== userId));
    } catch (err) {
      console.error('Error removing collaborator:', err);
    }
  };

  // Called by PublishModal with the finalized fields.
  const handleConfirmPublish = async (updatedFields) => {
    try {
      // Before the draft goes for review, not just before the redirect:
      // otherwise the reviewer can open a version up to 60 s behind the
      // author's last keystroke.
      await forceSave(true);
      const res = await fetch(`${API_BASE}/api/submit-review/${doc._id}`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
        },
        body: JSON.stringify(updatedFields)
      });

      if (res.ok) {
        setDoc(prev => ({ ...prev, ...updatedFields, status: 'pending_review' }));
        setShowPublishModal(false);
        navigate('/dashboard', { replace: true });
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Failed to submit for review. ${err?.error ? `(${err.error})` : ''}`);
      }
    } catch (err) {
      console.error('Publish error:', err);
      alert('Network error while submitting for review.');
    }
  };
   const run = (cmd, value = null) => {
    // Ensure editor has focus (required for Safari/iOS)
    const editor = editorRef.current;
    if (editor) {
      editor.focus();
    }
    
    // restore the caret where the user left it so commands apply correctly
    restoreIntoEditor();
    
    try {
      if (value !== null) {
        document.execCommand(cmd, false, value);
      } else {
        document.execCommand(cmd, false, null);
      }
    } catch (e) {
      console.warn('execCommand failed:', cmd, e);
    } finally {
      // keep our latest range fresh after formatting
      captureSelection();
    }
  };
  // Inline styles
  const onBold = () => run('bold');
  const onItalic = () => run('italic');
  const onUnderline = () => run('underline');

  // Alignment
  const onAlignLeft = () => run('justifyLeft');
  const onAlignCenter = () => run('justifyCenter');
  const onAlignRight = () => run('justifyRight');

  // Lists
  const onBulletedList = () => run('insertUnorderedList');
  const onNumberedList = () => run('insertOrderedList');

  // History
  const onUndo = () => run('undo');
  const onRedo = () => run('redo');

  // Fonts - iOS/Safari compatible
  const onFontFamilyChange = (family) => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    
    try {
      // Focus editor first
      editor.focus();
      
      // Restore selection using stored boundaries (iOS Safari compatible)
      if (!selectionData) {
        // Store as pending format for next typing
        setPendingFormat(prev => ({ ...prev, fontFamily: family }));
        return;
      }
      
      let range;
      try {
        range = document.createRange();
        range.setStart(selectionData.startContainer, selectionData.startOffset);
        range.setEnd(selectionData.endContainer, selectionData.endOffset);
        
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      } catch (e) {
        return;
      }
      
      // If nothing selected, store for next typing
      if (range.collapsed) {
        setPendingFormat(prev => ({ ...prev, fontFamily: family }));
        return;
      }
      
      // Extract and wrap - use the range directly
      const contents = range.extractContents();
      const wrapper = document.createElement('span');
      wrapper.style.fontFamily = family;
      wrapper.style.setProperty('font-family', family, 'important'); // Force override
      wrapper.appendChild(contents);
      
      // Clean up any nested font-family spans to avoid conflicts
      const nestedSpans = wrapper.querySelectorAll('span[style*="font-family"]');
      nestedSpans.forEach(span => {
        if (span !== wrapper) {
          span.style.fontFamily = '';
        }
      });
      
      // Insert the wrapper
      range.insertNode(wrapper);
      
      // Restore selection to cover the inserted content
      const sel = window.getSelection();
      if (sel) {
        const newRange = document.createRange();
        newRange.selectNodeContents(wrapper);
        sel.removeAllRanges();
        sel.addRange(newRange);
      }
      
      // Force a reflow to ensure Safari applies the style
      void wrapper.offsetHeight;
      
      captureSelection();
    } catch (e) {
      // Silently fail
    }
  };

  // Apply font size - iOS/Safari compatible
  const onFontSizeChange = (sizePx) => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    
    try {
      // Focus editor first
      editor.focus();
      
      // Restore selection using stored boundaries (iOS Safari compatible)
      if (!selectionData) {
        // Store as pending format for next typing
        setPendingFormat(prev => ({ ...prev, fontSize: sizePx }));
        return;
      }
      
      let range;
      try {
        range = document.createRange();
        range.setStart(selectionData.startContainer, selectionData.startOffset);
        range.setEnd(selectionData.endContainer, selectionData.endOffset);
        
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      } catch (e) {
        return;
      }
      
      // If nothing selected, store for next typing
      if (range.collapsed) {
        setPendingFormat(prev => ({ ...prev, fontSize: sizePx }));
        return;
      }
      
      // Extract and wrap - use the range directly
      const contents = range.extractContents();
      const wrapper = document.createElement('span');
      wrapper.style.fontSize = sizePx;
      wrapper.style.setProperty('font-size', sizePx, 'important'); // Force override parent styles
      wrapper.appendChild(contents);
      
      // Clean up any nested font-size spans to avoid conflicts
      const nestedSpans = wrapper.querySelectorAll('span[style*="font-size"]');
      nestedSpans.forEach(span => {
        if (span !== wrapper) {
          span.style.fontSize = '';
        }
      });
      
      // Insert the wrapper
      range.insertNode(wrapper);
      
      // Restore selection to cover the inserted content
      const sel = window.getSelection();
      if (sel) {
        const newRange = document.createRange();
        newRange.selectNodeContents(wrapper);
        sel.removeAllRanges();
        sel.addRange(newRange);
      }
      
      // Force a reflow to ensure Safari applies the style
      void wrapper.offsetHeight;
      
      captureSelection();
    } catch (e) {
      // Silently fail
    }
  };

  // Text color
  const onTextColorChange = (color) => {
    const editor = editorRef.current;
    if (editor) {
      editor.focus();
    }
    document.execCommand('styleWithCSS', false, true);
    run('foreColor', color);
    captureSelection();
  };

  // Line spacing
  const onLineSpacingChange = (spacing) => {
    const editor = editorRef.current;
    if (editor) {
      editor.focus();
    }
    
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    
    // Get the current block element(s)
    const range = sel.getRangeAt(0);
    let container = range.commonAncestorContainer;
    
    // If it's a text node, get its parent element
    if (container.nodeType === Node.TEXT_NODE) {
      container = container.parentElement;
    }
    
    // Find the closest block-level element (p, div, h1-h6, etc.)
    let blockElement = container;
    while (blockElement && blockElement !== editorRef.current) {
      const display = window.getComputedStyle(blockElement).display;
      if (display === 'block' || blockElement.tagName.match(/^(P|DIV|H[1-6]|LI)$/)) {
        break;
      }
      blockElement = blockElement.parentElement;
    }
    
    if (blockElement && blockElement !== editorRef.current) {
      blockElement.style.lineHeight = spacing;
    }
    
    captureSelection();
  };

  // Strikethrough
  const onStrikethrough = () => run('strikeThrough');

  // Justify (full)
  const onAlignJustify = () => run('justifyFull');

  // Subscript / Superscript
  const onSubscript = () => run('subscript');
  const onSuperscript = () => run('superscript');

  // Clear formatting
  const onClearFormatting = () => {
    const editor = editorRef.current;
    if (editor) editor.focus();
    run('removeFormat');
  };

  // Highlight / background color
  const onHighlightColorChange = (color) => {
    const editor = editorRef.current;
    if (editor) editor.focus();
    document.execCommand('styleWithCSS', false, true);
    run('hiliteColor', color);
  };

  // List indent increase / decrease
  const onIndentIncrease = () => run('indent');
  const onIndentDecrease = () => run('outdent');

  // Paragraph spacing (before / after in px)
  const onParagraphSpacingChange = ({ before, after }) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    restoreIntoEditor(); // selection is gone by blur/Enter — restore saved range first
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let container = sel.getRangeAt(0).commonAncestorContainer;
    if (container.nodeType === Node.TEXT_NODE) container = container.parentElement;
    let block = container;
    while (block && block !== editor) {
      if (['P','DIV','H1','H2','H3','H4','H5','H6','LI'].includes(block.tagName)) break;
      block = block.parentElement;
    }
    if (block && block !== editor) {
      if (before !== undefined) block.style.marginTop = `${before}px`;
      if (after !== undefined) block.style.marginBottom = `${after}px`;
    }
    captureSelection();
  };

  // Header / Footer visibility on all pages
  // --- Heading model helpers (Navigation/Outline) ---
  // Return an element's existing block id, or assign+return a fresh one.
  const ensureBlockId = (el) => {
    if (!el) return null;
    // The same pass the outline runs, so there is one rule for ids: Enter
    // copies a block's id onto the new paragraph and the copy gets its own
    // (or a comment or heading made on it would attach to the paragraph
    // above), while both halves of a paragraph cut at a page break share one.
    if (editorRef.current) ensureBlockIds(editorRef.current, makeBlockId);
    let bid = el.getAttribute('data-block-id');
    if (!bid) {
      bid = makeBlockId();
      el.setAttribute('data-block-id', bid);
    }
    return bid;
  };

  // Turn the caret's block into a heading of `level` (1..4), or back into normal
  // body text when `level` is 0. This is the structural marker the outline reads;
  // the inline styling keeps the block looking like the old heading dropdown.
  const applyHeadingToBlock = (level) => {
    const block = getActiveBlock();
    if (!block) return;

    if (!level) {
      // Demote to normal text: drop the structural marker and heading styling.
      // Not the block id — it's the line's identity, not its style, and
      // dropping it orphaned any comment on the heading into "Unassigned".
      block.removeAttribute('data-heading-level');
      block.style.removeProperty('font-size');
      block.style.removeProperty('font-weight');
      block.style.removeProperty('font-family');
      block.style.removeProperty('text-align');
    } else {
      const style = HEADING_STYLES[level] || HEADING_STYLES[1];
      block.setAttribute('data-heading-level', String(level));
      ensureBlockId(block);
      block.style.fontSize = style.fontSize;
      block.style.fontWeight = style.fontWeight;
      block.style.fontFamily = style.fontFamily;
      block.style.textAlign = style.textAlign;
    }
    triggerChange();
  };

  // One-time migration: existing drafts were written before the structural
  // heading model, so their "headings" are only styled paragraphs (often carrying
  // an inner <span data-text-style="heading1|title|…>) or real <h1>-<h4> tags.
  // Stamp the block-level marker so those documents get an outline too. Best
  // effort — visual styling is left untouched. Returns true if anything changed.
  // Put an earlier visit's unsaved work back into the editor, through the same
  // steps a load takes, and save it. Presigned image links in the copy may have
  // expired, so images the current page also shows keep its fresh links.
  const restoreRecovery = () => {
    const editor = editorRef.current;
    const rec = recovery;
    if (!editor || !rec || !draftLoadedRef.current) return;
    const fresh = {};
    editor.querySelectorAll('img[data-s3-key]').forEach((img) => {
      fresh[img.getAttribute('data-s3-key')] = img.getAttribute('src');
    });
    editor.innerHTML = rec.html;
    editor.querySelectorAll('img[data-s3-key]').forEach((img) => {
      const url = fresh[img.getAttribute('data-s3-key')];
      if (url) img.setAttribute('src', url);
    });
    try {
      migratePageStructure();
      backfillHeadings(editor);
      applyPageNumbers?.();
    } catch {}
    const updated = getCleanHTML(editor);
    setContent(updated);
    emitDraftUpdate(updated);
    triggerChange();
    setTimeout(() => { paginateAllPages(); }, 100);
    // Restored work is this visit's now (its own snapshot follows the change
    // above). Copies up to this one have served their turn; a newer one is
    // still unsaved work, and is offered next.
    clearLeftoverDrafts(id, visitId, { upToTs: rec.ts });
    setRecovery(null);
    checkForLeftovers(id);
  };

  const discardRecovery = () => {
    clearLeftoverDrafts(id, visitId, { upToTs: recovery?.ts });
    setRecovery(null);
    checkForLeftovers(id);
  };

  const backfillHeadings = (editor) => {
    if (!editor) return false;
    const styleToLevel = (v) => {
      if (v === 'title' || v === 'heading1') return 1;
      if (v === 'heading2') return 2;
      if (v === 'heading3') return 3;
      if (v === 'heading4') return 4;
      return 0; // subtitle/quote/normal are not chapter headings
    };
    let changed = false;
    const bodies = editor.querySelectorAll('.rv-page-body');
    bodies.forEach((body) => {
      Array.from(body.children).forEach((block) => {
        if (block.classList?.contains('rv-end-spacer')) return;
        if (block.hasAttribute('data-heading-level')) return;

        let level = 0;
        const tag = block.tagName;
        if (/^H[1-4]$/.test(tag)) {
          level = Number(tag[1]);
        } else if (['P', 'DIV'].includes(tag)) {
          const marker = block.matches('[data-text-style]')
            ? block
            : block.querySelector('[data-text-style]');
          if (marker) level = styleToLevel(marker.getAttribute('data-text-style'));
        }

        if (level >= 1 && level <= 4) {
          block.setAttribute('data-heading-level', String(level));
          ensureBlockId(block);
          changed = true;
        }
      });
    });
    return changed;
  };

  const onToggleHeader = () => {
    const pages = document.querySelectorAll('.rv-page');
    pages.forEach(page => {
      const header = page.querySelector('.rv-page-header');
      if (header) header.style.display = header.style.display === 'none' ? '' : 'none';
    });
    // Header height just changed (→ 0 or back) — the ruler's body origin and
    // limits depend on it, so re-measure immediately.
    measurePageGeomRef.current?.();
  };

  const onToggleFooter = () => {
    const pages = document.querySelectorAll('.rv-page');
    pages.forEach(page => {
      const footer = page.querySelector('.rv-page-footer');
      if (footer) footer.style.display = footer.style.display === 'none' ? '' : 'none';
    });
    measurePageGeomRef.current?.();
  };

  // Page setup — paper size and orientation
  const onPageSetupChange = ({ width, height }) => {
    const container = document.querySelector('.editor-main-content');
    if (!container) return;
    // Strip residual inline styles from all pages — inline styles override
    // CSS custom properties, so any leftover value causes pages to render
    // at inconsistent sizes.
    container.querySelectorAll('.rv-page').forEach(page => {
      page.style.removeProperty('width');
      page.style.removeProperty('height');
      page.style.removeProperty('min-height');
    });
    // Set CSS custom properties — all pages (current and newly created) inherit these
    if (width)  container.style.setProperty('--rv-page-width',  width);
    if (height) container.style.setProperty('--rv-page-height', height);

    // Keep the ruler's dimensions/limits in sync with the new paper size.
    const numW = parseFloat(width)  || pageSize.width;
    const numH = parseFloat(height) || pageSize.height;
    setPageSize({ width: numW, height: numH });

    // A smaller page (e.g. switching from Tabloid down to Letter, or to a
    // landscape orientation with far less height) can leave the existing
    // margins wider than the new page allows — reclamp so the printable area
    // never inverts. Header/footer heights are size-independent, so the current
    // measured values apply to the new dimensions too.
    const maxH = maxHMargin(numW);
    const maxV = maxVMargin(numH, pageGeom.headerH, pageGeom.footerH);
    setPageMargins((m) => {
      const clamped = {
        top:    Math.min(m.top,    maxV),
        bottom: Math.min(m.bottom, maxV),
        left:   Math.min(m.left,   maxH),
        right:  Math.min(m.right,  maxH),
      };
      if (clamped.top !== m.top || clamped.bottom !== m.bottom || clamped.left !== m.left || clamped.right !== m.right) {
        editorRef.current?.querySelectorAll('.rv-page-body').forEach(b => {
          b.style.padding = `${clamped.top}px ${clamped.right}px ${clamped.bottom}px ${clamped.left}px`;
        });
        return clamped;
      }
      return m;
    });

    // Wait one frame for the browser to apply the new dimensions, then re-lay
    // out every page — forced, because a bigger page overflows nothing and the
    // content still has to move up to fill it.
    setTimeout(() => paginateAllPages({ force: true }), 50);
  };

  // The page is the book's page, always. The Format panel's toolbar applies
  // the trim size too, but only while that panel is open — without this the
  // editor laid every book out at the CSS default (US Letter) whatever size it
  // is, while the previews and the PDF used the real size.
  useEffect(() => {
    const { width, height } = pageSizePx(trimSize);
    onPageSetupChange({ width: `${width}px`, height: `${height}px` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimSize]);

  // Measure the real rendered page geometry from the DOM and feed it to the
  // ruler AND the zoom system (fit-width needs the real page width even when
  // the ruler is hidden). This is the source of truth — it survives box-sizing
  // (border-box via MUI CssBaseline), paper-size / orientation changes, and
  // header/footer toggles, none of which a hardcoded constant could track.
  useEffect(() => {
    const measure = () => {
      const scrollEl = mainContentRef.current;
      const pages = scrollEl?.querySelectorAll('.rv-page');
      const page = pages?.[0];
      if (!page) return;
      const header = page.querySelector('.rv-page-header');
      const footer = page.querySelector('.rv-page-footer');
      const width   = page.offsetWidth;
      const height  = page.offsetHeight;
      // offsetHeight is 0 when the band is toggled off (display:none) — exactly
      // what we want, since the body then reclaims that space.
      const headerH = header ? header.offsetHeight : 0;
      const footerH = footer ? footer.offsetHeight : 0;
      if (!width || !height) return;
      setPageGeom((g) =>
        g.width === width && g.height === height && g.headerH === headerH && g.footerH === footerH
          ? g
          : { width, height, headerH, footerH }
      );
      // Real page count — the vertical ruler used to hardcode this at 20,
      // silently truncating any longer document. Reusing this same
      // MutationObserver/ResizeObserver pass keeps it free to track.
      setPageCount((c) => (c === pages.length ? c : pages.length));
    };

    measurePageGeomRef.current = measure;
    // Measure now, and again next frame once the browser has settled layout
    // (e.g. right after a paper-size change writes new CSS vars).
    measure();
    const raf = requestAnimationFrame(measure);

    const scrollEl = mainContentRef.current;
    const ro = new ResizeObserver(measure);
    if (scrollEl) ro.observe(scrollEl);
    // Pages are created/replaced asynchronously (doc load, pagination), so
    // re-measure when the editor's page list changes. Watch only the top-level
    // child list (pages are direct children) — a subtree watch would fire on
    // every keystroke and force a reflow per character.
    const mo = new MutationObserver(measure);
    if (editorRef.current) mo.observe(editorRef.current, { childList: true });

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
    };
    // isSpread: below 900px a page is sized to the viewport in single view and
    // to its true width in a spread, so toggling the mode changes the page's
    // real width. Neither observer above catches that — the ResizeObserver
    // watches the scroll container, not the page, and a class change on an
    // ancestor is not a childList mutation — so the measurement has to be
    // re-run explicitly or layoutWidth keeps sizing to the old width.
  }, [pageSize, isSpread]);

  // Natural (unscaled) content height — how tall .editable-content actually is
  // before the zoom transform is applied. ResizeObserver alone is enough: it
  // fires on any border-box size change (typing causing rewrap, images
  // loading, pages added/removed) without a separate MutationObserver, and it
  // reports the pre-transform layout size regardless of the CSS scale applied
  // below — exactly the "logical" height the zoom sizer needs to scale from.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const measure = () => {
      const height = el.offsetHeight;
      if (!height) return;
      setContentHeight((h) => (h === height ? h : height));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Zoom resolution:
  //  - 'fit-width': largest scale capped at 100% that fits the page width, so
  //    no paper size ever requires horizontal scrolling at the default zoom.
  //  - 'fit-page': largest scale (uncapped, unlike fit-width) that fits BOTH
  //    the page's width and height — for small pages on a big screen this can
  //    zoom in past 100%, matching Docs' "fit page" behavior.
  //  - a number (Phase 2 zoom control): explicit user zoom, passes straight
  //    through clampZoom.
  useEffect(() => {
    if (zoomMode !== 'fit-width' && zoomMode !== 'fit-page') {
      setZoom(clampZoom(zoomMode));
      return;
    }
    const scrollEl = mainContentRef.current;
    if (!scrollEl || !pageGeom.width || !pageGeom.height) return;

    const RULER_WIDTH = 24; // must match EditorRuler.jsx RULER_SIZE
    // .editor-main-content's own top+bottom padding (24 + 56) — see EditorPage.css
    const CONTENT_VPAD = 80;
    const compute = () => {
      const rulerSpace = rulersVisible ? RULER_WIDTH : 0;
      const availW = scrollEl.clientWidth - rulerSpace;
      if (availW <= 0) return;
      // layoutWidth, not pageGeom.width: in spread view the thing that has to
      // fit is the pair, otherwise fitting "the page" leaves half the spread
      // off-screen.
      if (zoomMode === 'fit-width') {
        // Capped at 1 — fit-width shrinks oversized pages but never magnifies
        // a page that already fits.
        setZoom(Math.max(MIN_ZOOM, Math.min(1, availW / layoutWidth)));
      } else {
        const availH = scrollEl.clientHeight - CONTENT_VPAD;
        if (availH <= 0) return;
        setZoom(clampZoom(Math.min(availW / layoutWidth, availH / pageGeom.height)));
      }
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, [zoomMode, layoutWidth, pageGeom.height, rulersVisible]);

// --- Insert openers (save caret) ---
// Utility: get .rv-page-body at caret
const getActivePageBody = (range) => {
  if (!range) return null;
  let node = range.startContainer;
  if (node.nodeType === 3) node = node.parentElement;
  return node?.closest('.rv-page-body');
};

const openInsertImage = () => {
  captureSelection();      // ✅ ensures savedRange is fresh before the modal steals focus
  setShowImage(true);
};

// --- Caret marker helpers ---
const CARET_MARK_ID = 'rv-caret-marker';

const placeCaretMarker = () => {
  try {
    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0) { dbg('No window selection at modal open'); return false; }

    const r = sel.getRangeAt(0);
    if (!editorRef.current?.contains(r.commonAncestorContainer)) { dbg('Selection is not inside editor'); return false; }

    // put a tiny span marker at the caret
    const marker = document.createElement('span');
    marker.id = CARET_MARK_ID;
    marker.style.display = 'inline-block';
    marker.style.width = '0px';
    marker.style.height = '0px';
    marker.style.lineHeight = '0';
    marker.style.overflow = 'hidden';
    marker.style.pointerEvents = 'none';

    const cloned = r.cloneRange();
    cloned.collapse(true);
    cloned.insertNode(marker);

    // keep a cloned range as a secondary fallback
    setSavedRange(r.cloneRange());
    dbg('Caret marker placed');
    return true;
  } catch (e) {
    dbg('placeCaretMarker error', e);
    return false;
  }
};


const consumeCaretMarker = () => {
  const marker = editorRef.current?.querySelector?.(`#${CARET_MARK_ID}`);
  if (!marker) return null;

  // Build a range right before the marker, then remove it.
  const doc = marker.ownerDocument;
  const range = doc.createRange();
  range.setStartBefore(marker);
  range.collapse(true);
  marker.remove();
  return range;
};

const openInsertTable = () => {
  const ok = placeCaretMarker();
  dbg('openInsertTable → marker placed?', ok);
  setShowTable(true);
};

const openInsertShape = () => { setSavedRange(getSelectionRange()); setShowShape(true); };
const openInsertDivider = () => { 
  setSavedRange(getSelectionRange()); 
  setShowDivider(true); 
};
// Insert citation / bibliography HTML from the Citation tool at the saved caret.
const handleInsertCitation = (html) => {
  const range = savedRange || getSelectionRange();
  const pageBody = getActivePageBody(range);
  if (!pageBody) {
    alert('Please place the cursor inside a page to insert a citation.');
    return;
  }
  pageBody.focus();
  restoreSelection(range);
  insertHTMLAtCursor(html);
  triggerChange();
  paginateAllPages();
};

// Insert the bibliography, OR replace an existing one in place so there's only
// ever a single works-cited list (matches Word/Docs). Bibliography nodes are
// tagged data-rv-bib="1" by CitationModal.
const handleInsertBibliography = (html) => {
  const editor = editorRef.current;
  if (!editor) return;

  const existing = editor.querySelectorAll('[data-rv-bib]');
  if (existing.length) {
    // Replace in place: drop the fresh nodes where the old list started.
    const first = existing[0];
    const parent = first.parentNode;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const fresh = Array.from(temp.childNodes);
    fresh.forEach((n) => parent.insertBefore(n, first));
    existing.forEach((n) => n.remove());
    triggerChange();
    paginateAllPages();
    return;
  }
  // No existing list — insert at the caret (user typically places it at the end).
  handleInsertCitation(html);
};

// Reformat every in-text citation already in the document to the current style.
// `map` is { [sourceId]: formattedText }.
const handleRestyleCitations = (map) => {
  const editor = editorRef.current;
  if (!editor || !map) return;
  let changed = 0;
  editor.querySelectorAll('.rv-citation[data-citation-id]').forEach((span) => {
    const id = span.getAttribute('data-citation-id');
    const txt = map[id];
    if (txt != null) { span.textContent = txt; changed += 1; }
  });
  if (changed) {
    triggerChange();
    paginateAllPages();
  }
};

const handleInsertDivider = (dividerConfig) => {
  const range = savedRange || getSelectionRange();
  const pageBody = getActivePageBody(range);
  if (!pageBody) {
    alert('Please place the cursor inside a page to insert a divider.');
    return;
  }
  pageBody.focus();
  restoreSelection(range);
  const hrHTML = `<hr class="rv-hr" data-style="${dividerConfig.style}" data-thickness="${dividerConfig.thickness}" data-color="${dividerConfig.color}" style="border-top: ${dividerConfig.thickness}px ${dividerConfig.style} ${dividerConfig.color}; border-bottom: none; border-left: none; border-right: none; margin: 1.5rem 0;" />`;
  insertHTMLAtCursor(hrHTML);
};
// --- helper: upload local image to backend (preferred: multipart) ---
async function uploadEditorImage(fileOrDataUrl, draftId, user, token) {
  try {
    // Case 1: real File via multipart/form-data
    if (fileOrDataUrl instanceof File) {
      const form = new FormData();
      form.append('file', fileOrDataUrl);
      const res = await fetch(`${API_BASE}/api/drafts/${draftId}/images`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }, // DO NOT set Content-Type for multipart
        body: form,
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`upload failed (${res.status} ${res.statusText}) ${body}`);
      }
      const data = await res.json();
      return { url: data.url, s3Key: data.s3Key };
    }

    // Case 2: data URL string
    const res = await fetch(`${API_BASE}/api/drafts/${draftId}/images`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ dataUrl: fileOrDataUrl }),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`upload failed (${res.status} ${res.statusText}) ${body}`);
    }
    const data = await res.json();
    return { url: data.url, s3Key: data.s3Key }; // Return both presigned URL and S3 key
  } catch (e) {
    console.error('Image upload error:', e);
    return null;
  }
}


// Insert image at caret; if local file, upload first to get a stable URL.
const handleInsertImage = async ({ file, url, alt, alignment = 'center', wrapText = false }) => {
  setShowImage(false);

  // 1) Resolve src (upload file if provided)
  let finalSrc = (url || '').trim();
  let s3Key = null;
  if (file) {
    if (!id) {
      alert('Cannot upload: this draft has no ID yet. Try saving the document first.');
      return;
    }
    const result = await uploadEditorImage(file, id, user, accessToken);
    if (!result || !result.url) {
      // uploadEditorImage already logged the underlying error to the console.
      alert('Image upload failed. Please check your connection / sign-in and try again (see console for details).');
      return;
    }
    finalSrc = result.url;
    s3Key = result.s3Key;
  }
  if (!finalSrc) {
    alert('No image to insert — please choose a file or enter an image URL.');
    return;
  }

  // 2) Get the editor and ensure focus
  const editor = editorRef.current;
  if (!editor) return;
  
  // Check for duplicate s3Key in the document
  if (s3Key) {
    const existing = editor.querySelectorAll(`img[data-s3-key="${s3Key}"]`);
    if (existing.length > 0) {
      console.warn('[INSERT IMAGE] Image with this s3Key already exists in document, skipping insert');
      return;
    }
  }
  
  editor.focus();

  // 3) Restore the saved range or get current selection
  let range = savedRange || getSelectionRange?.();
  if (!range) {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      range = sel.getRangeAt(0);
    }
  }

  // If still no range, find the first page body and insert at the start
  if (!range) {
    const firstBody = editor.querySelector('.rv-page-body');
    if (!firstBody) return;
    
    const sel = window.getSelection();
    range = document.createRange();
    const firstP = firstBody.querySelector('p:not(.rv-end-spacer)') || firstBody.firstChild;
    if (firstP) {
      range.setStart(firstP, 0);
      range.setEnd(firstP, 0);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  // 4) Measure natural size
  const natural = await new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    img.onerror = () => resolve({ w: 16, h: 9 });
    img.src = finalSrc;
  });

  // Calculate reasonable display size (max 300px for wrapped, 600px for centered)
  const maxWidth = (wrapText && (alignment === 'left' || alignment === 'right')) ? 300 : 600;
  const ratio = natural.w / Math.max(1, natural.h);
  let displayWidth = Math.min(natural.w, maxWidth);
  let displayHeight = displayWidth / ratio;

  // 5) Create the image element with inline styles and resize handles
  const imgWrapper = document.createElement('span');
  imgWrapper.contentEditable = 'false';
  imgWrapper.style.display = 'inline-block';
  imgWrapper.style.width = `${displayWidth}px`;
  imgWrapper.style.height = `${displayHeight}px`;
  imgWrapper.className = 'rv-resizable-image';
  imgWrapper.setAttribute('data-width', displayWidth);
  imgWrapper.setAttribute('data-height', displayHeight);
  
  // Apply wrapping (float) if enabled and aligned left/right
  if (wrapText && (alignment === 'left' || alignment === 'right')) {
    imgWrapper.style.float = alignment;
    imgWrapper.style.margin = alignment === 'left' ? '0 16px 12px 0' : '0 0 12px 16px';
    imgWrapper.style.shapeOutside = `margin-box`; // Better text wrapping
  } else {
    imgWrapper.style.margin = '12px auto';
    if (alignment === 'center') {
      imgWrapper.style.display = 'block';
    }
  }
  
  const imgEl = document.createElement('img');
  imgEl.src = finalSrc;
  imgEl.alt = alt || '';
  imgEl.setAttribute('data-alignment', alignment);
  imgEl.setAttribute('data-wrap-text', wrapText);
  if (s3Key) {
    imgEl.setAttribute('data-s3-key', s3Key);
  }
  
  imgWrapper.appendChild(imgEl);

  // 6) Insert the image at the caret position
  try {
    // If we're inside a text node or inline element, we need to break out to block level
    let container = range.startContainer;
    if (container.nodeType === 3) {
      container = container.parentElement;
    }

    // Find the current page body
    let pageBody = container;
    while (pageBody && !pageBody.classList?.contains('rv-page-body')) {
      pageBody = pageBody.parentElement;
    }

    if (!pageBody) {
      pageBody = editor.querySelector('.rv-page-body');
    }

    // Find the paragraph or block element
    let blockEl = container;
    while (blockEl && blockEl !== editor && !['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(blockEl.tagName)) {
      blockEl = blockEl.parentElement;
    }

    if (wrapText && (alignment === 'left' || alignment === 'right')) {
      // For wrapped images, insert at the caret position but as a direct child of page body
      // This allows text after it to wrap around
      if (blockEl && blockEl !== editor && pageBody) {
        // Insert the floated image just before the current paragraph
        pageBody.insertBefore(imgWrapper, blockEl);
        
        // Keep caret in the current paragraph
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        // Fallback: insert at beginning of page body
        const firstChild = pageBody?.firstChild;
        pageBody?.insertBefore(imgWrapper, firstChild);
        
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } else {
      // For centered or non-wrapped images, insert as block after current paragraph
      if (blockEl && blockEl !== editor) {
        const wrapper = document.createElement('p');
        wrapper.style.textAlign = alignment;
        wrapper.appendChild(imgWrapper);
        blockEl.parentNode.insertBefore(wrapper, blockEl.nextSibling);

        // Move caret after the image
        const newP = document.createElement('p');
        newP.innerHTML = '<br/>';
        wrapper.parentNode.insertBefore(newP, wrapper.nextSibling);
        
        const sel = window.getSelection();
        const newRange = document.createRange();
        newRange.setStart(newP, 0);
        newRange.setEnd(newP, 0);
        sel.removeAllRanges();
        sel.addRange(newRange);
      } else {
        // Fallback: just insert at range
        const wrapper = document.createElement('p');
        wrapper.style.textAlign = alignment;
        wrapper.appendChild(imgWrapper);
        range.deleteContents();
        range.insertNode(wrapper);
        
        // Move caret after the image
        range.setStartAfter(wrapper);
        range.setEndAfter(wrapper);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }

    // Inserted up to 600px wide; on a small book that is wider — and taller —
    // than the page. A fresh image fits the page both ways.
    fitNewImageToPage(imgWrapper, imgWrapper.closest('.rv-page-body'));

    // Trigger text change to save
    handleTextChange();
  } catch (e) {
    console.error('Failed to insert image:', e);
  }
};

// Ensure we have at least one .rv-page with a .rv-page-body.
// Returns the .rv-page-body element (existing or newly created).
const ensurePageBody = () => {
  const editor = editorRef.current;
  if (!editor) return null;

  // If it already exists, use it
  const existing = editor.querySelector('.rv-page-body');
  if (existing) return existing;

  // Otherwise create a fresh page structure
  const doc = editor.ownerDocument || document;

  const page = doc.createElement('section');
page.className = 'rv-page';
page.setAttribute('data-type', 'page');

const body = doc.createElement('div');
body.className = 'rv-page-body';
body.setAttribute('contenteditable', 'true');
body.innerHTML = '<p><br/></p><p class="rv-end-spacer" contenteditable="false"><br/></p>';

  page.appendChild(body);
  editor.appendChild(page);

  return body;
};



const handleInsertTable = ({ rows, cols, hasHeader }) => {
  dbg('handleInsertTable called', { rows, cols, hasHeader });

  // 0) Coerce & clamp — always build at least 1×1
  const R = Math.max(1, Math.min(20, Number(rows) || 0));
  const C = Math.max(1, Math.min(12, Number(cols) || 0));

  const editorEl = editorRef.current;
  if (!editorEl) { dbg('No editorEl'); return; }

  // 1) Best anchor = marker; then savedRange; then fallback
  let range = consumeCaretMarker();
  if (range) {
    dbg('Using range from MARKER');
  } else {
    const liveOrSaved = savedRange || (typeof getSelectionRange === 'function' ? getSelectionRange() : null);
    if (liveOrSaved && editorEl.contains(liveOrSaved.commonAncestorContainer)) {
      range = liveOrSaved.cloneRange();
      dbg('Using range from savedRange/live selection');
    }
  }
  if (!range) {
    const firstBody = editorEl.querySelector('.rv-page-body');
    if (firstBody) {
      const r = firstBody.ownerDocument.createRange();
      r.selectNodeContents(firstBody);
      r.collapse(true);
      range = r;
      dbg('Using FALLBACK: start of first .rv-page-body');
    }
  }
  if (!range) { dbg('No valid range → abort'); return; }

  // (Optional) Visual pin where we think the caret is
  try {
    const rect = range.getBoundingClientRect?.();
    if (rect?.width !== undefined) {
      const pin = document.createElement('div');
      pin.style.position = 'fixed';
      pin.style.left = `${rect.left - 4}px`;
      pin.style.top = `${rect.top - 4}px`;
      pin.style.width = '8px';
      pin.style.height = '8px';
      pin.style.borderRadius = '50%';
      pin.style.background = 'red';
      pin.style.zIndex = '999999';
      document.body.appendChild(pin);
      setTimeout(() => pin.remove(), 1500);
      dbg('Pinned caret rect', rect);
    }
  } catch (e) {
    dbg('Pin rect error', e);
  }

  // Resolve the target page body
  let pageBody =
    ((range.startContainer.nodeType === 3
      ? range.startContainer.parentElement
      : range.startContainer) || editorRef.current)
        ?.closest('.rv-page-body') ||
    editorRef.current?.querySelector('.rv-page-body');

  if (!pageBody) {
    dbg('No .rv-page-body resolved → creating one via ensurePageBody()');
    pageBody = ensurePageBody();
  }
  if (!pageBody) { dbg('Still no pageBody → abort'); return; }
  dbg('Resolved pageBody OK');

  // 2) Build the table in the same document as the page body
  const doc = pageBody.ownerDocument;

  const table = doc.createElement('table');
  table.className = 'rv-table';
  table.setAttribute('contenteditable', 'false');
  table.tabIndex = 0;

  if (hasHeader) {
    const thead = doc.createElement('thead');
    const tr = doc.createElement('tr');
    for (let c = 0; c < C; c += 1) {
      const th = doc.createElement('th');
      th.setAttribute('scope', 'col');
      const cellHost = doc.createElement('div');
      cellHost.className = 'rv-cell';
      cellHost.setAttribute('contenteditable', 'true');
      cellHost.innerHTML = '<p><br/></p>';
      th.appendChild(cellHost);
      tr.appendChild(th);
    }
    thead.appendChild(tr);
    table.appendChild(thead);
  }

  const tbody = doc.createElement('tbody');
  for (let r = 0; r < R; r += 1) {
    const tr = doc.createElement('tr');
    for (let c = 0; c < C; c += 1) {
      const td = doc.createElement('td');
      const cellHost = doc.createElement('div');
      cellHost.className = 'rv-cell';
      cellHost.setAttribute('contenteditable', 'true');
      cellHost.innerHTML = '<p><br/></p>';
      td.appendChild(cellHost);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);

  // 3) Insert at the precise caret (marker/lived-in range)
  const wrap = doc.createElement('div');
  wrap.className = 'rv-table-wrap';
  wrap.setAttribute('contenteditable', 'false');
  // (optional) wrap.style.width = '80%'; // set initial width here if not using CSS default
  wrap.appendChild(table);

  // 3) Insert the WRAPPER instead of the table
  const safeRange = range.cloneRange();
  pageBody.focus();

  let inserted = false;
  try {
    safeRange.deleteContents();
    safeRange.insertNode(wrap);
    inserted = true;
    dbg('Inserted table WRAP via safeRange.insertNode');
  } catch (e) {
    dbg('insertNode failed, fallback append', e);
    try {
      pageBody.appendChild(wrap);
      inserted = true;
      dbg('Inserted table WRAP via pageBody.appendChild');
    } catch (e2) {
      dbg('appendChild failed', e2);
    }
  }
  if (!inserted) { dbg('Insertion failed'); return; }

  // 4) Spacer paragraph so caret can move below the table later
  const spacer = doc.createElement('p');
  spacer.innerHTML = '<br/>';
  wrap.insertAdjacentElement('afterend', spacer);

  // 5) Place caret into the first editable cell
  const firstEditable = table.querySelector('.rv-cell');
  if (firstEditable) {
    const caret = doc.createRange();
    caret.selectNodeContents(firstEditable);
    caret.collapse(true);
    const sel = doc.defaultView.getSelection();
    sel.removeAllRanges();
    sel.addRange(caret);
    firstEditable.focus();
    dbg('Caret moved into first .rv-cell');
  } else {
    dbg('No .rv-cell found to move caret');
  }

  // 6) Persist content (DOM insert won't emit input)
  try { handleTextChange(); } catch (e) { dbg('handleTextChange error', e); }

  // 7) Close the modal after we’re safely done
  setTimeout(() => { setShowTable(false); dbg('Closed modal'); }, 0);

   // Sanity: count tables after a tick
    setTimeout(() => {
        const count = editorRef.current?.querySelectorAll?.('table.rv-table')?.length ?? 0;
        dbg('Tables in editor now:', count);
      }, 0);

    // DISABLED: Don't auto-paginate - let user manage pages manually
    // try {
    //   const bodyForPaging =
    //     wrap.closest('.rv-page')?.querySelector('.rv-page-body') ||
    //     editorRef.current?.querySelector('.rv-page-body');
    //   if (isBodyOverflowing(bodyForPaging)) {
    //     paginateIfOverflow(bodyForPaging);
    //   }
    // } catch {}
  };

// ============================================================
// Table Row/Column Manipulation
// ============================================================

// The image menu's one switch. Re-flows straight away rather than through
// paginateAllPages: turning it off leaves the page under-full rather than
// overflowing, which the "work needed?" gate would not act on.
const handleToggleImageBleed = (on) => {
  const span = imageMenu?.span;
  if (!span || !span.isConnected || (on && bleedBlocker(span))) return;
  if (on) span.setAttribute(BLEED_ATTR, 'true');
  else span.removeAttribute(BLEED_ATTR);
  reflowDocument();
  try { applyPageNumbers(); } catch {}
  try { handleTextChange?.(); } catch {}
  setImageMenu((m) => (m ? { ...m } : m));
};

// Dragging an image (see utils/imageDrag.js). The image is lifted out of the
// manuscript when the drag starts and the pages are laid out without it, so
// the drop line shows where it will land once the text has closed up behind
// it. Measured with the image still in place, a drop at the foot of a page
// landed an image-height higher, too high to be cut across the break.
//
// Lifting saves nothing. The drop is one undo step from the page as it was
// before the lift, the same as a chapter move; a cancel puts that page back.
const liftDraggedImage = (span) => {
  const editor = editorRef.current;
  if (!editor || !span.isConnected) return null;
  const snapshot = editor.innerHTML;
  // A running image is put back together; the reflow cuts it again wherever it lands.
  const pieces = bleedBlocksOf(span, editor);
  if (pieces.length > 1) joinBleedPieces(pieces);
  const whole = !bleedBlocker(span);
  const block = liftImage(span);
  const neighbour = (b) => (b && !b.classList.contains('rv-end-spacer') ? b : null);
  // Where it came from, so a drop back there is no move at all.
  const from = whole ? { after: neighbour(block.previousElementSibling), before: neighbour(block.nextElementSibling) } : null;
  block.remove();
  skipAutoPaginationRef.current = true;
  try { reflowDocument(); } finally { skipAutoPaginationRef.current = false; }
  try { applyPageNumbers(); } catch {}
  return { block, snapshot, from };
};

const dropDraggedImage = (lifted, gap) => {
  commitStructuralChange(() => {
    if (gap.before) gap.before.before(lifted.block);
    else gap.after.after(lifted.block);
  }, 'Image moved', lifted.snapshot);
  try { applyPageNumbers(); } catch {}
};

// Saved anyway: an autosave that ran mid-drag would have stored the page
// without the image.
const cancelImageDrag = (lifted) => {
  const editor = editorRef.current;
  if (!editor) return;
  skipAutoPaginationRef.current = true;
  try {
    editor.innerHTML = lifted.snapshot;
    reflowDocument();
  } finally {
    skipAutoPaginationRef.current = false;
  }
  try { applyPageNumbers(); } catch {}
  setContent(getCleanHTML(editor));
  triggerChange();
};

// The image listeners are set up once, so they reach the current render's
// functions through this.
const imageDragRef = useRef(null);
imageDragRef.current = { lift: liftDraggedImage, drop: dropDraggedImage, cancel: cancelImageDrag };

const handleTableAddRow = () => {
  if (!tableContextMenu?.table) return;
  
  const table = tableContextMenu.table;
  const cell = tableContextMenu.cell;
  
  // Find which row the cell belongs to
  const row = cell?.closest('tr');
  if (!row) return;
  
  const rowIndex = Array.from(row.parentElement.children).indexOf(row);
  const colCount = row.children.length;
  
  // Create new row
  const newRow = document.createElement('tr');
  for (let i = 0; i < colCount; i++) {
    const td = document.createElement('td');
    const cellHost = document.createElement('div');
    cellHost.className = 'rv-cell';
    cellHost.setAttribute('contenteditable', 'true');
    cellHost.innerHTML = '<p><br/></p>';
    td.appendChild(cellHost);
    newRow.appendChild(td);
  }
  
  // Insert after current row
  row.parentElement.insertBefore(newRow, row.nextSibling);
  
  // Save and close menu
  handleTextChange();
  setTableContextMenu(null);
  
  // Check if table now overflows and needs pagination
  setTimeout(() => {
    const pageBody = table.closest('.rv-page-body');
    if (pageBody && isBodyOverflowing(pageBody)) {
      paginateAllPages();
    }
  }, 50);
};

const handleTableDeleteRow = () => {
  if (!tableContextMenu?.table) return;
  
  const table = tableContextMenu.table;
  const cell = tableContextMenu.cell;
  
  // Find which row the cell belongs to
  const row = cell?.closest('tr');
  if (!row) return;
  
  const tbody = row.closest('tbody');
  if (tbody && tbody.children.length <= 1) {
    // If this is the last row, remove the entire table wrapper
    const tableWrap = tbody.closest('.rv-table-wrap');
    if (tableWrap) {
      tableWrap.remove();
    } else {
      row.remove();
    }
  } else {
    // Delete row as usual
    row.remove();
  }
  // Save and close menu
  handleTextChange();
  setTableContextMenu(null);
};

const handleTableAddColumn = () => {
  if (!tableContextMenu?.table) return;
  
  const table = tableContextMenu.table;
  const cell = tableContextMenu.cell;
  
  // Find which column the cell belongs to
  const cellElement = cell?.closest('td, th');
  if (!cellElement) return;
  
  const row = cellElement.closest('tr');
  const colIndex = Array.from(row.children).indexOf(cellElement);
  
  // Add cell to each row (thead and tbody)
  const allRows = table.querySelectorAll('tr');
  allRows.forEach((r, idx) => {
    const isHeader = r.closest('thead') !== null;
    const newCell = document.createElement(isHeader ? 'th' : 'td');
    
    if (isHeader) {
      newCell.setAttribute('scope', 'col');
    }
    
    const cellHost = document.createElement('div');
    cellHost.className = 'rv-cell';
    cellHost.setAttribute('contenteditable', 'true');
    cellHost.innerHTML = '<p><br/></p>';
    newCell.appendChild(cellHost);
    
    // Insert after current column
    const targetCell = r.children[colIndex];
    if (targetCell && targetCell.nextSibling) {
      r.insertBefore(newCell, targetCell.nextSibling);
    } else {
      r.appendChild(newCell);
    }
  });
  
  // Save and close menu
  handleTextChange();
  setTableContextMenu(null);
};

const handleTableDeleteColumn = () => {
  if (!tableContextMenu?.table) return;
  
  const table = tableContextMenu.table;
  const cell = tableContextMenu.cell;
  
  // Find which column the cell belongs to
  const cellElement = cell?.closest('td, th');
  if (!cellElement) return;
  
  const row = cellElement.closest('tr');
  const colIndex = Array.from(row.children).indexOf(cellElement);
  
  // Don't delete if it's the only column
  if (row.children.length <= 1) {
    alert('Cannot delete the last column');
    setTableContextMenu(null);
    return;
  }
  
  // Remove cell from each row
  const allRows = table.querySelectorAll('tr');
  allRows.forEach(r => {
    if (r.children[colIndex]) {
      r.children[colIndex].remove();
    }
  });
  
  // Save and close menu
  handleTextChange();
  setTableContextMenu(null);
};

const handleTableInsertImage = async (file) => {
  if (!file || !tableContextMenu?.cell) return;

  try {
    // Use the existing uploadEditorImage helper function
    const result = await uploadEditorImage(file, id, user, accessToken);
    if (!result) {
      throw new Error('Upload failed');
    }

    const imageUrl = result.url;
    const s3Key = result.s3Key;

    // Insert image into the current cell
    const cell = tableContextMenu.cell;
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = file.name;
    img.className = 'rv-table-image';
    if (s3Key) {
      img.setAttribute('data-s3-key', s3Key);
    }
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.display = 'block';
    img.style.margin = '4px auto';
    img.style.cursor = 'pointer';
    
    // Add click handler to enlarge
    img.addEventListener('click', (e) => {
      e.stopPropagation();
      setEnlargedImage({ src: imageUrl, alt: file.name });
    });

    // Clear cell and insert image
    cell.innerHTML = '';
    cell.appendChild(img);

    // Save and close menu
    handleTextChange();
    setTableContextMenu(null);
  } catch (error) {
    console.error('Failed to insert image into table cell:', error);
    alert('Failed to upload image. Please try again.');
  }
};

const handleInsertShape = (shapeConfig) => {
  setShowShape(false);
  const range = savedRange || getSelectionRange();
  const pageBody = getActivePageBody(range);
  if (!pageBody) {
    alert('Please place the cursor inside a page to insert a shape.');
    return;
  }
  pageBody.focus();
  
  const { type, fillColor, borderColor, borderWidth, width, height } = shapeConfig;
  
  // Generate unique ID for this shape
  const shapeId = `shape_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Generate SVG based on shape type
  const generateShapeSVG = () => {
    const stroke = borderColor === 'transparent' ? 'none' : borderColor;
    const fill = fillColor === 'transparent' ? 'none' : fillColor;
    const sw = borderWidth;
    const pad = sw; // padding for stroke
    
    switch (type) {
      case 'rectangle':
        return `<rect x="${pad}" y="${pad}" width="${width - pad*2}" height="${height - pad*2}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'rounded-rect':
        return `<rect x="${pad}" y="${pad}" width="${width - pad*2}" height="${height - pad*2}" rx="12" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'circle':
        const r = Math.min(width, height) / 2 - pad;
        return `<circle cx="${width/2}" cy="${height/2}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'ellipse':
        return `<ellipse cx="${width/2}" cy="${height/2}" rx="${width/2 - pad}" ry="${height/2 - pad}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'triangle':
        return `<polygon points="${width/2},${pad} ${width - pad},${height - pad} ${pad},${height - pad}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'diamond':
        return `<polygon points="${width/2},${pad} ${width - pad},${height/2} ${width/2},${height - pad} ${pad},${height/2}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'pentagon':
        const pPts = [];
        for (let i = 0; i < 5; i++) {
          const angle = (i * 72 - 90) * Math.PI / 180;
          const px = width/2 + (width/2 - pad) * Math.cos(angle);
          const py = height/2 + (height/2 - pad) * Math.sin(angle);
          pPts.push(`${px},${py}`);
        }
        return `<polygon points="${pPts.join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'hexagon':
        const hPts = [];
        for (let i = 0; i < 6; i++) {
          const angle = (i * 60 - 90) * Math.PI / 180;
          const hx = width/2 + (width/2 - pad) * Math.cos(angle);
          const hy = height/2 + (height/2 - pad) * Math.sin(angle);
          hPts.push(`${hx},${hy}`);
        }
        return `<polygon points="${hPts.join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'star':
        const starPts = [];
        for (let i = 0; i < 10; i++) {
          const angle = (i * 36 - 90) * Math.PI / 180;
          const rad = i % 2 === 0 ? (Math.min(width, height)/2 - pad) : (Math.min(width, height)/4);
          const sx = width/2 + rad * Math.cos(angle);
          const sy = height/2 + rad * Math.sin(angle);
          starPts.push(`${sx},${sy}`);
        }
        return `<polygon points="${starPts.join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'arrow-right':
        const arH = height * 0.3; // arrow head portion
        return `<polygon points="${pad},${height*0.3} ${width*0.6},${height*0.3} ${width*0.6},${pad} ${width - pad},${height/2} ${width*0.6},${height - pad} ${width*0.6},${height*0.7} ${pad},${height*0.7}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'arrow-left':
        return `<polygon points="${width - pad},${height*0.3} ${width*0.4},${height*0.3} ${width*0.4},${pad} ${pad},${height/2} ${width*0.4},${height - pad} ${width*0.4},${height*0.7} ${width - pad},${height*0.7}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'callout':
        return `<path d="M${pad},${pad} L${width - pad},${pad} L${width - pad},${height*0.7} L${width*0.35},${height*0.7} L${width*0.2},${height - pad} L${width*0.28},${height*0.7} L${pad},${height*0.7} Z" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      case 'line-horizontal':
        return `<line x1="${sw}" y1="${height/2}" x2="${width - sw}" y2="${height/2}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      case 'line-vertical':
        return `<line x1="${width/2}" y1="${sw}" x2="${width/2}" y2="${height - sw}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      case 'line-diagonal-down':
        return `<line x1="${sw}" y1="${sw}" x2="${width - sw}" y2="${height - sw}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      case 'line-diagonal-up':
        return `<line x1="${sw}" y1="${height - sw}" x2="${width - sw}" y2="${sw}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      default:
        return `<rect x="${pad}" y="${pad}" width="${width - pad*2}" height="${height - pad*2}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
    }
  };
  
  const svgContent = generateShapeSVG();
  
  const shapeHTML = `
    <div class="rv-shape" 
         data-shape-id="${shapeId}"
         data-shape-type="${type}" 
         data-fill="${fillColor}" 
         data-border="${borderColor}" 
         data-border-width="${borderWidth}"
         contenteditable="false" 
         tabindex="0"
         style="display: inline-block; position: relative; cursor: move; user-select: none; margin: 8px; background: transparent;">
      <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
        ${svgContent}
      </svg>
      <div class="rv-shape-resize-handle" style="position: absolute; right: -4px; bottom: -4px; width: 10px; height: 10px; background: #5A52A3; border: 1px solid white; cursor: se-resize; border-radius: 2px;"></div>
    </div>
  `;
  
  insertHTMLAtCursor(shapeHTML);
  handleTextChange();
};

// Insert Text Box at caret (only inside a .rv-page-body)
const openTextBox = () => {
  setSavedRange(getSelectionRange());
  setShowTextBox(true);
};

const handleInsertTextBox = (textBoxConfig) => {
  const range = savedRange || getSelectionRange();
  const pageBody = getActivePageBody(range);
  if (!pageBody) {
    alert('Please place the cursor inside a page to insert a text box.');
    return;
  }
  pageBody.focus();
  restoreSelection(range);
  
  const { styleProps, backgroundColor, width, padding, placeholder } = textBoxConfig;
  
  // Insert as inline-block so it appears at the caret position
  const textBoxHTML = `<div class="rv-textbox" contenteditable="false" data-style="${textBoxConfig.style}" data-width="${width}" data-padding="${padding}" tabindex="0" style="display: inline-block; position: relative; width: ${width}px; min-height: 80px; padding: 0; border: ${styleProps.border}; background: ${backgroundColor}; box-shadow: ${styleProps.boxShadow || 'none'}; border-radius: 4px; margin: 0.5rem; vertical-align: top;"><div class="rv-textbox-content" contenteditable="true" style="padding: ${padding}px; outline: none; cursor: text; min-height: 80px;" placeholder="${placeholder}">${placeholder}</div></div>`;
  
  insertHTMLAtCursor(textBoxHTML);
  handleTextChange();
};
/* Insert Page: open modal for Before / After / End */
const openInsertPage = () => {
  setSavedRange(getSelectionRange());
  setShowInsertPage(true);
};

/* Page Break: splits current page at the cursor, moving tail content to a new page */
// A scene break is one block the theme draws, never text, so a new theme
// restyles every break in the book. A paragraph follows it to type into.
const handleInsertSceneBreak = () => {
  const range = savedRange || getSelectionRange();
  if (!range || !editorRef.current?.contains(range.startContainer)) {
    setThemeToast('Click into the text first, then insert a scene break.');
    return;
  }
  commitStructuralChange(() => {
    const brk = makeSceneBreak(document);
    insertBlocksAtBodyLevel([brk], range);
    const next = brk.nextElementSibling;
    if (!next || next.classList.contains('rv-end-spacer')) {
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      brk.after(p);
    }
  }, 'Scene break inserted');
};

const handleInsertPageBreak = () => {
  const range = savedRange || getSelectionRange();
  if (!range) {
    alert('Please click inside a page first, then insert a page break.');
    return;
  }

  let host = range.startContainer;
  if (host.nodeType === 3) host = host.parentElement;
  const body = host?.closest?.('.rv-page-body');
  if (!body) {
    alert('Please click inside a page first, then insert a page break.');
    return;
  }

  const page = body.closest('.rv-page');
  if (!page) return;

  const doc = body.ownerDocument;

  // Find the direct child of body that contains the cursor
  let anchorEl = host;
  while (anchorEl && anchorEl.parentElement !== body) {
    anchorEl = anchorEl.parentElement;
  }

  // Create a new page immediately after the current one
  const newPage = createEmptyPageEl(doc);
  page.insertAdjacentElement('afterend', newPage);
  const newBody = newPage.querySelector('.rv-page-body');
  if (!newBody) return;

  const appendToNewBody = (block) => {
    const spacer = Array.from(newBody.children).find(c => c.classList?.contains('rv-end-spacer'));
    if (spacer) newBody.insertBefore(block, spacer);
    else newBody.appendChild(block);
  };

  if (anchorEl && !anchorEl.classList?.contains('rv-end-spacer')) {
    // Split the anchor block at the cursor: extract everything after the caret
    const tailRange = doc.createRange();
    tailRange.setStart(range.startContainer, range.startOffset);
    const lastChild = anchorEl.lastChild;
    if (lastChild) tailRange.setEndAfter(lastChild);
    else tailRange.setEnd(anchorEl, anchorEl.childNodes.length);

    const tailFrag = tailRange.extractContents();
    const tailBlock = doc.createElement(anchorEl.tagName || 'P');
    for (const a of anchorEl.attributes) tailBlock.setAttribute(a.name, a.value);
    tailBlock.appendChild(tailFrag);

    const tailHasContent =
      tailBlock.textContent?.trim?.() ||
      tailBlock.querySelector?.('img, table, .rv-shape, .rv-textbox');
    if (tailHasContent) appendToNewBody(tailBlock);

    // Leave a placeholder in anchorEl if now empty
    if (!anchorEl.textContent?.trim?.() && !anchorEl.querySelector?.('img, table, .rv-shape, .rv-textbox')) {
      anchorEl.innerHTML = '<br/>';
    }

    // Move all subsequent siblings (before the spacer) to the new page
    let sib = anchorEl.nextElementSibling;
    while (sib) {
      const next = sib.nextElementSibling;
      if (!sib.classList?.contains('rv-end-spacer')) appendToNewBody(sib);
      sib = next;
    }
  }

  // Focus start of new page
  try {
    const firstContent = Array.from(newBody.children).find(c => !c.classList?.contains('rv-end-spacer'));
    const focusTarget = firstContent || newBody;
    const sel = window.getSelection();
    const r = doc.createRange();
    r.selectNodeContents(focusTarget);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
    newBody.focus();
  } catch {}

  applyPageNumbers();
  handleTextChange();
};

// Create a full page element (Letter ~ 8.5x11in @ 96dpi ≈ 816x1056)
const newPageHTML = () => `
  <section class="rv-page" data-type="page">
    <div class="rv-page-body" contenteditable="true">
      <p><br/></p>
      <p class="rv-end-spacer" contenteditable="false"><br/></p>
    </div>
  </section>
`;

const ensureAtLeastOnePage = () => {
  const host = editorRef.current;
  if (!host) return;
  if (host.querySelector('.rv-page')) return;

  const doc = host.ownerDocument;
  const page = doc.createElement('section');
  page.className = 'rv-page';
  page.setAttribute('data-type', 'page');

  const body = doc.createElement('div');
  body.className = 'rv-page-body';
  body.setAttribute('contenteditable', 'true');
  body.innerHTML = '<p><br/></p><p class="rv-end-spacer" contenteditable="false"><br/></p>';

  page.appendChild(body);
  host.appendChild(page);

// lay down page numbers for this new page
try { applyPageNumbers?.(); } catch {}

try {
  const sel = doc.getSelection();
  const r = doc.createRange();
  r.selectNodeContents(body);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
  body.focus();
} catch {}
};

// Sanitizes legacy/misaligned page nodes and reinforces spacer integrity
const sanitizePageStructure = (hostEl) => {
  if (!hostEl) return;

  const pages = hostEl.querySelectorAll('.rv-page');
  pages.forEach((page) => {
    // Ensure wrapper is NOT contentEditable
    if (page.getAttribute('contenteditable') === 'true') {
      page.removeAttribute('contenteditable');
    }
    // Ensure body
    let body = page.querySelector('.rv-page-body');
    if (!body) {
      body = document.createElement('div');
      body.className = 'rv-page-body';
      body.setAttribute('contenteditable', 'true');
      page.appendChild(body);
    } else {
      body.setAttribute('contenteditable', 'true');
    }
    // Ensure exactly one end spacer at the tail
    const kids = Array.from(body.children);
    const spacers = kids.filter(k => k.classList?.contains?.('rv-end-spacer'));
    if (spacers.length === 0) {
      const spacer = document.createElement('p');
      spacer.className = 'rv-end-spacer';
      spacer.setAttribute('contenteditable', 'false');
      spacer.innerHTML = '<br/>';
      body.appendChild(spacer);
    } else {
      // collapse multiples to one at the end
      spacers.slice(0, -1).forEach(s => s.remove());
      const last = body.lastElementChild;
      if (!last || !last.classList.contains('rv-end-spacer')) {
        const main = spacers[spacers.length - 1];
        if (main) body.appendChild(main);
      }
    }
  });
};

const handleInsertPage = (position) => {
  setShowInsertPage(false);
  const editor = editorRef.current;
  if (!editor) return;

  const range = savedRange || getSelectionRange();
  if (position === 'end') {
    editor.insertAdjacentHTML('beforeend', newPageHTML());
    return;
  }

  if (!range || !editor.contains(range.startContainer)) {
    alert('Please click inside a page to insert a new page.');
    return;
  }

  // Find the page the caret is currently in
  const anchorEl = (range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement);
  const currentPage = anchorEl?.closest('.rv-page');

  if (currentPage) {
    currentPage.insertAdjacentHTML(position === 'before' ? 'beforebegin' : 'afterend', newPageHTML());
    // Move caret to new page's body
    setTimeout(() => {
      const pages = Array.from(editor.querySelectorAll('.rv-page'));
      const idx = pages.indexOf(currentPage);
      const newPage = position === 'before' ? pages[idx] : pages[idx + 1];
      if (newPage) {
        const body = newPage.querySelector('.rv-page-body');
        if (body) {
          body.focus();
          // Place caret at start
          const sel = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(body);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    }, 0);
  } else {
    // Fallback: insert at caret
    editor.focus();
    restoreSelection(range);
    insertHTMLAtCursor(newPageHTML());
  }

  // persist after inserting a page
  try { handleTextChange(); } catch {}
  applyPageNumbers();
};
// Treat these as non-movable structural nodes during merges
const isStructuralNode = (node) => {
  if (!node || node.nodeType !== 1) return false; // only element nodes
  const el = node;
  if (el.classList.contains('rv-end-spacer')) return true;
  if (el.classList.contains('rv-page-number')) return true;
  if (el.closest?.('.rv-page-footer')) return true;
  if (el.getAttribute?.('data-nomerge') === 'true') return true;
  return false;
};

const isTrulyEmptyP = (node) => {
  if (!node || node.nodeName !== 'P') return false;
  const html = node.innerHTML.replace(/&nbsp;|\s/g, '');
  return html === '' || html === '<br/>' || html === '<br>';
};

// Under a theme the margins are the theme's: the rulers show them, and a drag
// says where to change them instead of moving them.
const themePage = manuscriptTheme ? completeTheme(manuscriptTheme).page : null;
const themeMargins = themePage ? {
  top: themePage.top * 96,
  bottom: themePage.bottom * 96,
  left: themePage.inside * 96,
  right: themePage.outside * 96,
} : null;
const holdThemeMargins = () => setThemeToast('The margins come from the theme. Change them in Customize theme.');

const handleMarginsChange = (newMargins) => {
  setPageMargins(newMargins);
  editorRef.current?.querySelectorAll('.rv-page-body').forEach(b => {
    b.style.padding = `${newMargins.top}px ${newMargins.right}px ${newMargins.bottom}px ${newMargins.left}px`;
  });
};

// Track which paragraph the cursor is in, read its indent values into ruler state
useEffect(() => {
  if (!rulersVisible) return;

  const adoptPara = (para) => {
    currentParaRef.current = para;
    setParaIndents({
      left:      parseFloat(para.style.marginLeft)  || 0,
      firstLine: parseFloat(para.style.textIndent)  || 0,
      right:     parseFloat(para.style.marginRight) || 0,
    });
  };

  // The indent handles are always visible once the ruler is shown, but
  // handleIndentChange is a no-op until currentParaRef points at a real
  // paragraph — normally set by the selectionchange listener below. If the
  // ruler is toggled on before the user has ever clicked into the document
  // (e.g. straight from the settings panel), there's no caret yet, so the
  // handles would silently do nothing on drag. Fall back to the first
  // paragraph so there's always a live target, same as Word/Docs always
  // having an implicit cursor position.
  if (!currentParaRef.current || !editorRef.current?.contains(currentParaRef.current)) {
    const firstPara = editorRef.current?.querySelector('p, h1, h2, h3, h4, h5, h6, li, blockquote');
    if (firstPara) adoptPara(firstPara);
  }

  const onSelChange = () => {
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const anchor = sel.getRangeAt(0).startContainer;
    const el = anchor.nodeType === 3 ? anchor.parentElement : anchor;
    const para = el?.closest('p, h1, h2, h3, h4, h5, h6, li, blockquote');
    if (!para || !editorRef.current?.contains(para)) return;
    adoptPara(para);
  };
  document.addEventListener('selectionchange', onSelChange);
  return () => document.removeEventListener('selectionchange', onSelChange);
}, [rulersVisible]);

const handleIndentChange = (partial) => {
  const para = currentParaRef.current;
  if (!para) return;
  if ('left'      in partial) para.style.marginLeft  = partial.left      > 0 ? `${partial.left}px`      : '';
  if ('firstLine' in partial) para.style.textIndent  = partial.firstLine !== 0 ? `${partial.firstLine}px` : '';
  if ('right'     in partial) para.style.marginRight = partial.right     > 0 ? `${partial.right}px`     : '';
  setParaIndents(prev => ({ ...prev, ...partial }));
};

// Delete a specific page. Pass targetPageEl from the hover button; omit to
// fall back to the page containing the caret (used by keyboard/legacy paths).
const handleDeletePage = (targetPageEl = null) => {
  const editor = editorRef.current;
  if (!editor) return;

  skipAutoPaginationRef.current = true;

  const resolveBody = () => {
    if (targetPageEl) return targetPageEl.querySelector('.rv-page-body');
    const fromSel = getCurrentPageBodyFromSelection?.();
    if (fromSel) return fromSel;
    if (lastBodyRef.current && editor.contains(lastBodyRef.current)) return lastBodyRef.current;
    return editor.querySelector('.rv-page-body');
  };
  const body = resolveBody();
  const page = targetPageEl || body?.closest('.rv-page');
  if (!page) {
    skipAutoPaginationRef.current = false;
    return;
  }

  const pages = Array.from(editor.querySelectorAll('.rv-page'));
  if (pages.length <= 1) {
    // Never remove the last page — clear it to a blank page
    body.innerHTML = '<p><br/></p><p class="rv-end-spacer" contenteditable="false"><br/></p>';
    focusBodyStart(body);
    try { handleTextChange(); } catch {}
    return;
  }

  const srcBody = page.querySelector('.rv-page-body');
  
  // Check if this page is empty (only has spacer or empty paragraphs)
  const hasContent = Array.from(srcBody.children).some(el => {
    if (isStructuralNode(el)) return false;
    if (el.classList?.contains('rv-end-spacer')) return false;
    if (isEmptyParagraph(el)) return false;
    return true;
  });

  if (!hasContent) {
    // Page is empty - just remove it, no need to merge anything
    const prevPage = page.previousElementSibling;
    const nextPage = page.nextElementSibling;
    page.remove();
    
    // Focus on the previous page if available, otherwise next
    const targetPage = prevPage?.classList?.contains('rv-page') ? prevPage : nextPage;
    if (targetPage) {
      const targetBody = targetPage.querySelector('.rv-page-body');
      if (targetBody) focusBodyStart(targetBody);
    }
    
    try { handleTextChange(); } catch {}
    applyPageNumbers();
    return;
  }

  // Page has content - merge it into receiver
  // Choose receiver: prefer PREVIOUS page (to consolidate content upward); otherwise next
  let receiver = page.previousElementSibling?.classList?.contains('rv-page')
    ? page.previousElementSibling
    : page.nextElementSibling;
  if (!receiver) receiver = pages[0] === page ? pages[1] : pages[0];

  const dstBody = receiver.querySelector('.rv-page-body');
  if (!srcBody || !dstBody) return;

  // Collect *contentful* children (skip structural nodes and empty <p>)
  const toMove = [];
  Array.from(srcBody.children).forEach((el) => {
    if (isStructuralNode(el)) return;   // ← also skips page numbers, footer, spacer, data-nomerge
    if (isEmptyParagraph(el)) return;
    toMove.push(el);
  });

  // Move nodes in DOM order:
  // - If receiver is the *next* page, insert at the *front* (before its first child).
  // - If receiver is the *previous* page, append at the *end*, but before its spacer.
  const receiverIsNext = receiver === page.nextElementSibling;
  if (receiverIsNext) {
    // Insert before the first NON-structural node; fallback = before the spacer
    const firstNonStructural =
      Array.from(dstBody.childNodes).find(
        (n) => n.nodeType === 1 && !isStructuralNode(n)
      ) || dstBody.querySelector('.rv-end-spacer');
    toMove.forEach((el) => dstBody.insertBefore(el, firstNonStructural));
  } else {
    // Append at end, but before spacer if present
    const last = dstBody.lastElementChild;
    const anchor = last && last.classList?.contains('rv-end-spacer') ? last : null;
    toMove.forEach((el) => dstBody.insertBefore(el, anchor));
  }
  // Remove the page node
  page.remove();

  // Ensure exactly one end spacer at receiver tail
  const tail = dstBody.lastElementChild;
  if (!tail || !tail.classList?.contains('rv-end-spacer')) {
    const spacer = dstBody.ownerDocument.createElement('p');
    spacer.className = 'rv-end-spacer';
    spacer.setAttribute('contenteditable', 'false');
    spacer.innerHTML = '<br/>';
    dstBody.appendChild(spacer);
  }

  // Focus at the merge boundary
  if (receiverIsNext) {
    // Start of the receiver (top of next page)
    focusBodyStart(dstBody);
  } else {
    // End of previous page (before spacer)
    focusBodyEndBeforeSpacer(dstBody);
  }

  // DISABLED: Don't auto-paginate after delete - let user manage pages manually
  // try { if (isBodyOverflowing(dstBody)) paginateIfOverflow(dstBody); } catch {}
  applyPageNumbers();
  
  // Re-enable auto-pagination after a delay
  setTimeout(() => {
    skipAutoPaginationRef.current = false;
  }, 2000);
};

// Inject a hover-activated trash button into every .rv-page, and re-inject
// whenever the pagination engine adds new pages. Buttons are non-editable
// so they never interfere with typing or selection.
useEffect(() => {
  const editor = editorRef.current;
  if (!editor) return;

  const TRASH_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>`;

  const inject = () => {
    editor.querySelectorAll('.rv-page').forEach((page) => {
      if (page.querySelector('.rv-page-delete-btn')) return;
      const btn = document.createElement('button');
      btn.className = 'rv-page-delete-btn';
      btn.setAttribute('contenteditable', 'false');
      btn.setAttribute('aria-label', 'Delete page');
      btn.setAttribute('title', 'Delete page');
      btn.innerHTML = TRASH_SVG;
      page.appendChild(btn);
    });
  };

  inject();

  const observer = new MutationObserver(inject);
  observer.observe(editor, { childList: true });

  const handleClick = (e) => {
    const btn = e.target.closest('.rv-page-delete-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const page = btn.closest('.rv-page');
    if (!page) return;
    const body = page.querySelector('.rv-page-body');
    const hasContent = body && Array.from(body.children).some((el) => {
      if (isStructuralNode(el)) return false;
      if (el.classList?.contains('rv-end-spacer')) return false;
      if (isEmptyParagraph(el)) return false;
      return true;
    });
    if (hasContent) {
      if (!window.confirm('Delete this page and all its content? This cannot be undone.')) return;
    }
    handleDeletePage(page);
  };

  editor.addEventListener('click', handleClick);
  return () => {
    observer.disconnect();
    editor.removeEventListener('click', handleClick);
  };
}, []);

// Caret is at the start of a page's content when it sits at offset 0 of the
// first CONTENT block (only empty/structural nodes may precede it on the page).
// More robust than a raw range compare, which trips on stray whitespace nodes.
const caretBlockAtPageContentStart = () => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
  const body = getCurrentPageBodyFromSelection?.();
  if (!body) return null;

  const range = sel.getRangeAt(0);
  const container = range.startContainer;
  const block = container.nodeType === 3
    ? container.parentElement?.closest('p, h1, h2, h3, h4, h5, h6, li, div:not(.rv-page-body)')
    : container.closest('p, h1, h2, h3, h4, h5, h6, li, div:not(.rv-page-body)');
  if (!block || !body.contains(block)) return null;

  // Nothing but whitespace before the caret WITHIN its own block.
  const before = document.createRange();
  before.selectNodeContents(block);
  before.setEnd(range.startContainer, range.startOffset);
  if (before.toString().replace(/​/g, '').length > 0) return null;

  // Every earlier sibling on this page must be empty/structural (no real text).
  let prev = block.previousElementSibling;
  while (prev) {
    const skippable =
      prev.classList?.contains('rv-end-spacer') ||
      isStructuralNode(prev) ||
      isEmptyParagraph(prev);
    if (!skippable) return null; // real content precedes → normal backspace
    prev = prev.previousElementSibling;
  }
  return { body, block };
};

// AUTO-FILL pull-up. When the caret is at the start of a page's content and a
// previous page exists, remove the filler blank lines that were pushing content
// down, then re-flow the whole document. Reflow re-packs paragraphs to fill each
// page and splits a paragraph at the line boundary when it doesn't fully fit —
// so page 1 fills to capacity and the remainder stays on page 2 (Word/Docs
// behavior). Returns true if it acted (caller should preventDefault).
// Shared by the keydown handler (Windows/desktop) and the beforeinput handler
// (iOS soft keyboards fire `deleteContentBackward`, not a reliable Backspace).
const mergePageUpAtCaret = () => {
  const at = caretBlockAtPageContentStart();
  if (!at) return false;
  const { body, block } = at;

  const page = body.closest('.rv-page');
  const prevPage = page?.previousElementSibling;
  if (!prevPage?.classList?.contains('rv-page')) return false; // already first page
  const prevBody = prevPage.querySelector('.rv-page-body');
  if (!prevBody) return false;

  // Drop empty filler paragraphs before the caret block on THIS page.
  let lead = block.previousElementSibling;
  while (lead) {
    const toRemove = isEmptyParagraph(lead) ? lead : null;
    lead = lead.previousElementSibling;
    if (toRemove) toRemove.remove();
  }

  // Drop trailing empty filler paragraphs at the END of the previous page.
  const prevSpacer = prevBody.querySelector('.rv-end-spacer');
  let tail = prevSpacer ? prevSpacer.previousElementSibling : prevBody.lastElementChild;
  while (tail && isEmptyParagraph(tail)) {
    const toRemove = tail;
    tail = tail.previousElementSibling;
    toRemove.remove();
  }

  // Keep the caret at the start of the block we're pulling up so the user stays
  // in place; reflow saves/restores this position across the re-pack.
  const startRange = document.createRange();
  startRange.setStart(block, 0);
  startRange.collapse(true);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(startRange);

  // Force a real reflow (bypass the "work needed?" gate — we just changed the
  // DOM and want the fill to happen now).
  reflowDocument();
  try { applyPageNumbers(); } catch {}
  try { handleTextChange?.(); } catch {}
  return true;
};

// AUTO-FILL pull-down (the Delete-key twin of mergePageUpAtCaret). Strips the
// blank filler between `currentBody` and the next page, then re-flows the whole
// document so the next page's content rises to fill this one — splitting a
// paragraph at the line boundary when it doesn't fully fit. No whole-block jump.
const pullContentUpToFill = (currentBody) => {
  if (!currentBody) return;

  // Remove trailing empty filler paragraphs at the end of THIS page.
  const spacer = currentBody.querySelector('.rv-end-spacer');
  let tail = spacer ? spacer.previousElementSibling : currentBody.lastElementChild;
  while (tail && isEmptyParagraph(tail)) {
    const toRemove = tail;
    tail = tail.previousElementSibling;
    toRemove.remove();
  }

  // Remove leading empty filler at the START of the next page.
  const page = currentBody.closest('.rv-page');
  const nextBody = page?.nextElementSibling?.classList?.contains('rv-page')
    ? page.nextElementSibling.querySelector('.rv-page-body')
    : null;
  if (nextBody) {
    let lead = nextBody.firstElementChild;
    while (lead && isEmptyParagraph(lead)) {
      const toRemove = lead;
      lead = lead.nextElementSibling;
      toRemove.remove();
    }
  }

  reflowDocument();
  try { applyPageNumbers(); } catch {}
  try { handleTextChange?.(); } catch {}
};

// Remove adjacent widget (textbox/page/shape/image/table/divider) with Backspace/Delete
const removeAdjacentWidget = (dir /* 'back' | 'forward' */) => {
  const sel = window.getSelection?.();
  if (!sel || sel.rangeCount === 0) return false;
  const r = sel.getRangeAt(0);
  if (!r.collapsed) return false;

  let node = r.startContainer;
  if (node.nodeType === 3) node = node.parentElement;
  if (!node) return false;

  // Find sibling in the correct direction
  let sibling = dir === 'back' ? node.previousSibling : node.nextSibling;
  // If at start/end of a block, may need to go up to parent
  if (!sibling && node.parentElement && node.parentElement !== editorRef.current) {
    sibling = dir === 'back' ? node.parentElement.previousSibling : node.parentElement.nextSibling;
  }

  const isWidget = el =>
    el &&
    el.nodeType === 1 &&
    (
      el.matches('.rv-textbox, .rv-shape, .rv-page-break, figure.rv-image, table.rv-table')
      // Do NOT allow deleting .rv-page here!
    );

  if (isWidget(sibling)) {
    // Focus previous/next node after deletion
    const focusNode = dir === 'back' ? sibling.previousSibling : sibling.nextSibling;
    sibling.remove();
    if (focusNode && focusNode.focus) focusNode.focus();
    return true;
  }
  return false;
};

// True when a collapsed caret sits at the very start ('back') or end
// ('forward') of a page-body block whose neighbour that way is an image
// standing as its own block. Used to stop the browser merging that image into
// the paragraph; see onBeforeInputCapture.
const caretAgainstBodyImage = (dir /* 'back' | 'forward' */) => {
  const sel = window.getSelection?.();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return false;
  const r = sel.getRangeAt(0);
  let block = r.startContainer.nodeType === 3 ? r.startContainer.parentElement : r.startContainer;
  while (block?.parentElement && !block.parentElement.classList?.contains('rv-page-body')) {
    block = block.parentElement;
  }
  if (!block?.parentElement) return false;
  const neighbour = dir === 'back' ? block.previousElementSibling : block.nextElementSibling;
  if (!neighbour?.matches?.('.rv-resizable-image')) return false;

  // Nothing between the caret and that edge of the block but line breaks and
  // ordinary whitespace. A paragraph split at a page bottom keeps a trailing
  // space the browser does not draw, and End puts the caret before it. (A
  // non-breaking space is drawn, so it still counts.)
  const edge = document.createRange();
  edge.selectNodeContents(block);
  if (dir === 'back') edge.setEnd(r.startContainer, r.startOffset);
  else edge.setStart(r.startContainer, r.startOffset);
  const between = edge.cloneContents();
  return !between.textContent.replace(/[ \t\n\r\u200B]/g, '') && !between.querySelector('img');
};

// Stop a delete at an image that sits between paragraphs as its own block (a
// wrapped image, or a piece of one running onto the next page). Left to the
// browser, Backspace at the start of the paragraph after it deletes it or
// merges it into the paragraph, leaving a bare <img> the editor no longer
// knows as an image. The image is deleted the way it always is: click it,
// then Delete. On the NATIVE beforeinput: the editor's onBeforeInputCapture is
// React's synthetic event, built from keypress/textInput, and never sees a
// delete. (Delete itself is stopped earlier, in onKeyDownCapture, because
// that handler cancels the key before any beforeinput.)
useEffect(() => {
  const host = editorRef.current;
  if (!host) return undefined;
  const onNativeBeforeInput = (e) => {
    const m = /^delete\w*(Backward|Forward)$/.exec(e.inputType || '');
    if (m && caretAgainstBodyImage(m[1] === 'Backward' ? 'back' : 'forward')) e.preventDefault();
  };
  host.addEventListener('beforeinput', onNativeBeforeInput);
  return () => host.removeEventListener('beforeinput', onNativeBeforeInput);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const handleEditorKeyDown = (e) => {
  // If a whole table wrapper is selected, delete it
  if (selectedTableRef.current && (e.key === 'Delete' || e.key === 'Backspace')) {
    const wrap = selectedTableRef.current;
    const spacer = wrap.nextElementSibling;

    wrap.remove();
    if (spacer && spacer.tagName === 'P' && /^\s*<br\/?>\s*$/.test(spacer.innerHTML || '')) {
      spacer.remove();
    }
    selectedTableRef.current = null;

    try { handleTextChange(); } catch {}
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  // Fallback: allow deleting a widget when the caret is adjacent (your existing behavior)
  if (e.key === 'Backspace') {
    if (removeAdjacentWidget('back')) { e.preventDefault(); }
  } else if (e.key === 'Delete') {
    if (removeAdjacentWidget('forward')) { e.preventDefault(); }
  }
};

  const handleToolsBookCover = () => {
    handleBookCoverClick?.();
  };


  // Download PDF: the book as the server assembles it — title page, contents with
  // page numbers, back matter, at the book's own trim size — the same PDF a
  // reviewer reads. It used to be a capture of the editor's screen made in the
  // browser (html2pdf), which had none of the book's structure and was always
  // Letter size, so the author's download and the reviewer's book disagreed.
  //
  // Saved first, so the download is what is on screen. Takes a few seconds:
  // the server builds it fresh for every download (see `?download=1` in
  // get_draft_pdf). A failure says so rather than falling back to the old
  // capture — two different versions of the same book is the problem this fixes.
  const handleDownloadPdf = async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      await forceSave(true);

      // The PDF route identifies the reader by `uid`, as BookReader's does.
      const uid = user?.id || user?._id;
      const query = `preview=1&download=1${uid ? `&uid=${encodeURIComponent(uid)}` : ''}`;
      const res = await fetch(`${API_BASE}/api/drafts/${id}/pdf?${query}`, {
        credentials: 'omit',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!res.ok) throw new Error(`PDF request failed: ${res.status}`);

      const href = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = href;
      link.download = `${(doc?.title || 'document').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (err) {
      console.error('PDF download failed:', err);
      alert('Your PDF could not be prepared. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Drill-down navigation: opening a panel replaces the icon strip with it, and
  // the panel's own back arrow is what returns to the strip. So a tab click only
  // ever opens — there is no longer a visible tab to click a second time to
  // close, which is what the old toggle relied on.
  const handleSidebarPanelClick = (panel) => setActiveSidebarPanel(panel);
  const closeSidebarPanel = () => setActiveSidebarPanel(null);

  // Add event listeners for resizable images
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    let resizing = null;
    let selectedImage = null;
    // A press on an image's body: { span, x, y } until the pointer moves far
    // enough to make it a drag, then `drag` takes over. Released first, it was
    // a click, and the menu it opened stays.
    let pressed = null;
    let drag = null;
    // The click that ends a drag is not a click on whatever it lands on.
    let swallowClickUntil = 0;

    // The first piece of a running image is the image; any other piece stands
    // in for it. A span that is not a piece is its own first piece.
    const firstPieceOf = (span) => firstBleedPieceOf(span, editor);

    // The element that scrolls the pages: the editor's own area when it
    // overflows, otherwise the window. Which one depends on the window size.
    const scrollerOf = () => {
      for (let n = editor.parentElement; n && n !== document.body; n = n.parentElement) {
        const oy = window.getComputedStyle(n).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n;
      }
      return document.scrollingElement;
    };
    const viewTop = (sc) => (sc === document.scrollingElement ? 0 : sc.getBoundingClientRect().top);
    const viewBottom = (sc) => (sc === document.scrollingElement ? window.innerHeight : sc.getBoundingClientRect().bottom);

    // Every place the lifted image can be dropped, measured once: nothing moves
    // while the drag runs, so only the scroll has to be added back in. A gap
    // between two pages has a spot at the foot of one and the head of the next.
    const dropSpots = (from, sc) => {
      const blocks = [];
      editor.querySelectorAll('.rv-page-body').forEach((body) => {
        Array.from(body.children).forEach((b) => {
          if (b.classList.contains('rv-end-spacer')) return;
          const pos = window.getComputedStyle(b).position;
          if (pos === 'absolute' || pos === 'fixed') return; // a text box, a shape
          blocks.push(b);
        });
      });
      const toContent = (clientY) => clientY - viewTop(sc) + sc.scrollTop;
      const column = (block) => {
        const body = block.closest('.rv-page-body');
        const r = body.getBoundingClientRect();
        const cs = window.getComputedStyle(body);
        const scale = r.width / (body.offsetWidth || r.width);
        const padL = (parseFloat(cs.paddingLeft) || 0) * scale;
        const padR = (parseFloat(cs.paddingRight) || 0) * scale;
        return { body, left: r.left + padL, width: r.width - padL - padR };
      };
      const spots = [];
      dropGaps(blocks, from).forEach((gap) => {
        const a = gap.after?.getBoundingClientRect();
        const b = gap.before?.getBoundingClientRect();
        const colA = gap.after && column(gap.after);
        const colB = gap.before && column(gap.before);
        if (a && b && colA.body === colB.body) {
          spots.push({ gap, y: toContent((a.bottom + b.top) / 2), left: colA.left, width: colA.width });
          return;
        }
        if (a) spots.push({ gap, y: toContent(a.bottom), left: colA.left, width: colA.width });
        if (b) spots.push({ gap, y: toContent(b.top), left: colB.left, width: colB.width });
      });
      return spots;
    };

    const placeLine = () => {
      const { sc, line, ghost } = drag;
      // Beside the pointer, or before it near the right edge (a phone).
      const ghostLeft = drag.pointerX + 12 + ghost.offsetWidth > window.innerWidth - 8
        ? drag.pointerX - 12 - ghost.offsetWidth
        : drag.pointerX + 12;
      ghost.style.left = `${ghostLeft}px`;
      ghost.style.top = `${drag.pointerY + 12}px`;
      drag.spot = nearestSpot(drag.spots, drag.pointerY - viewTop(sc) + sc.scrollTop);
      const { spot } = drag;
      if (!spot || spot.gap.noop) {
        line.style.display = 'none';
        return;
      }
      line.style.display = 'block';
      line.style.left = `${spot.left}px`;
      line.style.width = `${spot.width}px`;
      line.style.top = `${spot.y - sc.scrollTop + viewTop(sc) - 1.5}px`;
    };

    // Each frame: scroll when the pointer is near the top or bottom edge, so an
    // image can be taken to any page, and keep the line under the pointer.
    const EDGE_PX = 56;
    const tick = () => {
      if (!drag) return;
      const { sc } = drag;
      const top = viewTop(sc);
      const bottom = viewBottom(sc);
      const y = drag.pointerY;
      if (y < top + EDGE_PX) sc.scrollTop -= Math.ceil(((top + EDGE_PX - y) / EDGE_PX) * 18);
      else if (y > bottom - EDGE_PX) sc.scrollTop += Math.ceil(((y - bottom + EDGE_PX) / EDGE_PX) * 18);
      placeLine();
      drag.frame = requestAnimationFrame(tick);
    };

    // Lift the image out and show a small copy of it under the pointer.
    const startDrag = (span, pointerX, pointerY) => {
      const img = span.querySelector('img');
      const width = Math.min(span.getBoundingClientRect().width, 160);
      // The selection outline is not part of the page it was lifted from.
      bleedPiecesOf(span, editor).forEach((p) => { p.style.outline = ''; });
      selectedImage = null;
      const lifted = imageDragRef.current?.lift(span);
      if (!lifted) return null;
      const sc = scrollerOf();
      const line = document.createElement('div');
      Object.assign(line.style, {
        position: 'fixed',
        height: '3px',
        borderRadius: `${tokens.radius.base}px`,
        background: tokens.color.goldFlat,
        pointerEvents: 'none',
        zIndex: '10000',
        display: 'none',
      });
      const ghost = document.createElement('img');
      ghost.src = img?.src || '';
      ghost.alt = '';
      Object.assign(ghost.style, {
        position: 'fixed',
        width: `${width}px`,
        opacity: '0.75',
        boxShadow: tokens.shadow.md,
        borderRadius: `${tokens.radius.base}px`,
        pointerEvents: 'none',
        zIndex: '10001',
      });
      document.body.append(line, ghost);
      document.body.classList.add('rv-image-dragging');
      setImageMenu(null);
      drag = { lifted, sc, spots: dropSpots(lifted.from, sc), line, ghost, pointerX, pointerY, spot: null, frame: 0 };
      placeLine();
      drag.frame = requestAnimationFrame(tick);
      return drag;
    };

    // `drop`: put the image where the line is. Otherwise (Escape), or with no
    // line showing, put the page back the way it was.
    const endDrag = (drop) => {
      if (!drag) return;
      const { lifted, line, ghost, spot, frame } = drag;
      drag = null;
      pressed = null;
      cancelAnimationFrame(frame);
      line.remove();
      ghost.remove();
      document.body.classList.remove('rv-image-dragging');
      swallowClickUntil = Date.now() + 300;
      if (drop && spot && !spot.gap.noop) imageDragRef.current?.drop(lifted, spot.gap);
      else imageDragRef.current?.cancel(lifted);
    };

    const handleMouseDown = (e) => {
      let wrapper = e.target.closest('.rv-resizable-image');
      if (!wrapper) {
        // Clicked outside image, deselect
        if (selectedImage) {
          selectedImage.style.outline = '';
          selectedImage = null;
        }
        setImageMenu(null);
        return;
      }

      // Select the image
      if (selectedImage && selectedImage !== wrapper) {
        selectedImage.style.outline = '';
      }
      selectedImage = wrapper;
      wrapper.style.outline = '2px solid #D4AF37';

      // Check if clicking on the resize handle (bottom-right 12px)
      const rect = wrapper.getBoundingClientRect();
      const handleSize = 12;
      const isOnHandle = 
        e.clientX >= rect.right - handleSize &&
        e.clientX <= rect.right &&
        e.clientY >= rect.bottom - handleSize &&
        e.clientY <= rect.bottom;

      if (!isOnHandle) {
        setImageMenu({ x: e.clientX + 10, y: e.clientY + 10, span: firstPieceOf(wrapper) });
        // Held, it may become a drag. Kept from starting a text selection; the
        // caret stays where it was, as it always has for a click on an image.
        if (e.button === 0) {
          e.preventDefault();
          pressed = { span: firstPieceOf(wrapper), x: e.clientX, y: e.clientY };
        }
        return;
      }
      setImageMenu(null);

      e.preventDefault();
      e.stopPropagation();

      // A running image is resized whole: put its pieces back together, then
      // the reflow after mouseup cuts it again at the new size.
      const pieces = wrapper.hasAttribute(OFFSET_ATTR) ? bleedBlocksOf(wrapper, editor) : [];
      if (pieces.length > 1) {
        const head = firstPieceOf(wrapper);
        joinBleedPieces(pieces);
        wrapper.style.outline = '';
        wrapper = head;
        selectedImage = head;
        head.style.outline = `2px solid ${tokens.color.goldFlat}`;
      }

      const img = wrapper.querySelector('img');
      if (!img) return;

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = wrapper.offsetWidth;
      const startHeight = wrapper.offsetHeight;
      const aspectRatio = startWidth / startHeight;

      resizing = { wrapper, img, startX, startY, startWidth, startHeight, aspectRatio };
      document.body.style.cursor = 'nwse-resize';
    };

    const handleMouseMove = (e) => {
      if (pressed && !drag) {
        if (Math.hypot(e.clientX - pressed.x, e.clientY - pressed.y) < DRAG_START_PX) return;
        if (!startDrag(pressed.span, e.clientX, e.clientY)) pressed = null;
      }
      if (drag) {
        e.preventDefault();
        drag.pointerX = e.clientX;
        drag.pointerY = e.clientY;
        return;
      }
      if (!resizing) return;

      e.preventDefault();
      const { wrapper, startX, startWidth, aspectRatio } = resizing;
      
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + deltaX);
      const newHeight = newWidth / aspectRatio;

      wrapper.style.width = `${newWidth}px`;
      wrapper.style.height = `${newHeight}px`;
      wrapper.setAttribute('data-width', newWidth);
      wrapper.setAttribute('data-height', newHeight);
    };

    const handleMouseUp = () => {
      if (drag) {
        endDrag(true);
        return;
      }
      pressed = null;
      if (resizing) {
        // Dragged wider than the page: bring it back inside the column.
        fitImageToColumn(resizing.wrapper, columnWidthOf(resizing.wrapper.closest('.rv-page-body')));
        resizing = null;
        document.body.style.cursor = '';
        // Trigger save
        try { handleTextChange(); } catch {}
      }
    };

    const handleKeyDown = async (e) => {
      if (drag) {
        // Escape puts the image back; no other key acts mid-drag.
        e.preventDefault();
        e.stopPropagation();
        if (e.key === 'Escape') endDrag(false);
        return;
      }
      if (!selectedImage) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        setImageMenu(null);

        // Deleting any piece of a running image deletes the whole image.
        const pieces = selectedImage.hasAttribute(OFFSET_ATTR) ? bleedBlocksOf(selectedImage, editor) : [];
        if (pieces.length > 1) {
          const head = firstPieceOf(selectedImage);
          joinBleedPieces(pieces.slice(1));
          selectedImage = head;
        }
        
        // Get the s3Key before removing
        const img = selectedImage.querySelector('img');
        const s3Key = img?.getAttribute('data-s3-key');
        
        // Get parent element before removal
        const parent = selectedImage.parentElement;
        
        // Remove the image from DOM
        try {
          if (parent) {
            parent.removeChild(selectedImage);
            
            // Clean up: if parent is now empty paragraph, remove it too
            if (parent.tagName === 'P' && (!parent.textContent || parent.textContent.trim() === '')) {
              parent.remove();
            }
          } else {
            selectedImage.remove();
          }
        } catch (err) {
          console.error('Image removal failed:', err);
          // Force removal by replacing with empty
          if (parent) {
            selectedImage.outerHTML = '';
          }
        }
        
        selectedImage = null;
        
        // Force a micro-task delay to ensure DOM is updated
        await new Promise(resolve => setTimeout(resolve, 10));
        
        // Update content state
        const updatedContent = getCleanHTML(editorRef.current);
        setContent(updatedContent);
        
        // Remove from images state if it has an s3Key
        let updatedImages = images;
        if (s3Key) {
          updatedImages = images.filter(img => img.s3Key !== s3Key);
          setImages(updatedImages);
        }
        
        // Force immediate save of both content AND images (don't wait for debounce)
        // Guarded like every other content write: deleting an image must never
        // be the thing that saves an empty document over a manuscript. This
        // path had no check at all, and it writes getCleanHTML straight to the
        // server, so a delete fired while the editor was between renders would
        // persist the blank.
        if (id && hasVisibleText(updatedContent)) {
          try {
            // Save HTML content
            await fetch(`${API_BASE}/api/drafts/${id}`, {
              method: 'PUT',
              credentials: 'omit',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
              },
              body: JSON.stringify({ content: updatedContent })
            });
            
            // Save images array
            await fetch(`${API_BASE}/api/drafts/${id}/images`, {
              method: 'PUT',
              credentials: 'omit',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
              },
              body: JSON.stringify({ images: updatedImages })
            });
            
            // Delete from S3 if it has an s3Key
            if (s3Key) {
              try {
                await fetch(`${API_BASE}/api/drafts/${id}/images/${encodeURIComponent(s3Key)}`, {
                  method: 'DELETE',
                  credentials: 'omit',
                  headers: {
                    'Accept': 'application/json',
                    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
                  }
                });
              } catch (err) {
                console.warn('S3 image deletion failed (non-critical):', err);
              }
            }
          } catch (err) {
            console.error('Image deletion save failed:', err);
          }
        }
      }
    };

    const handleClickCapture = (e) => {
      if (Date.now() < swallowClickUntil) {
        swallowClickUntil = 0;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    editor.addEventListener('mousedown', handleMouseDown);
    editor.addEventListener('click', handleClickCapture, true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      endDrag(false);
      editor.removeEventListener('mousedown', handleMouseDown);
      editor.removeEventListener('click', handleClickCapture, true);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Add event listeners for draggable and resizable shapes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    let shapeDragging = null;
    let shapeResizing = null;
    let selectedShape = null;

    const handleShapeMouseDown = (e) => {
      const shape = e.target.closest('.rv-shape');
      const resizeHandle = e.target.closest('.rv-shape-resize-handle');
      
      if (!shape) {
        // Clicked outside shape, deselect
        if (selectedShape) {
          selectedShape.classList.remove('rv-shape-selected');
          selectedShape = null;
        }
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Select the shape
      if (selectedShape && selectedShape !== shape) {
        selectedShape.classList.remove('rv-shape-selected');
      }
      selectedShape = shape;
      shape.classList.add('rv-shape-selected');
      shape.focus();

      const rect = shape.getBoundingClientRect();

      // Check if clicking on resize handle
      if (resizeHandle) {
        const svg = shape.querySelector('svg');
        if (!svg) return;
        
        shapeResizing = {
          shape,
          svg,
          startX: e.clientX,
          startY: e.clientY,
          startWidth: svg.getAttribute('width') ? parseInt(svg.getAttribute('width')) : rect.width,
          startHeight: svg.getAttribute('height') ? parseInt(svg.getAttribute('height')) : rect.height,
        };
        document.body.style.cursor = 'se-resize';
        return;
      }

      // Start dragging
      shapeDragging = {
        shape,
        startX: e.clientX,
        startY: e.clientY,
        originalMarginLeft: parseInt(shape.style.marginLeft) || 0,
        originalMarginTop: parseInt(shape.style.marginTop) || 0,
      };
      shape.classList.add('rv-shape-dragging');
      document.body.style.cursor = 'move';
    };

    const handleShapeMouseMove = (e) => {
      if (shapeResizing) {
        e.preventDefault();
        const { shape, svg, startX, startY, startWidth, startHeight } = shapeResizing;
        
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        const newWidth = Math.max(30, startWidth + deltaX);
        const newHeight = Math.max(30, startHeight + deltaY);

        // Update SVG dimensions
        svg.setAttribute('width', newWidth);
        svg.setAttribute('height', newHeight);
        svg.setAttribute('viewBox', `0 0 ${newWidth} ${newHeight}`);

        // Regenerate shape path based on new dimensions
        const shapeType = shape.getAttribute('data-shape-type');
        const fillColor = shape.getAttribute('data-fill') || '#FFFFFF';
        const borderColor = shape.getAttribute('data-border') || '#000000';
        const borderWidth = parseInt(shape.getAttribute('data-border-width')) || 2;
        
        const newSvgContent = generateShapePath(shapeType, newWidth, newHeight, fillColor, borderColor, borderWidth);
        const innerShape = svg.querySelector('rect, circle, ellipse, polygon, path');
        if (innerShape) {
          // Replace entire SVG content
          svg.innerHTML = newSvgContent;
        }
        
        return;
      }

      if (shapeDragging) {
        e.preventDefault();
        const { shape, startX, startY, originalMarginLeft, originalMarginTop } = shapeDragging;
        
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        
        shape.style.marginLeft = `${originalMarginLeft + deltaX}px`;
        shape.style.marginTop = `${originalMarginTop + deltaY}px`;
      }
    };

    const handleShapeMouseUp = () => {
      if (shapeDragging) {
        shapeDragging.shape.classList.remove('rv-shape-dragging');
        shapeDragging = null;
        document.body.style.cursor = '';
        try { handleTextChange(); } catch {}
      }
      if (shapeResizing) {
        shapeResizing = null;
        document.body.style.cursor = '';
        try { handleTextChange(); } catch {}
      }
    };

    const handleShapeKeyDown = (e) => {
      if (!selectedShape) return;
      
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        
        const parent = selectedShape.parentElement;
        selectedShape.remove();
        selectedShape = null;
        
        // Clean up empty paragraph
        if (parent && parent.tagName === 'P' && (!parent.textContent || parent.textContent.trim() === '')) {
          parent.remove();
        }
        
        try { handleTextChange(); } catch {}
      }
      
      // Arrow keys to nudge shape
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const nudge = e.shiftKey ? 10 : 1;
        const currentMarginLeft = parseInt(selectedShape.style.marginLeft) || 0;
        const currentMarginTop = parseInt(selectedShape.style.marginTop) || 0;
        
        switch (e.key) {
          case 'ArrowUp':
            selectedShape.style.marginTop = `${currentMarginTop - nudge}px`;
            break;
          case 'ArrowDown':
            selectedShape.style.marginTop = `${currentMarginTop + nudge}px`;
            break;
          case 'ArrowLeft':
            selectedShape.style.marginLeft = `${currentMarginLeft - nudge}px`;
            break;
          case 'ArrowRight':
            selectedShape.style.marginLeft = `${currentMarginLeft + nudge}px`;
            break;
        }
        try { handleTextChange(); } catch {}
      }
    };

    // Helper to regenerate shape SVG path
    const generateShapePath = (type, width, height, fillColor, borderColor, borderWidth) => {
      const stroke = borderColor === 'transparent' ? 'none' : borderColor;
      const fill = fillColor === 'transparent' ? 'none' : fillColor;
      const sw = borderWidth;
      const pad = sw;

      switch (type) {
        case 'rectangle':
          return `<rect x="${pad}" y="${pad}" width="${width - pad*2}" height="${height - pad*2}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'rounded-rect':
          return `<rect x="${pad}" y="${pad}" width="${width - pad*2}" height="${height - pad*2}" rx="12" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'circle':
          const r = Math.min(width, height) / 2 - pad;
          return `<circle cx="${width/2}" cy="${height/2}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'ellipse':
          return `<ellipse cx="${width/2}" cy="${height/2}" rx="${width/2 - pad}" ry="${height/2 - pad}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'triangle':
          return `<polygon points="${width/2},${pad} ${width - pad},${height - pad} ${pad},${height - pad}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'diamond':
          return `<polygon points="${width/2},${pad} ${width - pad},${height/2} ${width/2},${height - pad} ${pad},${height/2}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'pentagon':
          const pPts = [];
          for (let i = 0; i < 5; i++) {
            const angle = (i * 72 - 90) * Math.PI / 180;
            const px = width/2 + (width/2 - pad) * Math.cos(angle);
            const py = height/2 + (height/2 - pad) * Math.sin(angle);
            pPts.push(`${px},${py}`);
          }
          return `<polygon points="${pPts.join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'hexagon':
          const hPts = [];
          for (let i = 0; i < 6; i++) {
            const angle = (i * 60 - 90) * Math.PI / 180;
            const hx = width/2 + (width/2 - pad) * Math.cos(angle);
            const hy = height/2 + (height/2 - pad) * Math.sin(angle);
            hPts.push(`${hx},${hy}`);
          }
          return `<polygon points="${hPts.join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'star':
          const starPts = [];
          for (let i = 0; i < 10; i++) {
            const angle = (i * 36 - 90) * Math.PI / 180;
            const rad = i % 2 === 0 ? (Math.min(width, height)/2 - pad) : (Math.min(width, height)/4);
            const sx = width/2 + rad * Math.cos(angle);
            const sy = height/2 + rad * Math.sin(angle);
            starPts.push(`${sx},${sy}`);
          }
          return `<polygon points="${starPts.join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'arrow-right':
          return `<polygon points="${pad},${height*0.3} ${width*0.6},${height*0.3} ${width*0.6},${pad} ${width - pad},${height/2} ${width*0.6},${height - pad} ${width*0.6},${height*0.7} ${pad},${height*0.7}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'arrow-left':
          return `<polygon points="${width - pad},${height*0.3} ${width*0.4},${height*0.3} ${width*0.4},${pad} ${pad},${height/2} ${width*0.4},${height - pad} ${width*0.4},${height*0.7} ${width - pad},${height*0.7}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        case 'callout':
          return `<path d="M${pad},${pad} L${width - pad},${pad} L${width - pad},${height*0.7} L${width*0.35},${height*0.7} L${width*0.2},${height - pad} L${width*0.28},${height*0.7} L${pad},${height*0.7} Z" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
        default:
          return `<rect x="${pad}" y="${pad}" width="${width - pad*2}" height="${height - pad*2}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" />`;
      }
    };

    editor.addEventListener('mousedown', handleShapeMouseDown);
    document.addEventListener('mousemove', handleShapeMouseMove);
    document.addEventListener('mouseup', handleShapeMouseUp);
    document.addEventListener('keydown', handleShapeKeyDown);

    return () => {
      editor.removeEventListener('mousedown', handleShapeMouseDown);
      document.removeEventListener('mousemove', handleShapeMouseMove);
      document.removeEventListener('mouseup', handleShapeMouseUp);
      document.removeEventListener('keydown', handleShapeKeyDown);
    };
  }, []);

  // Add event listeners for draggable text boxes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    let dragState = null; // { textBox, startX, startY, initialX, initialY }
    let selectedTextBox = null;
    const BORDER_THRESHOLD = 20; // pixels from edge to count as "on border" - increased for easier grabbing

    const isOnBorder = (e, textBox) => {
      const rect = textBox.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Check if near any border
      const nearLeft = x < BORDER_THRESHOLD;
      const nearRight = x > rect.width - BORDER_THRESHOLD;
      const nearTop = y < BORDER_THRESHOLD;
      const nearBottom = y > rect.height - BORDER_THRESHOLD;
      
      return nearLeft || nearRight || nearTop || nearBottom;
    };

    const selectTextBox = (textBox) => {
      // Clear previous selection
      if (selectedTextBox && selectedTextBox !== textBox) {
        selectedTextBox.classList.remove('rv-textbox-selected');
      }
      selectedTextBox = textBox;
      if (textBox) {
        textBox.classList.add('rv-textbox-selected');
        textBox.focus();
      }
    };

    const handleTextBoxMouseDown = (e) => {
      const textBox = e.target.closest('.rv-textbox');
      
      // Deselect if clicking outside any text box
      if (!textBox) {
        if (selectedTextBox) {
          selectedTextBox.classList.remove('rv-textbox-selected');
          selectedTextBox = null;
        }
        return;
      }

      // Check if clicking directly on content area
      const content = e.target.closest('.rv-textbox-content');
      const clickedOnContent = content && e.target.tagName !== 'DIV' || (content && isOnBorder(e, textBox) === false);
      
      if (clickedOnContent) {
        // Deselect when clicking inside content to type
        if (selectedTextBox) {
          selectedTextBox.classList.remove('rv-textbox-selected');
          selectedTextBox = null;
        }
        return;
      }

      // Clicking on border or text box container (not content)
      
      // Select the text box
      selectTextBox(textBox);

      e.preventDefault();
      e.stopPropagation();

      const rect = textBox.getBoundingClientRect();
      const pageBody = textBox.closest('.rv-page-body');
      const pageBodyRect = pageBody?.getBoundingClientRect();
      
      if (!pageBodyRect) {
        return;
      }

      // Convert to absolute positioning for dragging
      const initialX = rect.left - pageBodyRect.left;
      const initialY = rect.top - pageBodyRect.top;
      
      textBox.style.position = 'absolute';
      textBox.style.left = `${initialX}px`;
      textBox.style.top = `${initialY}px`;
      textBox.style.margin = '0';
      textBox.setAttribute('data-x', initialX);
      textBox.setAttribute('data-y', initialY);

      dragState = {
        textBox,
        startX: e.clientX,
        startY: e.clientY,
        initialX: initialX,
        initialY: initialY,
      };

      textBox.classList.add('rv-textbox-dragging');
      textBox.style.zIndex = 1000;
    };

    const handleTextBoxMouseMove = (e) => {
      if (!dragState) return;

      e.preventDefault();
      e.stopPropagation();

      const dx = e.clientX - dragState.startX;
      const dy = e.clientY - dragState.startY;

      const newX = Math.max(0, dragState.initialX + dx);
      const newY = Math.max(0, dragState.initialY + dy);

      dragState.textBox.style.left = `${newX}px`;
      dragState.textBox.style.top = `${newY}px`;
      dragState.textBox.setAttribute('data-x', newX);
      dragState.textBox.setAttribute('data-y', newY);
    };

    const handleTextBoxMouseUp = (e) => {
      if (!dragState) return;

      dragState.textBox.classList.remove('rv-textbox-dragging');
      dragState.textBox.style.zIndex = 10;
      dragState = null;

      // Don't call handleTextChange() - dragging position doesn't change content
    };

    const handleTextBoxKeyDown = (e) => {
      // Simple check: if a text box is selected, Delete or Backspace removes it
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedTextBox) {
        // Only proceed if we're not actively editing text inside the content
        const activeElement = document.activeElement;
        const isEditingContent = activeElement && activeElement.closest('.rv-textbox-content');
        
        if (!isEditingContent) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          selectedTextBox.remove();
          selectedTextBox = null;
          handleTextChange();
          return;
        }
      }

      // Ctrl/Cmd + Delete/Backspace always removes (even when typing)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'Delete' || e.key === 'Backspace')) {
        const textBox = e.target.closest?.('.rv-textbox');
        if (textBox) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          textBox.remove();
          if (selectedTextBox === textBox) selectedTextBox = null;
          handleTextChange();
          return;
        }
      }
    };

    // Use capture phase to handle events BEFORE other handlers
    document.addEventListener('mousedown', handleTextBoxMouseDown, true);
    document.addEventListener('mousemove', handleTextBoxMouseMove);
    document.addEventListener('mouseup', handleTextBoxMouseUp);
    document.addEventListener('keydown', handleTextBoxKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', handleTextBoxMouseDown, true);
      document.removeEventListener('mousemove', handleTextBoxMouseMove);
      document.removeEventListener('mouseup', handleTextBoxMouseUp);
      document.removeEventListener('keydown', handleTextBoxKeyDown, true);
    };
  }, []);

  const authorName = doc?.author?.username || doc?.author?.name || '';

  // Below 900px the right rail floats instead of docking (see EditorPage.css),
  // and until now it only appeared when the preview or a comment summoned it —
  // so there was no way to simply look at chapter progress on a tablet. This is
  // the deliberate way in, driven by the handle on the canvas edge. It means
  // nothing at wider widths, where the rail is docked regardless.
  const [railOpen, setRailOpen] = useState(false);

  // Three separate things can put something in the rail, and the handle has to
  // agree with all of them — otherwise it sits on top of a rail it thinks is
  // closed.
  const commenterInRail =
    activeSidebarPanel === 'collab' && collabView === 'comments' && !!selectedComment && !railPreviewOpen;
  const railVisible = railOpen || railPreviewOpen || commenterInRail;

  // ── Status-bar page navigation ─────────────────────────────────────────────
  // Pages sit at a fixed stride, so which one is under the top of the viewport
  // is pure arithmetic against the editorScrollTop this column already tracks —
  // no per-page measurement and no extra scroll listener. The stride itself
  // comes from EditorRuler so the indicator and the ruler can't disagree.
  //
  // scrollToPage and currentPage are deliberate inverses: scrolling to a row
  // puts its top exactly where the first row's top sits at rest, which is the
  // same origin currentPage measures from. So Next always lands on the page the
  // indicator then names.
  //
  // Everything here counts ROWS, not pages — a row is one page in single view
  // and two in a spread. The stride is the same either way (see the isSpread
  // note above), so the spread costs nothing here beyond the row/page mapping.
  // A spread's rows touch (margin: 0 in .is-spread), so its stride is the bare
  // page height; the single-page column keeps PAGE_GAP between pages and so
  // keeps pageStrideFor's default. Getting this wrong is not subtle but it is
  // quiet: the indicator and Next/Prev would slip 48px further behind the page
  // actually on screen with every row scrolled.
  // Branching at the call rather than in the argument (`isSpread ? 0 :
  // undefined`) is deliberate. A default parameter fires on `undefined` only,
  // so that spelling works — but it reads like a mistake, and the two obvious
  // tidy-ups are both wrong: `: PAGE_GAP` needs the constant exported, which
  // puts a second copy of the 48 at the call site and is exactly the drift
  // pageStrideFor is exported to prevent; `: null` coerces to 0 and silently
  // gives SINGLE-page view the gapless stride. This spelling has nothing to
  // tidy.
  const pageStridePx = Math.max(1, isSpread
    ? pageStrideFor(pageGeom.height, zoom, 0)
    : pageStrideFor(pageGeom.height, zoom));
  const rowCount = Math.max(1, Math.ceil(pageCount / pagesPerRow));
  const SCROLL_EPSILON = 1;

  // The page at the START of whichever row is under the top of the viewport —
  // in a spread, the left-hand page of the visible pair.
  const currentPage = useMemo(() => {
    const el = mainContentRef.current;
    // A short document can't always scroll far enough to bring its last row to
    // the top. Once the scroll is bottomed out, that row is what the reader is
    // actually looking at — but only if they've scrolled at all, otherwise a
    // document that fits entirely on screen would open on its last page.
    const bottomed = el && editorScrollTop > 0 &&
      el.scrollHeight - (editorScrollTop + el.clientHeight) <= 2;
    const row = bottomed
      ? rowCount - 1
      // SCROLL_EPSILON, not a bare floor. Browsers report scrollTop as an
      // integer while a row boundary lands on a fraction (at fit-width the
      // stride is 783.28px), so scrolling exactly to a row leaves scrollTop a
      // fraction of a pixel SHORT of it and the floor names the row above.
      // The 48px band between rows used to hide this: a page top sat 48px
      // into its own stride band, so being a pixel short still landed inside
      // the right band. A spread closed that band to zero and with it the
      // tolerance, which turned a latent rounding bug into the indicator
      // naming the previous pair on most Next presses. 1px is enough — the
      // error it can introduce in the other direction is one pixel of a
      // ~780px band.
      : Math.min(rowCount - 1, Math.max(0, Math.floor((editorScrollTop + SCROLL_EPSILON) / pageStridePx)));
    return row * pagesPerRow + 1;
  }, [editorScrollTop, pageStridePx, rowCount, pagesPerRow]);

  const scrollToPage = (n) => {
    const el = mainContentRef.current;
    if (!el) return;
    const row = Math.min(rowCount - 1, Math.max(0, Math.floor((n - 1) / pagesPerRow)));
    el.scrollTo({ top: row * pageStridePx, behavior: 'smooth' });
  };

  // The preview toggle has two call sites now — the doc-bar button and the
  // rail's own close control — so the open-time work lives in one place.
  // Rendering immediately on open stops the panel flashing empty while the
  // debounced effect catches up.
  const handleTogglePreview = () => {
    if (!railPreviewOpen) {
      setRailPreviewHtml(getCleanHTML(editorRef.current));
      setRailPreviewRange(railPreviewChapterRange());
      fetchBookMatter();
    }
    setRailPreviewOpen((v) => !v);
  };

  return (
    <Box className="editor-page">
      <Box className="editor-sticky-header">
      {/* Header: title + author (left) · Story Builder / Preview / Publish (right).
          Save state and word count used to live up here; they moved to the
          status bar under the page, which is where the prototype gathers every
          "state of the document" readout. */}
      <Box className="editor-actions" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, px: 2, pt: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flex: 1, minWidth: 0 }}>
          <input
            type="text"
            className="draft-title-input"
            value={editableTitle}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Untitled Document"
            spellCheck={false}
          />
          {authorName && (
            <Typography
              sx={{
                fontSize: '0.9rem',
                fontWeight: 500,
                color: '#6b6b6b',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              by {authorName}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {/* The slot this button was reserved for: the Cork Board, at
              /manuscript/:id/cork-board. It shows the same story elements as
              the Story Elements panel in the sidebar — actors, scenes and
              artifacts, and the relationships between them — arranged as cards
              on a board instead of as a list. Its own "Manuscript Editor"
              button comes back here. */}
          <Tooltip title="Open the story elements as a board">
            <Button
              variant="outlined"
              size="small"
              onClick={() => saveThenGo(`/manuscript/${id}/cork-board`)}
            >
              Story Builder
            </Button>
          </Tooltip>
          <Button
            variant={railPreviewOpen ? 'contained' : 'outlined'}
            size="small"
            onClick={handleTogglePreview}
            aria-pressed={railPreviewOpen}
          >
            {railPreviewOpen ? 'Hide Preview' : 'Show Preview'}
          </Button>
          {/* Same call the File panel's Publish button used to make. */}
          <Button variant="contained" size="small" onClick={() => setShowPublishModal(true)}>
            Publish
          </Button>
        </Box>
      </Box>
      </Box>

     <Box className="editor-content" sx={{ position: 'relative' }}>
      {/* Story element description on hover over a recognized name. Portalled,
          so it sits outside the manuscript's DOM and text. */}
      <ElementHoverCard editorRef={editorRef} nodes={storyNodes} />

      {/* Vertical icon strip — the tab list. An open panel takes its place;
          SidebarPanelHeader's back arrow brings it back. */}
      {!activeSidebarPanel && (
        <EditorSidebar
          activePanel={activeSidebarPanel}
          onPanelClick={handleSidebarPanelClick}
          saveStatus={saveStatus}
        />
      )}

      {/* Content with sidebar */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Sidebar Panel Content */}
        {activeSidebarPanel === 'navigation' && (
          <Box className="sidebar-panel-vertical is-story-elements">
            <SidebarPanelHeader title="Story Elements" onBack={closeSidebarPanel} />
            <StoryElementsPanel
              draftId={id}
              refreshToken={storyElementsRev}
              onBoardChange={(board) => setStoryNodes(board.nodes || [])}
              structure={doc?.manuscriptStructure}
              onOpenStructureElement={(elementId) =>
                saveThenGo(`/manuscript/${id}/structure?element=${elementId}`)}
              outline={outline}
              wordCounts={chapterWordCounts}
              activeBlockId={activeHeadingId}
              onJump={handleOutlineJump}
              onInsertAfter={(blockId) => insertChapter(blockId)}
              onInsertAtStart={() => insertChapter(null)}
              onRename={(blockId, newText) => renameChapter(blockId, newText)}
              onDelete={(blockId) => deleteChapter(blockId)}
              onPromote={(blockId) => changeHeadingLevel(blockId, -1)}
              onDemote={(blockId) => changeHeadingLevel(blockId, +1)}
              onReorder={(sourceId, targetId, position) => moveChapter(sourceId, targetId, position)}
            />
          </Box>
        )}

        {activeSidebarPanel === 'file' && (
          <Box className="sidebar-panel-vertical">
            <SidebarPanelHeader title="File" onBack={closeSidebarPanel} />
            <FilePanel
              onCreateBookCover={handleToolsBookCover}
              onWriteBlurb={() => saveThenGo(`/editor/${id}/blurb`)}
              onOpenStructure={() => saveThenGo(`/manuscript/${id}/structure`)}
              onDownloadPdf={handleDownloadPdf}
              downloadingPdf={downloadingPdf}
              onSaveProgress={() => forceSave(false)}
              onSaveAndClose={async () => { await forceSave(false); navigate('/dashboard'); }}
              saveStatus={saveStatus}
            />
          </Box>
        )}

        {activeSidebarPanel === 'format' && (
          <Box className="sidebar-panel-vertical">
            <SidebarPanelHeader title="Format" onBack={closeSidebarPanel} />
            <EditorToolbar
              onBold={onBold}
              onItalic={onItalic}
              onUnderline={onUnderline}
              onStrikethrough={onStrikethrough}
              onSubscript={onSubscript}
              onSuperscript={onSuperscript}
              onClearFormatting={onClearFormatting}
              onAlignLeft={onAlignLeft}
              onAlignCenter={onAlignCenter}
              onAlignRight={onAlignRight}
              onAlignJustify={onAlignJustify}
              onBulletedList={onBulletedList}
              onNumberedList={onNumberedList}
              onIndentIncrease={onIndentIncrease}
              onIndentDecrease={onIndentDecrease}
              onUndo={onUndo}
              onRedo={onRedo}
              onFontFamilyChange={onFontFamilyChange}
              onFontSizeChange={onFontSizeChange}
              onLineSpacingChange={onLineSpacingChange}
              onTextColorChange={onTextColorChange}
              onHighlightColorChange={onHighlightColorChange}
              onParagraphSpacingChange={onParagraphSpacingChange}
              onToggleHeader={onToggleHeader}
              onToggleFooter={onToggleFooter}
              onPageSetupChange={onPageSetupChange}
              trimSize={trimSize}
              onTrimSizeChange={requestTrimSize}
              onCaptureSelection={captureSelection}
              onApplyHeading={applyHeadingToBlock}
              currentFormatting={currentFormatting}
            />
          </Box>
        )}
        
        {activeSidebarPanel === 'insert' && (
          <Box className="sidebar-panel-vertical">
            <SidebarPanelHeader title="Insert" onBack={closeSidebarPanel} />
            <InsertPanel
              onOpenImage={openInsertImage}
              onOpenTable={openInsertTable}
              onOpenShape={openInsertShape}
              onOpenDivider={openInsertDivider}
              onOpenTextBox={openTextBox}
              onOpenInsertPage={openInsertPage}
              onOpenPageNumber={handleOpenPageNumber}
              onInsertPageBreak={handleInsertPageBreak}
            />
          </Box>
        )}

        {activeSidebarPanel === 'view' && (
          <Box className="sidebar-panel-vertical">
            <SidebarPanelHeader title="View" onBack={closeSidebarPanel} />
            <ViewPanel
              // Document
              showRuler={showRuler}
              onToggleRuler={() => setShowRuler(v => !v)}
              showFormattingMarks={showFormattingMarks}
              onToggleFormattingMarks={() => setShowFormattingMarks(v => !v)}
              // Annotations
              showCommentHighlights={showCommentHighlights}
              onToggleCommentHighlights={() => setShowCommentHighlights(v => !v)}
              showCollabEdits={showCollabEdits}
              onToggleCollabEdits={() => setShowCollabEdits(v => !v)}
              collabEditCount={collabEdits.length}
              // Preview
              onOpenBookPreview={() => { fetchBookMatter(); setShowBookPreview(true); }}
              onOpenEbookPreview={() => setShowEbookPreview(true)}
            />
          </Box>
        )}

        {activeSidebarPanel === 'tools' && (
          <Box className="sidebar-panel-vertical">
            <SidebarPanelHeader title="Tool" onBack={closeSidebarPanel} />
            <ToolsPanel
              onSpellCheck={() => setShowSpellCheck(true)}
              onWordCount={() => setShowWordCount(true)}
              onCitations={() => setShowCitations(true)}
              showLineNumbers={showLineNumbers}
              onToggleLineNumbers={setShowLineNumbers}
            />
          </Box>
        )}

        {activeSidebarPanel === 'collab' && (
          <Box className="sidebar-panel-vertical is-collab">
            <SidebarPanelHeader title="Collab" onBack={closeSidebarPanel} />
            <CollabTabs value={collabView} onChange={setCollabView} />
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {collabView === 'comments' ? (
              <CommentsPanel
                comments={collabComments}
                outline={outline}
                selectedBlockId={selectedCommentBlockId}
                onSelectLine={handleSelectCommentLine}
                onDeselectLine={() => setSelectedCommentBlockId(null)}
                includeUnread={includeUnread}
                onToggleIncludeUnread={setIncludeUnread}
                loading={collabLoading}
                error={collabError}
                lineNumberFor={lineResolver}
                chapterFor={chapterResolver}
                bookId={doc?._id}
                itemType="draft"
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                isItemOwner={isItemOwner}
                collaborators={notifyCandidates}
                onCommentsChanged={() => setCollabReloadKey((k) => k + 1)}
                pendingComment={pendingComment}
                onSubmitComment={handleSubmitPassageComment}
                onCancelComment={() => setPendingComment(null)}
                openChapters={commentChaptersOpen}
                onOpenChaptersChange={setCommentChaptersOpen}
                revealSeq={commentRevealSeq}
                revealedRef={commentRevealedRef}
              />
            ) : (
              <CollabPanel
                author={doc?.author}
                collaborators={collaborators}
                onInviteClick={() => setIsInviteOpen(true)}
                onlineUsers={onlineUsers}
                onRemoveCollaborator={handleRemoveCollaborator}
              />
            )}
            </Box>
          </Box>
        )}

        {activeSidebarPanel === 'goals' && (
          <Box className={`sidebar-panel-vertical is-goals${goalsListTucked ? ' is-tucked' : ''}`}>
            <SidebarPanelHeader title="Goal" onBack={closeSidebarPanel} />
            <GoalsSidePanel
              goals={goalsApi.goals}
              live={liveWords}
              currentUserId={currentUserId}
              selectedId={goalsView.mode === 'detail' ? goalsView.id : null}
              onOpen={openGoal}
              onNew={() => setGoalForm({ goal: null })}
            />
          </Box>
        )}

        {/* Main content area */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0, position: 'relative' }}>
          {/* The goals page sits OVER the manuscript rather than replacing it:
              the editor stays mounted, so its load, autosave and caret are
              untouched while someone looks at their goals. */}
          {activeSidebarPanel === 'goals' && goalsView.mode !== 'manuscript' && (
            <GoalsView
              api={goalsApi}
              live={liveWords}
              chapters={chapterList}
              currentUserId={currentUserId}
              view={goalsView}
              onOpen={openGoal}
              onBack={() => setGoalsView({ mode: 'dashboard' })}
              onNew={() => setGoalForm({ goal: null })}
              onEdit={(goal) => setGoalForm({ goal })}
              onWrite={() => { setGoalsView({ mode: 'manuscript' }); setGoalsListTucked(true); }}
              onContinue={continueGoal}
              onToggleList={() => setGoalsListTucked((v) => !v)}
            />
          )}
          {/* Inline quick-format bar (mock (7)) — reuses the same handlers as the
              Format sidebar panel. The heading control restores the saved caret
              first, since applyHeadingToBlock reads the live selection. */}
          {/* Same handlers the Format panel uses — the bar is a second way to
              reach them, never a second implementation. The word count that
              used to sit here is gone: it is in the status bar below the page
              and again as Chapter progress in the right rail. */}
          <EditorFormatBar
            onBold={onBold}
            onItalic={onItalic}
            onUnderline={onUnderline}
            onStrikethrough={onStrikethrough}
            onQuote={() => run('formatBlock', 'blockquote')}
            onApplyHeading={(level) => { restoreIntoEditor(); applyHeadingToBlock(level); }}
            onUndo={onUndo}
            onRedo={onRedo}
            onFontFamilyChange={onFontFamilyChange}
            onFontSizeChange={onFontSizeChange}
            onBulletedList={onBulletedList}
            onNumberedList={onNumberedList}
            onAlignLeft={onAlignLeft}
            onAlignCenter={onAlignCenter}
            onAlignRight={onAlignRight}
            onAlignJustify={onAlignJustify}
            onHighlightColorChange={onHighlightColorChange}
            onClearFormatting={onClearFormatting}
            onInsertPageBreak={handleInsertPageBreak}
            onInsertSceneBreak={handleInsertSceneBreak}
            themeName={manuscriptTheme?.name}
            onOpenThemes={setThemeMenuAnchor}
            canChangeTheme={isItemOwner}
            templateName={templateName}
            onOpenTemplates={setTemplateMenuAnchor}
          />
          {/* H-ruler bar — outside the scroll container so it never scrolls away */}
          {rulersVisible && (
            <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0, background: '#5A52A3', borderBottom: '1px solid #D4AF37', zIndex: 10 }}>
              <EditorHRuler
                pageWidth={pageGeom.width}
                scale={zoom}
                scrollLeft={editorScrollLeft}
                margins={themeMargins || pageMargins}
                onMarginsChange={themeMargins ? holdThemeMargins : handleMarginsChange}
                paraIndents={paraIndents}
                onIndentChange={handleIndentChange}
              />
            </Box>
          )}
          <Box
            className={`editor-main-content${manuscriptTheme ? ' rv-themed' : ''}`}
            ref={mainContentRef}
            onScroll={(e) => {
              setEditorScrollTop(e.currentTarget.scrollTop);
              setEditorScrollLeft(e.currentTarget.scrollLeft);
              setCommentChip((c) => (c ? null : c));
            }}
            sx={{ flex: 1, position: 'relative' }}
          >
            {/* Stable overlay root for image widgets (lives as long as this EditorPage) */}
            <div ref={overlayRef} className="rv-overlay-root" />

              {rulersVisible && (
                <EditorVRuler
                  pageHeight={pageGeom.height}
                  headerHeight={pageGeom.headerH}
                  footerHeight={pageGeom.footerH}
                  scale={zoom}
                  pageCount={pageCount}
                  margins={themeMargins || pageMargins}
                  onMarginsChange={themeMargins ? holdThemeMargins : handleMarginsChange}
                  scrollTop={editorScrollTop}
                />
              )}

      {/*
        Zoom sizer: reserves real scroll space at the SCALED size
        (pageGeom.width/contentHeight × zoom) while .editable-content itself
        keeps laying out at its natural, unscaled size and is only visually
        scaled via transform. This keeps offsetWidth/offsetHeight (used by the
        pageGeom/contentHeight measurements above, and by pagination elsewhere)
        reporting true logical px regardless of zoom — only paint is scaled.
        Pulling editable-content out of flow (position:absolute) is required so
        its unscaled box doesn't also inflate the sizer's scroll area.
      */}
      {/* Unsaved work from an earlier visit. The author chooses; nothing is
          restored by itself, since a co-author may have saved since. */}
      {recovery && !loadError && (
        <Box
          role="status"
          sx={{
            position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
            zIndex: 20, width: 'min(600px, calc(100% - 32px))', boxSizing: 'border-box',
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
            borderRadius: '8px', bgcolor: tokens.color.purpleDeep, color: '#fff',
            borderLeft: `4px solid ${tokens.color.goldFlat}`,
          }}
        >
          <Typography sx={{ fontSize: 14, flex: 1 }}>
            {recovery.sameBase
              ? `We found changes from your last visit (${new Date(recovery.ts).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}) that weren't saved.`
              : `We found changes from your last visit (${new Date(recovery.ts).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}) that weren't saved, but this draft has been saved since, possibly by a co-author. Restoring replaces the current version.`}
          </Typography>
          <Button
            onClick={restoreRecovery}
            sx={{ color: tokens.color.purpleDeep, bgcolor: tokens.color.goldLight, flexShrink: 0, '&:hover': { bgcolor: tokens.color.goldLight } }}
          >
            Restore
          </Button>
          <Button
            onClick={discardRecovery}
            sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.7)', flexShrink: 0 }}
          >
            Discard
          </Button>
        </Box>
      )}
      {/* Saving can't reach the server. Say so plainly — and only promise the
          work is kept here when the browser actually accepted the copy. */}
      {connectionLost && !loadError && !recovery && (
        <Box
          role="status"
          sx={{
            position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
            zIndex: 20, width: 'min(560px, calc(100% - 32px))', boxSizing: 'border-box',
            px: 2, py: 1.5, borderRadius: '8px', bgcolor: tokens.color.purpleDeep, color: '#fff',
            borderLeft: `4px solid ${tokens.color.goldFlat}`,
          }}
        >
          <Typography sx={{ fontSize: 14 }}>
            {localBackupOk
              ? "Connection lost. Your changes are kept on this computer until you're back online."
              : "Connection lost. Keep this tab open: your latest changes aren't saved yet."}
          </Typography>
        </Box>
      )}
      {/* A book with the print team: edits made now don't reach the reviewer.
          Same slot as the banners around it, so it yields to all three. */}
      {!loadError && !recovery && !connectionLost && <PrintReviewNotice draftId={id} />}
      {/* A failed load leaves a blank page. Say so, rather than let the
          author write into it believing it is their manuscript. */}
      {loadError && (
        <Box
          role="alert"
          sx={{
            // Pinned over the top of the editor area: in flow it became a tall
            // column beside the page (this row also holds the ruler).
            position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
            zIndex: 20, width: 'min(560px, calc(100% - 32px))', boxSizing: 'border-box',
            display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5,
            borderRadius: '8px', bgcolor: tokens.color.error, color: '#fff',
          }}
        >
          <Typography sx={{ fontSize: 14, flex: 1 }}>{loadError}</Typography>
          <Button
            onClick={() => window.location.reload()}
            sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.7)', flexShrink: 0 }}
          >
            Retry
          </Button>
        </Box>
      )}
      <div
        className="rv-zoom-sizer"
        style={{ position: 'relative', width: layoutWidth * zoom, height: contentHeight * zoom }}
      >
      <div
        ref={editorRef}
        className={`editable-content${showFormattingMarks ? ' show-formatting' : ''}${isSpread ? ' is-spread' : ''}`}
        spellCheck="true"
        style={{
          position: 'absolute', top: 0, left: 0,
          width: layoutWidth, minHeight: 600,
          transform: `scale(${zoom})`, transformOrigin: 'top left',
        }}

        /* Intercept BEFORE the browser inserts characters/paragraphs */
        onBeforeInputCapture={(e) => {
        const type = e.nativeEvent?.inputType;
        const text = e.nativeEvent?.data || e.data;

        // Cross-page merge on Backspace at a page start. iOS soft keyboards
        // deliver this as beforeinput/deleteContentBackward rather than a
        // reliable 'Backspace' keydown, so handle it here too. No-op unless the
        // caret is exactly at a page body's start with a previous page above —
        // in every other case the browser's normal delete proceeds untouched.
        if (type === 'deleteContentBackward') {
          if (mergePageUpAtCaret()) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }

        // Reset alignment when typing in centered/right-aligned paragraphs
        if (type === 'insertText' || type === 'insertCompositionText') {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            let block = range.startContainer.nodeType === 3 
              ? range.startContainer.parentElement 
              : range.startContainer;
            block = block?.closest('p, div, h1, h2, h3, h4, h5, h6');
            
            if (block) {
              const align = window.getComputedStyle(block).textAlign;
              // If paragraph is centered or right-aligned, reset to left before typing
              if (align === 'center' || align === 'right') {
                block.style.textAlign = 'left';
              }
            }
          }
        }
        
        // Apply pending formatting to typed text first (check data instead of type for iOS compatibility)
        // Only intercept actual characters, not spaces or line breaks
        if (text && text.trim().length > 0 && (pendingFormat.fontFamily || pendingFormat.fontSize)) {
          e.preventDefault();
          e.stopPropagation();
          
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            
            // Create styled span with pending format
            const span = document.createElement('span');
            if (pendingFormat.fontFamily) {
              span.style.fontFamily = pendingFormat.fontFamily;
            }
            if (pendingFormat.fontSize) {
              span.style.fontSize = pendingFormat.fontSize;
            }
            span.textContent = text;
            
            // Insert the span
            range.deleteContents();
            range.insertNode(span);
            
            // Move cursor after the inserted text
            range.setStartAfter(span);
            range.setEndAfter(span);
            sel.removeAllRanges();
            sel.addRange(range);
          }
          return;
        }
        
        // iOS Safari compatibility: if type is undefined, allow normal processing
        if (!type) {
          return;
        }
        
        // Handle paragraph insertion to reset alignment AND heading formatting
        if (type === 'insertParagraph') {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            const currentBlock = range.startContainer.nodeType === 3 
              ? range.startContainer.parentElement 
              : range.startContainer;
            
            // Check if current block is a heading or has special formatting
            const block = currentBlock?.closest('p, div, h1, h2, h3, h4, h5, h6');
            if (block) {
              const computed = window.getComputedStyle(block);
              const align = computed.textAlign;
              const fontSize = parseInt(computed.fontSize);
              const fontWeight = computed.fontWeight;
              const isHeading = /^H[1-6]$/.test(block.tagName);
              const isStructuralHeading = block.hasAttribute('data-heading-level');
              const isLargeText = fontSize > 18; // Title is 27px
              const isBold = fontWeight === '700' || fontWeight === 'bold' || parseInt(fontWeight) >= 600;

              // Reset if it's a heading (tag or structural marker), or if it's
              // styled like a title (large, centered, bold). This also stops the
              // heading's data-heading-level/data-block-id being cloned onto the
              // new line by contentEditable, which would create duplicate ids.
              // A paragraph the last template change kept as regular text: let
              // Enter split it natively, then take the freeze off the new line
              // (which cloned it), so new writing follows the template. Done
              // here because the id pass that also catches clones only runs
              // while the outline panel is open.
              const frozen = block.hasAttribute(ORPHAN_ATTR);
              if (frozen) {
                setTimeout(() => {
                  const s2 = window.getSelection();
                  const node = s2?.anchorNode;
                  const fresh = (node?.nodeType === 3 ? node.parentElement : node)?.closest?.('.rv-page-body > *');
                  if (fresh && fresh !== block && releaseBlock(fresh)) {
                    fresh.setAttribute('data-block-id', makeBlockId());
                    try { handleTextChange?.(); } catch {}
                  }
                }, 0);
              }

              const needsReset = !frozen && (isHeading || isStructuralHeading || align === 'center' || align === 'right' || (isLargeText && isBold && align === 'center'));
              
              if (needsReset) {
                console.log('[Enter] Resetting formatting for:', block.tagName, { align, fontSize, fontWeight });
                e.preventDefault();
                
                // Create a new paragraph with normal styling
                const doc = block.ownerDocument;
                const newP = doc.createElement('p');
                newP.innerHTML = '<br/>';
                newP.style.textAlign = 'left';
                newP.style.fontSize = '16px';
                newP.style.fontWeight = 'normal';
                newP.style.fontFamily = 'Arial, sans-serif';
                newP.style.marginTop = '0';
                newP.style.marginBottom = '12px';
                newP.className = '';
                
                // Insert after current block
                block.insertAdjacentElement('afterend', newP);
                
                // Move cursor to new paragraph
                const newRange = doc.createRange();
                newRange.selectNodeContents(newP);
                newRange.collapse(true);
                sel.removeAllRanges();
                sel.addRange(newRange);
                
                // Update formatting state immediately
                setTimeout(() => {
                  const formatting = getCurrentFormatting();
                  if (formatting) {
                    setCurrentFormatting(formatting);
                    console.log('[Enter] New formatting:', formatting);
                  }
                  try { handleTextChange?.(); } catch {}
                }, 10);
                
                return;
              }
            }
          }
        }
        
        if (type !== 'insertText' && type !== 'insertParagraph' && type !== 'insertCompositionText') return;

        const body = getCurrentPageBodyFromSelection?.();
        if (!body) return;

        const atEnd = isCaretAtEndOfBody(body);
        const pageEl = body.closest('.rv-page');
        const pageH = pageEl?.clientHeight ?? Number.MAX_VALUE;
        const willOverflow = isBodyOverflowing(body) || (body.scrollHeight + 4 > pageH);


        // Only jump when:
        // - inserting a paragraph at (or near) the end, OR
        // - typing text at the end AND it would overflow
        const shouldJump = (type === 'insertParagraph')
          ? (atEnd || willOverflow)
          : (atEnd && willOverflow);

        if (!shouldJump) return;

        e.preventDefault();
        e.stopPropagation();

        const nextBody = ensureNextPageBody?.(pageEl);
        if (!nextBody) return;

        focusBodyStart(nextBody);
        if (type === 'insertParagraph') {
          insertParagraphAtCaret();
        } else {
          const text = e.nativeEvent.data ?? '';
          if (text) insertTextAtCaret(text);
        }
        try { handleTextChange?.(); } catch {}
      }}
        /* Arrow navigation for moving between pages + Delete key to merge/delete blocks */
        onKeyDownCapture={(e) => {
          // Delete/Backspace should always trigger a layout check, even when
          // the key doesn't actually mutate the DOM (e.g., pressed in the
          // empty zone between content and the page bottom). Without this,
          // no `input` event fires and reflow never gets a chance to pull
          // back content from a later page.
          if (e.key === 'Delete' || e.key === 'Backspace') {
            setTimeout(() => paginateAllPages(), 0);
          }

          // Handle Tab key for list indentation (nested bullets/numbers)
          if (e.key === 'Tab') {
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return;
            
            const range = sel.getRangeAt(0);
            const listItem = range.startContainer.nodeType === 3 
              ? range.startContainer.parentElement?.closest('li')
              : range.startContainer.closest('li');
            
            if (listItem) {
              e.preventDefault();
              e.stopPropagation();
              
              if (e.shiftKey) {
                // Shift+Tab: Outdent (decrease indentation)
                const parentList = listItem.parentElement; // ul or ol
                const grandparentItem = parentList?.parentElement?.closest('li');
                
                if (grandparentItem) {
                  // This is a nested list - move item up one level
                  const grandparentList = grandparentItem.parentElement;
                  
                  // Insert the item after the parent item
                  grandparentList.insertBefore(listItem, grandparentItem.nextSibling);
                  
                  // If the parent list is now empty, remove it
                  if (parentList.children.length === 0) {
                    parentList.remove();
                  }
                  
                  // Restore cursor position
                  const newRange = document.createRange();
                  newRange.selectNodeContents(listItem);
                  newRange.collapse(true);
                  sel.removeAllRanges();
                  sel.addRange(newRange);
                  
                  try { handleTextChange?.(); } catch {}
                }
              } else {
                // Tab: Indent (increase indentation) - only if there's a previous sibling
                const previousItem = listItem.previousElementSibling;
                
                if (previousItem && previousItem.tagName === 'LI') {
                  // Get the parent list type (UL or OL)
                  const parentList = listItem.parentElement;
                  const listType = parentList.tagName; // 'UL' or 'OL'
                  
                  // Check if previous item already has a nested list
                  let nestedList = previousItem.querySelector(':scope > ul, :scope > ol');
                  
                  if (!nestedList) {
                    // Create a new nested list of the same type
                    nestedList = document.createElement(listType);
                    previousItem.appendChild(nestedList);
                  }
                  
                  // Move the current item into the nested list
                  nestedList.appendChild(listItem);
                  
                  // Restore cursor position
                  const newRange = document.createRange();
                  newRange.selectNodeContents(listItem);
                  newRange.collapse(true);
                  sel.removeAllRanges();
                  sel.addRange(newRange);
                  
                  try { handleTextChange?.(); } catch {}
                }
              }
              return;
            }
          }
          
          if (e.key === 'Delete') {
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return;
            const range = sel.getRangeAt(0);
            
            if (!sel.isCollapsed) return; // Has selection, let browser handle it

            // A clicked image is what Delete is for. Clicking an image leaves
            // the text caret where it was, so the merge below would act on
            // that caret instead; leave the key to the image's own handler.
            if (imageMenu) return;

            // An image standing as its own block next: do nothing. The merge
            // below would move its <img> out of its wrapper into this block.
            // preventDefault only — not stopPropagation — so the document-level
            // handler that deletes a clicked image still sees the key.
            if (caretAgainstBodyImage('forward')) {
              e.preventDefault();
              return;
            }

            const container = range.startContainer;
            const body = container.nodeType === 3
              ? container.parentElement?.closest('.rv-page-body')
              : container.closest('.rv-page-body');
            
            if (!body) return;
            
            // Find the block containing the cursor
            let block = container.nodeType === 3 
              ? container.parentElement?.closest('p, h1, h2, h3, h4, h5, h6, li, div:not(.rv-page-body)')
              : container.closest('p, h1, h2, h3, h4, h5, h6, li, div:not(.rv-page-body)');
            
            // Get all content children (not spacers)
            const contentChildren = Array.from(body.children).filter(
              el => !el.classList?.contains('rv-end-spacer')
            );
            
            // Check if cursor is at the end of the LAST content block on this page
            const isLastBlock = !block || contentChildren.indexOf(block) === contentChildren.length - 1 || 
              contentChildren.every(el => {
                const elRect = el.getBoundingClientRect();
                const cursorRect = range.getBoundingClientRect();
                return cursorRect.top >= elRect.top || el === block;
              });
            
            // Check if cursor is at end of its container
            const atEnd = range.startOffset >= (container.nodeType === 3 
              ? container.textContent.length 
              : container.childNodes.length);
            
            // If at end of the last block on this page, AUTO-FILL from the next
            // page: strip the blank filler and re-flow so content rises to fill
            // this page (splitting a paragraph at the line boundary as needed)
            // instead of jumping a whole block up at once.
            if (isLastBlock && atEnd) {
              const page = body.closest('.rv-page');
              const nextPage = page?.nextElementSibling;

              if (nextPage?.classList?.contains('rv-page')) {
                const nextBody = nextPage.querySelector('.rv-page-body');
                const nextHasContent = nextBody && Array.from(nextBody.children).some(
                  el => !el.classList?.contains('rv-end-spacer') && el.textContent.trim() !== ''
                );
                if (nextHasContent) {
                  e.preventDefault();
                  e.stopPropagation();
                  pullContentUpToFill(body);
                  return;
                }
              }
            }
            
            // Check if there's a next sibling block to merge with
            if (block) {
              const nextSibling = block.nextElementSibling;
              
              if (nextSibling && !nextSibling.classList?.contains('rv-end-spacer')) {
                const atEndOfBlock = range.startOffset >= (container.nodeType === 3 
                  ? container.textContent.length 
                  : container.childNodes.length);
                
                if (atEndOfBlock) {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (nextSibling.textContent.trim() === '' && !nextSibling.querySelector('img, table, .rv-shape, .rv-textbox')) {
                    nextSibling.remove();
                  } else {
                    // Merge next block's content into current block
                    while (nextSibling.firstChild) {
                      block.appendChild(nextSibling.firstChild);
                    }
                    nextSibling.remove();
                  }
                  
                  try { handleTextChange?.(); } catch {}
                  return;
                }
              }
            }
            
            // Not at end of a block - let browser handle
            return;
          }

          // Backspace at the very START of a page body: merge this page's first
          // block up into the previous page. Desktop browsers deliver a reliable
          // 'Backspace' keydown; iOS soft keyboards do not, so the same merge is
          // also wired into onBeforeInputCapture (deleteContentBackward) below.
          if (e.key === 'Backspace') {
            if (mergePageUpAtCaret()) {
              e.preventDefault();
              e.stopPropagation();
            }
            return;
          }

          if (e.key === 'ArrowDown') {
            const body = getCurrentPageBodyFromSelection?.();
            if (!body) return;
            // Only intercept when there's no next visual line inside this body.
            // Otherwise let the browser move the caret naturally line-by-line.
            if (!isCaretAtVisualEnd(body)) return;

            const page = body.closest('.rv-page');
            const nextPage = page?.nextElementSibling;
            // Only hop to an existing next page. Never create a new page from
            // an arrow key — pages are created by overflow (typing/Enter/paste),
            // matching Google Docs / Word behavior.
            if (!nextPage?.classList?.contains('rv-page')) return;

            const nextBody = nextPage.querySelector('.rv-page-body');
            if (!nextBody) return;

            e.preventDefault();
            e.stopPropagation();

            focusBodyStart(nextBody);
            try { handleTextChange?.(); } catch {}
          } else if (e.key === 'ArrowUp') {
            const body = getCurrentPageBodyFromSelection?.();
            if (!body) return;
            if (!isCaretAtVisualStart(body)) return;

            const page = body.closest('.rv-page');
            const prevPage = page?.previousElementSibling;
            if (!prevPage || !prevPage.classList.contains('rv-page')) return;

            const prevBody = prevPage.querySelector('.rv-page-body');
            if (!prevBody) return;

            e.preventDefault();
            e.stopPropagation();

            focusBodyEndBeforeSpacer(prevBody);
            try { handleTextChange?.(); } catch {}
          }
        }}

        onKeyDown={(e) => {
          // your existing handler for other keys
          handleEditorKeyDown(e);
        }}

        onPaste={handlePaste}

        onCompositionStart={() => { composingRef.current = true; }}
        onCompositionEnd={() => { composingRef.current = false; }}

        onInput={(e) => {
          handleTextChange(e);
          // After any input, check all pages for overflow and paginate as needed
          // Use longer timeout to ensure DOM is fully updated after paste
          setTimeout(() => {
            paginateAllPages();
          }, 50);
          captureSelection();
        }}
        onKeyUp={(e) => { captureSelection(e); updateCommentChip(); }}
        onMouseUp={(e) => { captureSelection(e); updateCommentChip(); }}
        onBlur={captureSelection}
        onClick={(e) => {
          // Handle clicking on table cell images to enlarge
          if (e.target.classList.contains('rv-table-image')) {
            e.stopPropagation();
            setEnlargedImage({
              src: e.target.src,
              alt: e.target.alt || 'Table image',
            });
            return;
          }

          // Show table context menu when clicking on table cells
          const cell = e.target.closest('.rv-cell');
          const table = e.target.closest('.rv-table');
          
          if (cell && table) {
            e.stopPropagation();
            setTableContextMenu({
              x: e.clientX + 10,
              y: e.clientY + 10,
              table,
              cell,
            });
          } else {
            // Close menu if clicking outside table
            setTableContextMenu(null);
          }
        }}
        suppressContentEditableWarning
      />
      </div>{/* end rv-zoom-sizer */}


      {/* DISABLED: Old overlay image system - now using inline images in HTML
      {images.length > 0 && (
        <ImageLayer
          editorRef={editorRef}
          overlayRef={overlayRef}
          images={images}
          setImages={setImages}
        />
      )}
      */}
          </Box>{/* end editor-main-content */}

          {/* Status strip — deliberately a sibling of .editor-main-content, not
              a child: that container's padding is mirrored by CONTENT_VPAD here
              and CONTAINER_PAD_TOP/BOTTOM_PADDING in EditorRuler. */}
          <EditorStatusBar
            // No save state to show for a draft that never loaded — "Saved"
            // beside the could-not-load banner read as all being well.
            saveStatus={loadError ? null : saveStatus}
            onRetrySave={() => forceSave(false)}
            wordCount={liveWordCount}
            currentPage={currentPage}
            pageCount={pageCount}
            onPrevPage={() => scrollToPage(currentPage - pagesPerRow)}
            onNextPage={() => scrollToPage(currentPage + pagesPerRow)}
            viewMode={pageViewMode}
            onViewModeChange={setPageViewMode}
            zoomMode={zoomMode}
            zoom={zoom}
            onSetZoomMode={setZoomMode}
            onSetZoom={(z) => setZoomMode(clampZoom(z))}
            onBackToGoals={activeSidebarPanel === 'goals' && goalsView.mode === 'manuscript'
              ? () => setGoalsView({ mode: 'dashboard' }) : null}
          />
        </Box>{/* end column wrapper */}

        {/* Right rail — sibling flex column (never nested in editor-main-content,
            so it doesn't disturb the rulers/zoom centering math). */}
        {/* While the Collab tab has a comment selected, the rail shows who
            wrote it (per the collaborator design). Otherwise it keeps its
            normal contents, so the progress/theme controls aren't lost behind
            a tab. An open preview outranks both: the Preview button now lives
            in the doc bar and is always reachable, so pressing it has to
            actually show the preview rather than silently lose to whatever
            else currently owns the rail. */}
        {commenterInRail ? (
          <CommenterCredentials comment={selectedComment} />
        ) : (
          <EditorRightRail
            open={railOpen || railPreviewOpen}
            currentWords={liveWordCount}
            reviewers={collaborators}
            pendingSuggestions={draftComments.length}
            onPreview={handleTogglePreview}
            previewOpen={railPreviewOpen}
            previewHtml={railPreviewScope === 'book' ? withBookMatter(railPreviewHtml) : railPreviewHtml}
            previewFormat={railPreviewFormat}
            onPreviewFormatChange={setRailPreviewFormat}
            previewScope={railPreviewScope}
            onPreviewScopeChange={setRailPreviewScope}
            previewChapterRange={railPreviewRange}
            trimSize={trimSize}
            theme={manuscriptTheme}
            themeSample={themeSample}
            trimLabel={dimensionsLabel(trimSize)}
            canChangeTheme={isItemOwner}
            onCustomizeTheme={openThemeEditor}
            themeOverrideCount={themeOverrideCount}
            onResetToTheme={clearThemeOverrides}
            goalsSection={activeSidebarPanel === 'goals' ? (
              <GoalsRailSection goals={goalsApi.goals} writing={goalsApi.writing} live={liveWords} onOpen={openGoal} />
            ) : null}
          />
        )}

        {/* The way back to a floating rail. CSS hides this entirely above 900px,
            where the rail is docked and there is nothing to summon. It sits on
            the canvas edge rather than in the doc bar because it belongs to the
            thing it opens, and the doc bar is already three buttons wide on a
            screen that cannot spare a fourth. */}
        <button
          type="button"
          className={`rail-handle${railVisible ? ' is-open' : ''}`}
          onClick={() => {
            // Closing has to close whatever is actually holding the rail open —
            // the preview, or a selected comment — or the rail stays put and the
            // handle looks broken.
            if (railVisible) {
              if (railPreviewOpen) handleTogglePreview();
              if (commenterInRail) setSelectedCommentBlockId(null);
              setRailOpen(false);
            } else {
              setRailOpen(true);
            }
          }}
          aria-label={railVisible ? 'Hide chapter panel' : 'Show chapter panel'}
        >
          {railVisible ? '\u203A' : '\u2039'}
        </button>
      </Box>
    </Box>

      {/* The comment sidebar overlay used to live here. Comments are now part
          of the Collab panel, so there's a single surface instead of an
          overlay that covered the very list it was launched from. The
          component itself stays — BookReader still uses it for published
          books, where a floating panel is the right shape. */}
      <FloatingToolbar
        onCommentClick={() => { setActiveSidebarPanel('collab'); setCollabView('comments'); }}
      />
      <CommentSelectionChip chip={commentChip} onClick={openCommentForSelection} />

      {/* View tab → Preview overlays. HTML is read clean (decorations/highlights
          stripped) at open time so previews never show editor-only artifacts. */}
      <Snackbar
        open={!!undoToast}
        autoHideDuration={6000}
        onClose={() => setUndoToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={undoToast?.label}
        action={
          <Button
            size="small"
            onClick={undoStructural}
            sx={{ color: '#F5D76E', fontWeight: 700 }}
          >
            UNDO
          </Button>
        }
      />

      <ManuscriptThemeMenu
        anchorEl={themeMenuAnchor}
        onClose={() => setThemeMenuAnchor(null)}
        currentSourceId={manuscriptTheme?.sourceId}
        hasTheme={Boolean(manuscriptTheme)}
        onPick={applyManuscriptTheme}
        onCustomize={openThemeEditor}
        onRemove={() => applyManuscriptTheme(null)}
      />

      <TemplateMenu
        anchorEl={templateMenuAnchor}
        onClose={() => setTemplateMenuAnchor(null)}
        currentId={templateId}
        canUndo={templateHistory.length > 0}
        undoName={templateHistory[templateHistory.length - 1]?.template_name}
        onPick={pickTemplate}
        onUndo={undoTemplateSwitch}
      />

      <ConfirmModal
        open={Boolean(templateToSwitch)}
        title={`Switch to ${templateToSwitch?.name || 'this template'}?`}
        message={`The text you have already written becomes regular text: it keeps the look it has without a template, and you format it yourself from here. ${templateToSwitch?.name || 'The new template'} sets how everything you write next looks, and the page layout of the whole manuscript. You can undo this from the Template menu.`}
        confirmLabel="Switch template"
        cancelLabel="Cancel"
        onConfirm={confirmTemplateSwitch}
        onCancel={() => setTemplateToSwitch(null)}
      />

      {/* Straight after a theme is applied, if old inline formatting would hide
          it. Keeping it is a real choice (a passage set in its own face on
          purpose), so it is asked, not done. */}
      <ConfirmModal
        open={themeOverridePrompt > 0}
        title="Let the theme show everywhere?"
        message={`${themeOverridePrompt} ${themeOverridePrompt === 1 ? 'place in this manuscript has its' : 'places in this manuscript have their'} own font or size, which will sit on top of the theme. Clear that formatting? Bold, italic and colours stay, and you can undo it.`}
        confirmLabel="Clear formatting"
        cancelLabel="Keep it"
        onConfirm={clearThemeOverrides}
        onCancel={() => setThemeOverridePrompt(0)}
      />

      <Snackbar
        open={!!themeToast}
        autoHideDuration={4000}
        onClose={() => setThemeToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={themeToast}
      />

      <ConfirmModal
        open={!!chapterToDelete}
        title="Delete chapter?"
        highlight={chapterToDelete?.title}
        message="This removes the heading and all of its content from the document."
        confirmLabel="Delete"
        destructive
        onCancel={() => setChapterToDelete(null)}
        onConfirm={() => {
          const bid = chapterToDelete?.blockId;
          setChapterToDelete(null);
          if (bid) performDeleteChapter(bid);
        }}
      />

      {/* Changing an existing manuscript's trim size re-flows the whole book —
          every line break, every page break, the page count, and any page break
          the author placed by hand. Worth asking before, not explaining after.

          It also silently desynchronises the cover, which is sized per cover
          version and is not touched by this. Resizing the cover automatically
          is not the fix: the design is a Fabric canvas laid out against the old
          dimensions, and rescaling it would move every element on it. So the
          author is told, and goes and does it deliberately. */}
      <ConfirmModal
        open={Boolean(pendingTrimSize)}
        title="Change the book size?"
        highlight={pendingTrimSize
          ? `${bookSize(pendingTrimSize).label} (${dimensionsLabel(pendingTrimSize)})`
          : ''}
        message={
          'Your manuscript will be laid out again at the new size. Line breaks, page breaks '
          + 'and the page count will change, and any page break you inserted by hand may move.'
          + (hasCoverToResize
            ? ' This book also has a cover. The cover is NOT resized with the manuscript — '
              + 'it will still be drawn at the old size, and a cover and an interior of '
              + 'different sizes cannot be bound into one book. Open the Cover Designer '
              + 'afterwards and set the cover to the new size.'
            : '')
        }
        confirmLabel="Change size"
        onConfirm={() => { setTrimSize(pendingTrimSize); setPendingTrimSize(null); }}
        onCancel={() => setPendingTrimSize(null)}
      />

      <BookPreviewModal
        open={showBookPreview}
        onClose={() => setShowBookPreview(false)}
        html={showBookPreview ? withBookMatter(getCleanHTML(editorRef.current)) : ''}
        title={`${doc?.title || editableTitle || 'Untitled'} — Book Preview`}
        draft={doc}
        trimSize={trimSize}
      />
      <EbookPreviewModal
        open={showEbookPreview}
        onClose={() => setShowEbookPreview(false)}
        html={showEbookPreview ? withBookMatter(getCleanHTML(editorRef.current)) : ''}
        title={`${doc?.title || editableTitle || 'Untitled'} — Ebook Preview`}
      />

      <ImageUploadModal
        open={showImage}
        onClose={() => setShowImage(false)}
        onInsert={handleInsertImage}
      />
      <TableWizard
        open={showTable}
        onClose={() => setShowTable(false)}
        onInsert={handleInsertTable}
      />
      <TableContextMenu
        position={tableContextMenu}
        onAddRow={handleTableAddRow}
        onDeleteRow={handleTableDeleteRow}
        onAddColumn={handleTableAddColumn}
        onDeleteColumn={handleTableDeleteColumn}
        onInsertImage={handleTableInsertImage}
        onClose={() => setTableContextMenu(null)}
      />
      <ImageContextMenu
        position={imageMenu}
        bleed={isBleedOn(imageMenu?.span)}
        continues={!!imageMenu?.span && !!editorRef.current && bleedPiecesOf(imageMenu.span, editorRef.current).length > 1}
        shared={bleedBlocker(imageMenu?.span) === 'shared'}
        onToggleBleed={handleToggleImageBleed}
      />
      <ShapePicker
        open={showShape}
        onClose={() => setShowShape(false)}
        onInsert={handleInsertShape}
      />
      <DividerPicker
        open={showDivider}
        onClose={() => setShowDivider(false)}
        onInsert={handleInsertDivider}
      />
      <TextBoxPicker
        open={showTextBox}
        onClose={() => setShowTextBox(false)}
        onInsert={handleInsertTextBox}
      />
      <InsertPageModal
      open={showInsertPage}
      onClose={() => setShowInsertPage(false)}
      onInsert={handleInsertPage}
      />
      <PageNumberModal
        open={showPageNumberModal}
        onClose={() => setShowPageNumberModal(false)}
        initial={pageNumberCfg}
        onSave={(cfg) => {
          // use functional setState to avoid stale closures and then apply on next frame
          setPageNumberCfg(prev => ({ ...prev, ...cfg, enabled: true }));
          setShowPageNumberModal(false);
          // let React commit, then render numbers
          setTimeout(() => { try { applyPageNumbers?.(); } catch {} }, 0);
        }}
        onDisable={() => {
          setPageNumberCfg(prev => ({ ...prev, enabled: false }));
          setShowPageNumberModal(false);
          setTimeout(() => { try { applyPageNumbers?.(); } catch {} }, 0);
        }}
      />


      <GoalFormModal
        open={!!goalForm}
        goal={goalForm?.goal || null}
        api={goalsApi}
        live={liveWords}
        chapters={chapterList}
        currentUserId={currentUserId}
        onClose={() => { setGoalForm(null); goalsApi.clearError(); }}
        onSaved={(id) => { setGoalForm(null); if (id) openGoal(id); }}
      />

      <PublishModal
        open={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handleConfirmPublish}
        doc={doc}
      />

      <SpellCheckModal
        open={showSpellCheck}
        onClose={() => setShowSpellCheck(false)}
        editorRef={editorRef}
        onApplied={triggerChange}
      />

      <WordCountModal
        open={showWordCount}
        onClose={() => setShowWordCount(false)}
        editorRef={editorRef}
      />

      <CitationModal
        open={showCitations}
        onClose={() => setShowCitations(false)}
        draftId={id}
        editorRef={editorRef}
        onInsert={handleInsertCitation}
        onInsertBibliography={handleInsertBibliography}
        onRestyle={handleRestyleCitations}
      />

      <CollaboratorsDialog
        open={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        onAddCollaborator={handleAddCollaborator}
        existingCollaborators={collaborators}
        outgoingInvitations={outgoingInvitations}
      />

      {/* Paste Options Menu */}
      {pasteMenu && (
        <Box
          sx={{
            position: 'fixed',
            left: Math.min(pasteMenu.x, window.innerWidth - 220),
            top: Math.min(pasteMenu.y, window.innerHeight - 180),
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            zIndex: 10001,
            minWidth: 200,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1, borderBottom: '1px solid #eee', bgcolor: '#f5f5f5' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
              Paste Options
            </Typography>
          </Box>
          <Box
            onClick={pasteWithFormatting}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f0f0f0' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ fontSize: 18 }}>📋</Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Keep Source Formatting</Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>Paste with original styles</Typography>
            </Box>
          </Box>
          <Box
            onClick={pasteMatchingStyle}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f0f0f0' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ fontSize: 18 }}>🔤</Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Match Document Style</Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>Use document formatting</Typography>
            </Box>
          </Box>
          <Box
            onClick={pastePlainText}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f0f0f0' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ fontSize: 18 }}>📝</Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Paste as Plain Text</Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>No formatting</Typography>
            </Box>
          </Box>
          <Box
            onClick={() => setPasteMenu(null)}
            sx={{
              p: 1,
              cursor: 'pointer',
              textAlign: 'center',
              borderTop: '1px solid #eee',
              '&:hover': { bgcolor: '#fee' },
            }}
          >
            <Typography variant="caption" sx={{ color: '#999' }}>Cancel</Typography>
          </Box>
        </Box>
      )}

      {/* Click outside to close paste menu */}
      {pasteMenu && (
        <Box
          onClick={() => setPasteMenu(null)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
          }}
        />
      )}

      {/* Image Enlargement Modal */}
      {enlargedImage && (
        <Box
          onClick={() => setEnlargedImage(null)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            p: 4,
          }}
        >
          <Box
            component="img"
            src={enlargedImage.src}
            alt={enlargedImage.alt}
            sx={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: 24,
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
      )}
    </Box>
  );
};

export default EditorPage;